'use strict';

import "core-js/stable"
import "regenerator-runtime/runtime" // Required by d3.

import 'd3-transition'
import { COLOURS } from '../../common/constants.js'
import { select, selectAll} from 'd3-selection'
import { max, min, range, bisector } from 'd3-array'
import { scaleLinear, scaleBand } from 'd3-scale'
import { axisBottom, axisLeft } from 'd3-axis'
import { drag } from 'd3-drag'
import { line, area } from 'd3-shape'
import { ajaxRequest } from '../../common/ajax.js'
import { handleLoggedOutError } from '../../common/ajax-error-handlers.jsx'
import { formatTime } from '../../common/helpers.js'
import { TrainingSession, ActivityPlan } from '../../models/training-sessions.js'
import { Race } from '../../models/races.js'
import { logError } from '../../common/logger.js'
import enquire from 'enquire.js'
import React from 'react'
import ReactDOM from 'react-dom'
import activateConfirmationBox from '../../react-components/confirmation-box.jsx'
import { TrainingSessionForm } from '../../react-components/training-session-form.jsx'
import { DurationInput } from '../../react-components/duration-input.jsx'
import activate_persistent_sidebar from '../../common/activate-persistent-sidebar.jsx'
import {
  getFirstValidDatetime,
  filterElevationOutlierData,
  calculateMovingTimeFromTrackpoints,
  calculateElevationGainFromTrackpoints,
  SMOOTH_HEIGHT,
  SMOOTH_DISTANCE,
  filterTrackpointsWithoutKey,
  getSmoothedElevationPointsByDistance,
  formatSecondsAsPace,
} from './helpers.js'
import { ChartSectionSwimLapsPace, ChartSectionSwimLapsStrokes, SwimLapRow } from './react-components.jsx'
import {
    newZuluDatetime,
    meanWeightedByTrackpointDuration,
    getURLParameter,
    hasBetaAccess,
    addToArrayIfNotExists,
    removeFromArrayIfExists,
    toggleInArray,
    decimalPlaces,
    metersPerSecondAsSecondsPerHundredMeters,
} from '../../common/helpers.js'
import { saveTrainingSessions } from '../../persistence/training-sessions-and-races.js'
import L from 'leaflet';
import {
  LapRow,
  getActivitySummaries,
  ComparisionChart,
  SummaryTable,
  CollectionRow,
  fetchLapSetActivitySummariesBySessionIDs,
  fetchLapSetActivitySummariesByTags,
  getAveragePower,
  getAverageHeartRate,
  getAverageCadence,
  getAverageSpeedMetersPerSecond,
  SessionCollection,
} from '../../react-components/collections.jsx'

function formatHoursMinutesFromSeconds(seconds) {
  return formatTime(seconds, true, true, true);
}

var highlight_colours = ['#DF0000', '#4CB2F7', '#19B11E', '#FF9A00', '#B700DF'];

var training_session_form;
var x_distance_scale = {}
var x_duration_scale = {}
var y_scale          = { 'elevation': {}, 'speed': {}, 'heart_rate': {}, 'power': {}, 'cadence': {} }
var x_distance_axis  = {}
var x_duration_axis  = {}
var y_speed_axis    = {};
var y_speed_km_label_text = {};
var y_speed_miles_label_text = {};
var distance_line = { 'elevation': {}, 'speed': {}, 'heart_rate': {}, 'power': {}, 'cadence': {} }
var duration_line = { 'elevation': {}, 'speed': {}, 'heart_rate': {}, 'power': {}, 'cadence': {} }
var distance_area = { 'elevation': {}, 'speed': {}, 'heart_rate': {}, 'power': {}, 'cadence': {} }
var duration_area = { 'elevation': {}, 'speed': {}, 'heart_rate': {}, 'power': {}, 'cadence': {} }
var global_x_scale_position = 0;
var marker_line = null;
var mobile_mode = true;
var dragging = false;
var pool_unit = (xhale_meta_data.pool_unit_preference == 'meters') ? 'meters' : 'yards';
var activities_section;

xhale.global_state.current_location_markers = {};

// Set up dimensions of the charts (all charts need to have the same dimensions).
var margin = {top: 15, right: 15, bottom: 17, left: 55};
var height = 125 - margin.top - margin.bottom;
var chart_width = null;

function calculateDurationTickValues(scale) {

  function roundSecondsToNiceInterval(seconds) {
    if (seconds > 1800) return 3600; // Greater than 30 minutes, so round up to the hour.
    if (seconds >  900) return 1800; // Greater than 15 minutes, so round up the half hour.
    if (seconds >  600) return 900;  // Greater than 10 minutes, so round up to 15 minutes.
    if (seconds >  300) return 600;  // Greater than 5 minutes, so round up to 10 minutes.
    if (seconds >  120) return 300;  // Greater than 2 minutes, so round up to 5 minutes.
    if (seconds >   60) return 120;  // Greater than 1 minute, so round up to 2 minutes.
    if (seconds >   30) return 60;   // Greater than 30 seconds, so round up to 1 minute.
    if (seconds >   15) return 30;   // Greater than 15 seconds, so round up to 30 seconds.
    if (seconds >   10) return 15;   // Greater than 10 seconds, so round up to 15 seconds.
    if (seconds >    5) return 10;   // Greater than 5 seconds, so round up to 10 seconds.
    if (seconds >    2) return 5;   //  Greater than 2 seconds, so round up to 5 seconds.
    if (seconds >    1) return 2;   //  Greater than 1 second, so round up to 2 seconds.
    if (seconds <=   1) return 1;   //  Otherwise every one second.
  }

  /**
   * Round the seconds up to the first interval.
   */
  function roundSecondsToNextInterval(seconds, interval) {
    return interval * Math.ceil(seconds / interval);
  }

  var domain = scale.domain();
  var scale_range = scale.range();

  var scale_min_seconds = domain[0];
  var scale_max_seconds = domain[1];
  var scale_width = scale_range[1];

  var number_of_ticks = calculateNumberOfTicks(scale_width, 50);
  var scale_total_seconds = scale_max_seconds - scale_min_seconds;

  // We have seen the scale jump dramatically due to recording errors. This line
  // ensures the page doesn't crash if the duration exceeds a couple of days.
  // The duration scale will obviously break, but it will keep the distance
  // scale working correctly.
  if (scale_total_seconds > 172800) { return []; }

  var tick_spacing_in_seconds = roundSecondsToNiceInterval(scale_total_seconds / number_of_ticks);

  var start_value = scale_min_seconds > 0 ? roundSecondsToNextInterval(scale_min_seconds, tick_spacing_in_seconds) : 0;

  var tick_values = range(start_value, scale_max_seconds, tick_spacing_in_seconds);

  return tick_values;
}

function calculateNumberOfTicks(axis_width, space_per_label) {
  return Math.ceil(axis_width / space_per_label);
}

function updateGraphPositionMarker() {
  if (global_x_scale_position) {
    marker_line.style("left", global_x_scale_position + 'px');
  }

  global_x_scale_position = null;

  requestAnimationFrame(updateGraphPositionMarker);
}

function setChartsToDistance() {

  for (var activity_id in activities_section.state.base_activity_ids) {

    if (activities_section.refs['activity_section_' + activity_id].state.highlighted_trackpoints.first && activities_section.refs['activity_section_' + activity_id].state.highlighted_trackpoints.last) {
      var first_trackpoint_x_scale_position = x_distance_scale[activity_id](activities_section.refs['activity_section_' + activity_id].state.highlighted_trackpoints.first.distance_meters);
      var last_trackpoint_x_scale_position = x_distance_scale[activity_id](activities_section.refs['activity_section_' + activity_id].state.highlighted_trackpoints.last.distance_meters);
      var left_position = first_trackpoint_x_scale_position + margin.left;
      var right_position = last_trackpoint_x_scale_position + margin.left;

      if (left_position > right_position) {
        var temp_right_position = right_position;
        right_position = left_position;
        left_position = temp_right_position;
      }

      var width = right_position - left_position;
      let highlight_box = select("#activity-" + activity_id + " .highlight-box");
      highlight_box
        .transition()
        .duration(1000)
        .style("left", left_position + 'px')
        .style("width", width + 'px');
    }

  }

  // Resize compare lines.
  if (typeof(activities_section.refs['activity_section_' + activity_id]) != 'undefined') {
    activities_section.refs['activity_section_' + activity_id].rescale(1000, 'distance', null);
  }

  // Resize the points on the chart.
  for (var activity_id in duration_line.elevation) {
    select("#activity-" + activity_id + " .elevation-chart .trackpoint-line").transition().duration(1000).attr("d", distance_line.elevation[activity_id]);
    select("#activity-" + activity_id + " .elevation-chart .data-area").transition().duration(1000).attr("d", distance_area.elevation[activity_id]);
  }
  for (var activity_id in duration_line.speed) {
    select("#activity-" + activity_id + " .speed-chart .trackpoint-line").transition().duration(1000).attr("d", distance_line.speed[activity_id]);
    select("#activity-" + activity_id + " .speed-chart .data-area").transition().duration(1000).attr("d", distance_area.speed[activity_id]);
  }
  for (var activity_id in duration_line.heart_rate) {
    select("#activity-" + activity_id + " .heart-rate-chart .trackpoint-line").transition().duration(1000).attr("d", distance_line.heart_rate[activity_id]);
    select("#activity-" + activity_id + " .heart-rate-chart .data-area").transition().duration(1000).attr("d", distance_area.heart_rate[activity_id]);
  }
  for (var activity_id in duration_line.power) {
    select("#activity-" + activity_id + " .power-chart .trackpoint-line").transition().duration(1000).attr("d", distance_line.power[activity_id]);
    select("#activity-" + activity_id + " .power-chart .data-area").transition().duration(1000).attr("d", distance_area.power[activity_id]);
  }
  for (var activity_id in duration_line.cadence) {
    select("#activity-" + activity_id + " .cadence-chart .trackpoint-line").transition().duration(1000).attr("d", distance_line.cadence[activity_id]);
    select("#activity-" + activity_id + " .cadence-chart .data-area").transition().duration(1000).attr("d", distance_area.cadence[activity_id]);
  }

  selectAll(".axis.x.duration").transition()
    .duration(1000)
    .style('opacity', 0);

  selectAll(".axis.x.distance").transition()
    .duration(1000)
    .style('opacity', 1)
    .on('end', function () {
      activities_section.setState({x_scale_mode: 'distance'});
    });

}

function setChartsToDuration() {

  // Resize the highlight box.
  for (var activity_id in activities_section.state.base_activity_ids) {
    if (activities_section.refs['activity_section_' + activity_id].state.highlighted_trackpoints.first && activities_section.refs['activity_section_' + activity_id].state.highlighted_trackpoints.last) {
      var first_trackpoint_x_scale_position = x_duration_scale[activity_id](activities_section.refs['activity_section_' + activity_id].state.highlighted_trackpoints.first.cumulative_duration_seconds);
      var last_trackpoint_x_scale_position = x_duration_scale[activity_id](activities_section.refs['activity_section_' + activity_id].state.highlighted_trackpoints.last.cumulative_duration_seconds);
      var left_position = first_trackpoint_x_scale_position + margin.left;
      var right_position = last_trackpoint_x_scale_position + margin.left;

      if (left_position > right_position) {
        var temp_right_position = right_position;
        right_position = left_position;
        left_position = temp_right_position;
      }

      var width = right_position - left_position;
      let highlight_box = select("#activity-" + activity_id + " .highlight-box");
      highlight_box
        .transition()
        .duration(1000)
        .style("left", left_position + 'px')
        .style("width", width + 'px');
    }

    // Resize compare lines.
    if (typeof(activities_section.refs['activity_section' + activity_id]) != 'undefined') {
      activities_section.refs['activity_section' + activity_id].rescale(1000, 'duration', null);
    }

  }

  // Resize the points on the chart.
  for (var activity_id in duration_line.elevation) {
    select("#activity-" + activity_id + " .elevation-chart .trackpoint-line").transition().duration(1000).attr("d", duration_line.elevation[activity_id]);
    select("#activity-" + activity_id + " .elevation-chart .data-area").transition().duration(1000).attr("d", duration_area.elevation[activity_id]);
  }
  for (var activity_id in duration_line.speed) {
    select("#activity-" + activity_id + " .speed-chart .trackpoint-line").transition().duration(1000).attr("d", duration_line.speed[activity_id]);
    select("#activity-" + activity_id + " .speed-chart .data-area").transition().duration(1000).attr("d", duration_area.speed[activity_id]);
  }
  for (var activity_id in duration_line.heart_rate) {
    select("#activity-" + activity_id + " .heart-rate-chart .trackpoint-line").transition().duration(1000).attr("d", duration_line.heart_rate[activity_id]);
    select("#activity-" + activity_id + " .heart-rate-chart .data-area").transition().duration(1000).attr("d", duration_area.heart_rate[activity_id]);
  }
  for (var activity_id in duration_line.power) {
    select("#activity-" + activity_id + " .power-chart .trackpoint-line").transition().duration(1000).attr("d", duration_line.power[activity_id]);
    select("#activity-" + activity_id + " .power-chart .data-area").transition().duration(1000).attr("d", duration_area.power[activity_id]);
  }
  for (var activity_id in duration_line.cadence) {
    select("#activity-" + activity_id + " .cadence-chart .trackpoint-line").transition().duration(1000).attr("d", duration_line.cadence[activity_id]);
    select("#activity-" + activity_id + " .cadence-chart .data-area").transition().duration(1000).attr("d", duration_area.cadence[activity_id]);
  }

  // Resize the scale.
  selectAll(".axis.x.distance").transition()
    .duration(1000)
    .style('opacity', 0);

  selectAll(".axis.x.duration,.axis.x.lap-duration").transition()
    .duration(1000)
    .style('opacity', 1)
    .on('end', function () {
      activities_section.setState({x_scale_mode: 'duration'});
    });

}


/*
 * Determine the track point nearest to the cursor.
 *
 * x_position: the position of the mouse cursor
 * metric: either 'lap_distance_meters', 'distance_meters', 'lap_duration_seconds', 'cumulative_duration_seconds'.
 */
function getCurrentTrackpoint(x_position, reduced_trackpoints, scale, metric, on_scale_only) {

  var bisect = bisector(function (d) { return d[metric]; }).left;

  var value_of_metric = scale.invert(x_position); // Value of whatever metric we are looking at, closest the given x position.
  var trackpoint_index_right = bisect(reduced_trackpoints, value_of_metric, 1); // Index for the track point to the right of the value.
  var trackpoint_index_left = trackpoint_index_right - 1;

  if (on_scale_only && typeof(reduced_trackpoints[trackpoint_index_right]) == 'undefined') {
    return null;
  }

  if (typeof(reduced_trackpoints[trackpoint_index_right]) != 'undefined' && value_of_metric - reduced_trackpoints[trackpoint_index_left][metric] > reduced_trackpoints[trackpoint_index_right][metric] - value_of_metric) {
    var trackpoint = reduced_trackpoints[trackpoint_index_right];
  }
  else {
    var trackpoint = reduced_trackpoints[trackpoint_index_left];
  }

  trackpoint.x_scale_position = scale(trackpoint[metric]);

  return trackpoint;
}

// Reduce data to a maximum number of points.
function reduceData(activity_id, trackpoints_to_reduce, max_trackpoints) {

  // TODO Allow reducing this by number of seconds instead?

  var activity_id = parseInt(activity_id);

  var latest = {
    'cadence_rpm': 0,
    'altitude_meters': 0,
    'distance_meters': 0,
    'power_watts': 0,
    'heart_rate_bpm': 0,
    'pace_seconds_per_hectometer': 0,
    'speed_km_per_hour': 0,
    'speed_miles_per_hour': 0,
    'cumulative_duration_seconds': 0,
    'pace_seconds_per_km': null,
    'pace_seconds_per_mile': null
  }

  function get_average(trackpoints, property) {
    // Create lists of the values.
    var values = [];
    for (var i = 0; i < trackpoints.length; i++) {
      if (typeof trackpoints[i][property] == 'number') {
        values.push(trackpoints[i][property]);
      }
    }

    // Create averages.
    if (values.length > 0) {
      var sum = values.reduce(function(a, b) { return a + b }, 0);
      latest[property] = sum / values.length;
    }

    return latest[property];

  }

  function create_average_trackpoint(trackpoints) {

    var trackpoint = {
      id: trackpoints[0].id,
      type: 'reduced',
      altitude_meters: get_average(trackpoints, 'altitude_meters'),
      cadence_rpm: get_average(trackpoints, 'cadence_rpm'),
      datetime: trackpoints[0].datetime,
      cumulative_duration_seconds: trackpoints[0].cumulative_duration_seconds,
      unfiltered_array_index: trackpoints[0].unfiltered_array_index,
      distance_meters: get_average(trackpoints, 'distance_meters'),
      heart_rate_bpm: get_average(trackpoints, 'heart_rate_bpm'),
      latitude: trackpoints[0].latitude,
      longitude: trackpoints[0].longitude,
      power_watts: get_average(trackpoints, 'power_watts'),
      speed_meters_per_second: get_average(trackpoints, 'speed_meters_per_second'),
      speed_km_per_hour: get_average(trackpoints, 'speed_km_per_hour'),
      speed_miles_per_hour: get_average(trackpoints, 'speed_miles_per_hour'),
      pace_seconds_per_km: get_average(trackpoints, 'pace_seconds_per_km'),
      pace_seconds_per_mile: get_average(trackpoints, 'pace_seconds_per_mile')
    }

    return trackpoint;
  }

  function trackpoint_should_be_filtered_out(current_trackpoint, previous_trackpoint, next_trackpoint) {

    var filter_out_trackpoint = false;

    // Make sure distance gets larger at each point. Anything that does not
    // gets filtered out.
    var distance_less_or_equal_to_next_point = next_trackpoint && current_trackpoint.distance_meters <= next_trackpoint.distance_meters;
    var distance_greater_or_equal_to_previous_point = previous_trackpoint && current_trackpoint.distance_meters >= previous_trackpoint.distance_meters;
    if (!(distance_less_or_equal_to_next_point && distance_greater_or_equal_to_previous_point)) {
      filter_out_trackpoint = true;
    }

    // Ensure trackpoint has a datetime.
    if (typeof(current_trackpoint.datetime) == 'undefined') {
      filter_out_trackpoint = true;
    }

    return filter_out_trackpoint;
  }

  var reduced_list = [];
  var reduction_factor = (trackpoints_to_reduce.length / max_trackpoints);
  var previous_quotient = null;
  var average_trackpoint_collection = []; // list to draw moving averages from.
  var previous_unfiltered_trackpoint = trackpoints_to_reduce[0];
  var next_trackpoint = trackpoints_to_reduce[1];
  for (var i = 0; i < trackpoints_to_reduce.length; i++) {

    // The next reduced index is the same as the current length of the list.
    // Since we obviously haven't added the next set to the list yet, this
    // accounts for the list's index starting at zero.
    var next_reduced_array_index = reduced_list.length;

    // Add the current reduced array index to the original full list of
    // trackpoints. This will start out at index 0.
    var unfiltered_array_index = trackpoints_to_reduce[i].unfiltered_array_index;
    trackpoints_to_reduce[unfiltered_array_index].reduced_array_index = next_reduced_array_index;

    var current_trackpoint = trackpoints_to_reduce[i];
    var filter_out_trackpoint = trackpoint_should_be_filtered_out(current_trackpoint, previous_unfiltered_trackpoint, next_trackpoint);

    var quotient = parseInt(i / reduction_factor);

    if (!filter_out_trackpoint) {

      // Add this track point to the list.
      average_trackpoint_collection.push(trackpoints_to_reduce[i]);

      // When we have the right number of items in the list to draw an
      // average from go ahead and do that. Also make sure the last item
      // is included on its own by always finalising the second to last
      // and drawing average from the very last on its own.
      if (quotient > previous_quotient || i >= trackpoints_to_reduce.length - 2) {

        var trackpoint = create_average_trackpoint(average_trackpoint_collection);

        // Add the activity id.
        trackpoint.activity_id = activity_id;

        // Add the reduced array index so we can refer back to the full
        // list when this trackpoint is separated from the store later.
        trackpoint.reduced_array_index = next_reduced_array_index;

        reduced_list.push(trackpoint);

        // Reset list ready for calculating the new average.
        average_trackpoint_collection = [];
      }

      previous_unfiltered_trackpoint = current_trackpoint;

    }

    previous_quotient = quotient;
    next_trackpoint = trackpoints_to_reduce[i + 1];

  }

  return reduced_list;
}

function process_activity_data_trackpoints(activity_data) {

  // Nothing to process so just return the data unchanged.
  if (typeof(activity_data.trackpoints) == 'undefined' || activity_data.trackpoints.length == 0) {
    return activity_data;
  }

  // Convert datetime values to objects.
  for (var i = 0; i < activity_data.trackpoints.length; i++) {
    if (activity_data.trackpoints[i].datetime) {
      activity_data.trackpoints[i].datetime = newZuluDatetime(activity_data.trackpoints[i].datetime);
    }
  }

  // Sometimes distance restarts half way through a file. When this happens
  // update distance so that that it accumulates from where it previously
  // left off.
  var previous_distance_meters = 0;
  var starting_distance_meters = 0;
  for (var i = 0; i < activity_data.trackpoints.length; i++) {

    // When the distance restarts, update the starting distance. This number
    // will then be added to the trackpoint's distance. The 50 ensures that
    // the previous number is a low one. If it is high, it is obviously not
    // actually restarting.
    if (activity_data.trackpoints[i].distance_meters < previous_distance_meters && activity_data.trackpoints[i].distance_meters < 50) {

      // Look ahead a few trackpoints to make sure the number continues to be
      // lower than the previous trackpoint. If all looks abnormal still
      // update the starting distance.
      var values_ahead_look_normal_again = (typeof(activity_data.trackpoints[i + 10]) != 'undefined' && activity_data.trackpoints[i + 10].distance_meters > previous_distance_meters);
      if (!values_ahead_look_normal_again) {
        starting_distance_meters += previous_distance_meters;
      }

    }

    previous_distance_meters = activity_data.trackpoints[i].distance_meters;
    activity_data.trackpoints[i].distance_meters += starting_distance_meters;
  }

  activity_data.trackpoint_id_lookup = {};
  var first_datetime = getFirstValidDatetime(activity_data.trackpoints);
  for (var i = 0; i < activity_data.trackpoints.length; i++) {

    // Store the index for the trackpoint in the unfiltered array of
    // trackpoints. We refer back to this later when we need to
    // calculate values from the full dataset.
    activity_data.trackpoints[i].unfiltered_array_index = i;
    activity_data.trackpoint_id_lookup[activity_data.trackpoints[i].id] = i;

    // Convert data to appropriate speed unit.
    if (activity_data.trackpoints[i].speed_meters_per_second && activity_data.trackpoints[i].speed_meters_per_second < 20) {
      activity_data.trackpoints[i].pace_seconds_per_km = xhale.helper.meters_per_second_as_seconds_per_km(activity_data.trackpoints[i].speed_meters_per_second);
      activity_data.trackpoints[i].pace_seconds_per_mile = xhale.helper.meters_per_second_as_seconds_per_mile(activity_data.trackpoints[i].speed_meters_per_second);
      activity_data.trackpoints[i].speed_km_per_hour = xhale.helper.meters_per_second_to_kph(activity_data.trackpoints[i].speed_meters_per_second);
      activity_data.trackpoints[i].speed_miles_per_hour = xhale.helper.meters_per_second_to_mph(activity_data.trackpoints[i].speed_meters_per_second);
    }

    // Calculate duration in seconds from the start of this session.
    if (first_datetime && activity_data.trackpoints[i].datetime) {
      activity_data.trackpoints[i].cumulative_duration_seconds = (activity_data.trackpoints[i].datetime - first_datetime) / 1000;
    }

    // Record that this is an original trackpoint.
    activity_data.trackpoints[i].type = 'original';

  }

  return activity_data;

}

class SwimLengthRow extends React.Component {
  render() {

    if (pool_unit == 'yards') {
      var cumulative_distance = <span>{xhale.helper.decimal_places(xhale.helper.meters_to_yards(this.props.length.cumulative_distance_meters))}<span className="unit">yds</span></span>
    }
    else {
      var cumulative_distance = <span>{this.props.length.cumulative_distance_meters}<span className="unit">m</span></span>
    }

    return (
      <tr className={'lengths-row ' + this.props.visibility}>
        <td><div className="cell-content">{this.props.length.stroke}</div></td>
        <td><div className="cell-content">{cumulative_distance}</div></td>
        <td><div className="cell-content">{xhale.helper.decimal_places(this.props.length.duration_seconds, 2, 'zero', true)}</div></td>
        <td><div className="cell-content">{xhale.helper.format_minutes_seconds(xhale.helper.meters_per_second_as_seconds_per_hectometer(this.props.length.get_speed_meters_per_second()))}</div></td>
        <td><div className="cell-content">{this.props.length.count}</div></td>
        <td></td>
        <td><div className="cell-content">{this.props.length.strokes}</div></td>
      </tr>
    );

  }
}

class LapTable extends React.Component {

  /**
   * The lap compare option has been pressed.
   */
  handleCompareClicked = (lap) => {
    activities_section.refs['activity_section_' + this.props.base_activity_id].toggleCompareLap(lap.id);
    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({active_summary_tab: 'compare_laps'});
  }

  /**
   * The lap has been updated.
   */
  handleLapUpdate = (updated_lap) => {
    activities_section.state.laps[updated_lap.id] = updated_lap;
    activities_section.setState({laps: activities_section.state.laps});
  }

  /**
   * The lap row has been highlighted so display that section on the charts.
   */
  handleLapRowHighlighted = (lap) => {
    var first_trackpoint_index = this.props.activity_data[lap.activity_id].trackpoint_id_lookup[lap.first_trackpoint_id];
    var last_trackpoint_index = this.props.activity_data[lap.activity_id].trackpoint_id_lookup[lap.last_trackpoint_id];
    var first_trackpoint = this.props.activity_data[lap.activity_id].trackpoints[first_trackpoint_index];
    var last_trackpoint = this.props.activity_data[lap.activity_id].trackpoints[last_trackpoint_index];

    // Get the x position for the start and last track points on the graphs.
    // This has to be based on the filtered trackpoints since that is all that
    // is displayed on the actual chart.
    var first_reduced_trackpoint = activities_section.state.reduced_trackpoints[lap.activity_id][parseInt(first_trackpoint.reduced_array_index)];
    var last_reduced_trackpoint = activities_section.state.reduced_trackpoints[lap.activity_id][parseInt(last_trackpoint.reduced_array_index)];
    if (this.props.x_scale_mode == 'distance') {
      first_trackpoint.x_scale_position = x_distance_scale[this.props.base_activity_id](first_reduced_trackpoint.distance_meters);
      last_trackpoint.x_scale_position = x_distance_scale[this.props.base_activity_id](last_reduced_trackpoint.distance_meters);
    }
    else {
      first_trackpoint.x_scale_position = x_duration_scale[this.props.base_activity_id](first_reduced_trackpoint.cumulative_duration_seconds);
      last_trackpoint.x_scale_position = x_duration_scale[this.props.base_activity_id](last_reduced_trackpoint.cumulative_duration_seconds);
    }

    activities_section.refs['activity_section_' + this.props.base_activity_id].state.highlighted_trackpoints.first = first_trackpoint;
    activities_section.refs['activity_section_' + this.props.base_activity_id].state.highlighted_trackpoints.last = last_trackpoint;

    activities_section.refs['activity_section_' + this.props.base_activity_id].updateHighlightedLap(lap.activity_id, lap.id);
    if (xhale_meta_data.has_premium_features) {
      activities_section.refs['activity_section_' + this.props.base_activity_id].separateLaps();
    }
    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({active_summary_tab: 'highlighted'});
  }

  toggleLapsetEditMode = (activity_id) => {
    if (this.props.lapset_edit_mode.active) {

      var chart_display_mode = (this.props.current_lapset_id == 'overall_activity' && this.props.visible_lapset_activities.length == 0) ? 'normal' : 'compare_activities';

      activities_section.refs['activity_section_' + this.props.base_activity_id].setState({
        chart_display_mode: chart_display_mode,
        lapset_edit_mode: {
          active: false,
        }
      });

    }
    else {
      // Place this lapset into edit mode so we can add or remove laps and
      // create new laps on the chart.
      activities_section.refs['activity_section_' + this.props.base_activity_id].setState({
        chart_display_mode: 'normal',
        lapset_edit_mode: {
          active: true,
          editing_lapset: this.props.current_lapset_id,
          editing_activity: parseInt(activity_id)
        }
      });
    }
  }

  updateLap = (lap, new_lapset_activity_summaries, lapset, is_new_lapset) => {

    var laps = activities_section.state.laps;
    laps[lap.id] = new Lap(lap);

    var lapset_activity_summaries = activities_section.refs['activity_section_' + this.props.base_activity_id].state.lap_set_activity_summaries;
    if (new_lapset_activity_summaries) {
      for (var i = 0; i < new_lapset_activity_summaries.length; i++) {
        var activity_summary = new_lapset_activity_summaries[i]
        lapset_activity_summaries[activity_summary.activity_id][activity_summary.lapset_id] = activity_summary;
      }
    }

    var new_state = {
      laps: laps,
      lap_set_activity_summaries: lapset_activity_summaries,
    }

    if (is_new_lapset && activities_section.refs['activity_section_' + this.props.base_activity_id].state.current_lapset_id == 'new_lapset') {

      var lapsets = activities_section.refs['activity_section_' + this.props.base_activity_id].state.lapsets;
      lapsets[lapset.id] = new LapSet(lapset.id, lapset.name)
      delete lapsets.new_lapset;
      new_state.lapsets = lapsets;

      new_state.current_lapset_id = lapset.id;

      var lapset_edit_mode = activities_section.refs['activity_section_' + this.props.base_activity_id].state.lapset_edit_mode;
      lapset_edit_mode.editing_lapset = lapset.id;
      new_state.lapset_edit_mode = lapset_edit_mode;
    }

    activities_section.refs['activity_section_' + this.props.base_activity_id].setState(new_state);

  }

  showAllLaps = () => {
    let laps = activities_section.state.laps;
    Object.keys(laps).forEach((lap_id) => {
      laps[lap_id].visible = true;
    });
    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({laps: laps});
  }

  hideRestLaps = () => {
    let laps = activities_section.state.laps;
    Object.keys(laps).forEach((lap_id) => {
      if (laps[lap_id].swim_stroke == 'resting') {
        laps[lap_id].visible = false;
      }
    });
    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({laps: laps});
  }

  render() {

    var self = this;
    var number_of_activities = Object.keys(this.props.activity_data).length;

    // Determine which fields have any data in them so that only those columns
    // will be created in the table.
    var lap_features = {
      'distance': false,
      'duration': false,
      'speed': false,
      'height_climbed': false,
      'heart_rate': false,
      'cadence': false,
      'normalised_power': false,
      'average_power': false,
      'num_lengths': false,
      'has_lengths': false,
      'swim_stroke': false,
      'hideable': true,
      'comparable': true
    };

    for (var index = 0; index < this.props.laps.length; index++) {
      var lap = this.props.laps[index];
      if (lap.distance_meters) {
        lap_features.distance = true;
      }
      if (lap.duration_seconds) {
        lap_features.duration = true;
      }
      if (lap.get_speed_meters_per_second) {
        lap_features.speed = true;
      }
      if (lap.height_climbed_meters && this.props.discipline != 'swimming') {
        lap_features.height_climbed = true;
      }
      if (lap.average_heart_rate_bpm) {
        lap_features.heart_rate = true;
      }
      if (lap.average_cadence_rpm && this.props.discipline != 'swimming') {
        lap_features.cadence = true;
      }
      if (lap.normalized_power_watts) {
        lap_features.normalized_power = true;
      }
      if (lap.average_power_watts) {
        lap_features.average_power = true;
      }
      if (lap.swim_stroke && lap.swim_stroke != 'resting') {
        lap_features.swim_stroke = true;
      }
      if (lap.num_lengths) {
        lap_features.num_lengths = true;
      }
      if (lap.lengths.length > 0) {
        lap_features.has_lengths = true;
      }
    }
    // Create the appropriate header columns based on the what data was found.
    var lap_table_headers = [];
    var total_columns = 0;

    if (this.props.current_lapset.id != 'overall_activity') {
      lap_table_headers.push(<th key="lap-number" className="lap-number">Lap</th>);
      total_columns++;
    }

    if (lap_features.swim_stroke) {
      lap_table_headers.push(<th key="swim-stroke" className="swim-stroke">Stroke</th>);
      total_columns++;
    }

    if (lap_features.distance) {
      if (this.props.discipline == 'swimming') {
        lap_table_headers.push(<th key="distance" className="distance">Total distance</th>);
        total_columns++;
      }
      else {
        lap_table_headers.push(<th key="distance" className="distance">Distance</th>);
        total_columns++;
      }
    }
    if (lap_features.duration) {
      var header_text = (this.props.discipline == 'swimming') ? 'Lap time' : 'Duration';
      lap_table_headers.push(<th key="duration" className="duration">{header_text}</th>);
      total_columns++;
    }
    if (lap_features.speed && this.props.discipline == 'running') {
      lap_table_headers.push(<th key="speed" className="speed">Average pace</th>);
      total_columns++;
    }
    else if (lap_features.speed && this.props.discipline == 'swimming') {
      lap_table_headers.push(<th key="speed" className="speed">Pace min/100m</th>);
      total_columns++;
    }
    else {
      lap_table_headers.push(<th key="speed" className="speed">Average speed</th>);
      total_columns++;
    }
    if (lap_features.height_climbed) {
      lap_table_headers.push(<th key="height-climbed" className="height-climbed">Climbed</th>);
      total_columns++;
    }
    if (lap_features.heart_rate) {
      lap_table_headers.push(<th key="heart-rate" className="heart-rate">Heart</th>);
      total_columns++;
    }
    if (lap_features.average_power) {
      lap_table_headers.push(<th key="power" className="average-power">Power</th>);
      total_columns++;
    }
    if (lap_features.normalized_power) {
      lap_table_headers.push(<th key="normalized-power" className="normalized-power">Normalised</th>);
      total_columns++;
    }
    if (lap_features.cadence) {
      lap_table_headers.push(<th key="cadence" className="cadence">Cadence</th>);
      total_columns++;
    }
    if (lap_features.num_lengths || lap_features.has_lengths) {
      lap_table_headers.push(<th key="lengths" className="num-lengths" colSpan="2">Lengths</th>);
      total_columns += 2;
    }

    if (number_of_activities == 1 && this.props.current_lapset.id != 'overall_activity') {
      lap_table_headers.push(
        <th key="menu" className="menu">
          <div className="cell-content"><span className="table-button table-header-button" style={{color: 'black'}} onClick={() => this.toggleLapsetEditMode(this.props.base_activity_id)}>edit laps</span></div>
        </th>
      );
    }
    else {
      lap_table_headers.push(<th key="menu" className="menu"></th>);
    }

    total_columns++;

    // Create a structure for organising lap rows.
    var lap_rows_by_activity = {};
    if (this.props.lapset_edit_mode.active) {
      lap_rows_by_activity[this.props.lapset_edit_mode.editing_activity] = [];
    }
    else {
      lap_rows_by_activity[this.props.base_activity_id] = [];
      for (var index = 0; index < this.props.visible_lapset_activities.length; index++) {
        lap_rows_by_activity[this.props.visible_lapset_activities[index]] = [];
      }
    }

    // Create the table rows for each lap.
    for (var index = 0; index < this.props.laps.length; index++) {

      var lap = this.props.laps[index];

      // Only display laps in displayed activities.
      if (!(lap.activity_id in lap_rows_by_activity)) {
        continue;
      }
      var row_highlighted = (lap.id == this.props.highlighted_lap) ? true : false;

      if (this.props.discipline == 'swimming') {
        if (lap.swim_stroke == 'resting') {
          lap_rows_by_activity[lap.activity_id].push(
            <SwimLapRow
              key={lap.id}
              lap={lap}
              lap_features={lap_features}
              discipline={this.props.discipline}
              pool_unit={pool_unit}
              updateLap={this.updateLap}
              setSwimLapMouseHover={this.props.setSwimLapMouseHover}
            />
          );
        }
        else {
          lap_rows_by_activity[lap.activity_id].push(
            <SwimLapRow
              key={lap.id}
              lap={lap}
              selected_lap_ids={this.props.selected_lap_ids}
              lap_features={lap_features}
              discipline={this.props.discipline}
              pool_unit={pool_unit}
              highlighted={row_highlighted}
              highlightable={this.props.highlightable}
              updateLap={this.updateLap}
              setSwimLapMouseHover={this.props.setSwimLapMouseHover}
            />
          );

          var visibility_class = lap.lengths_visible ? 'visible' : 'hidden';

          if (lap_features.has_lengths) {
            lap_rows_by_activity[lap.activity_id].push(
              <tr key={'length-header-' + lap.id} className={'lengths-header ' + visibility_class}>
                <th>Stroke</th>
                <th>Distance</th>
                <th>Length time</th>
                <th>Pace min/100m</th>
                <th>Length #</th>
                <th></th>
                <th>Strokes</th>
              </tr>
            );
            var length_rows = [];
            for (var length_index = 0; length_index < lap.lengths.length; length_index++) {
              lap_rows_by_activity[lap.activity_id].push(<SwimLengthRow key={'length-' + lap.lengths[length_index].id} length={lap.lengths[length_index]} visibility={visibility_class}/>)
            }
          }
        }
      }
      else {

        var lap_number = (this.props.current_lapset.id != 'overall_activity') ? lap_rows_by_activity[lap.activity_id].length + 1 : null;

        lap_rows_by_activity[lap.activity_id].push(
          <LapRow
            key={lap.id}
            athlete_id={this.props.athlete_id}
            lap={lap}
            lap_number={lap_number}
            base_activity_id={this.props.base_activity_id}
            lap_features={lap_features}
            discipline={this.props.discipline}
            highlighted={row_highlighted}
            highlightable={this.props.highlightable}
            current_lapset={this.props.current_lapset}
            handleRowHighlighted={this.handleLapRowHighlighted}
            handleCompareClicked={this.handleCompareClicked}
            handleLapUpdate={this.handleLapUpdate}
            distance_unit={this.props.distance_unit}
            csrf_token={this.props.csrf_token}
            lapset_edit_mode={this.props.lapset_edit_mode}
            updateLap={this.updateLap}
            activity_colour={this.props.visible_lapset_activity_colours[lap.activity_id]}
          />
        );
      }
    }

    // Organise the final set of table rows.
    var table_rows = [];

    Object.keys(lap_rows_by_activity).forEach((activity_id) => {

      let lap_rows = lap_rows_by_activity[activity_id];

      // Display a header making it easier to see which activity this is, if multiple activities are listed.
      if (number_of_activities > 1) {

        var colour = this.props.visible_lapset_activity_colours[activity_id];

        if (this.props.lapset_edit_mode.active) {
          var add_laps_text = 'Finish editing laps'
        }
        else {
          var add_laps_text = 'Edit laps'
        }

        var start_time = newZuluDatetime(this.props.activity_data[activity_id].start_time);
        table_rows.push(
          <tr key={'activity-header-' + activity_id} className="activity-header" style={{borderTop: '3px solid #515151'}}>
            <td colSpan={total_columns -1 } style={{textAlign: 'left', padding: '7px 5px 2px', color: colour, background: '#efefef'}}>{start_time.format('jS M Y g:ia')}</td>
            <td className="menu" style={{background: '#efefef'}}><div className="cell-content"><span className="table-button table-header-button" onClick={() => this.toggleLapsetEditMode(activity_id)}>{add_laps_text}</span></div></td>
          </tr>
        );

      }

      for (var i = 0; i < lap_rows.length; i++) {
        table_rows.push(lap_rows[i]);
      }

      if (table_rows.length == 0) {
        table_rows.push(<tr key={'activity-no-laps-' + activity_id}><td colSpan={total_columns} style={{textAlign: 'left', padding: '7px 5px 2px', fontStyle: 'italic', color: '#a7a7a7'}}>No laps have been saved to this selection</td></tr>);
      }

    });

    var title;
    if (this.props.creator != 'any') {
      title = <h2>Laps saved by {this.props.creator}</h2>;
    }

    let lap_hide_buttons = [];
    if (this.props.discipline == 'swimming') {

      let hide_rest_laps_button_disabled_active = 'disabled';
      let show_all_laps_button_disabled_active = 'disabled';
      Object.keys(this.props.laps).forEach((lap_id) => {

        if (this.props.laps[lap_id].swim_stroke == 'resting' && !(this.props.laps[lap_id].visible === false)) {
          hide_rest_laps_button_disabled_active = 'active';
        };

        if (this.props.laps[lap_id].visible === false) {
          show_all_laps_button_disabled_active = 'active';
        }

      });

      lap_hide_buttons.push(<div key="hide-rest" className={"small-button small-button--dark small-button_" + hide_rest_laps_button_disabled_active} onClick={this.hideRestLaps}>hide rest laps</div>);
      lap_hide_buttons.push(<div key="show-all" className={"small-button small-button--dark small-button_" + show_all_laps_button_disabled_active} onClick={this.showAllLaps}>show all</div>);

    }

    return (
      <div className={'lap-table ' + this.props.creator}>
        <div className="laps-container">
          {title}
          <span style={{float: 'right', margin: '-24px 5px 8px 0'}} className="small-buttons-group">{lap_hide_buttons}</span>
          <table>
            <thead>
              <tr>
                {lap_table_headers}
              </tr>
            </thead>
            <tbody>
              {table_rows}
            </tbody>
          </table>
        </div>
      </div>
    );

  }
}

/**
 * React component representing the section to download the original training file.
 */
class EditActivitySection extends React.Component {

  handleDetachActivityClicked = () => {
    activateConfirmationBox({
      message: "Are you sure you want to detach this activity from the session?",
      confirm_callback: this.confirmDetachActivity,
      yes_button_text: "Yes, remove it",
      show_saving_message: "Removing activity...",
    });
  }

  confirmDetachActivity = () => {

    const post_data = {
      activity_id: this.props.activity_id,
      csrfmiddlewaretoken: this.props.csrf_token,
    }

    ajaxRequest({
      url: '/coach/detach-activity/',
      data: post_data,
      method: 'POST',
      success: this.activityDetached,
      send_as_form_values: true,
      expected_response_type: 'json',
    });

  }

  activityDetached = (result, status) => {
    window.location.reload(false);
  }

  render() {

    if (typeof(this.props.activity_data[this.props.activity_id]) == 'undefined') {
      return null;
    }
    else {

      var download_button;
      if (hasBetaAccess('download_training_files')) {
        var download_url = '/training-logs/download-training-file/' + this.props.activity_data[this.props.activity_id].file_id + '/';
        download_button = (
          <div style={{display: 'block', marginBottom: '25px', color: 'white'}}>
            <p style={{margin: '0 0 5px 0'}}>Download your original training file for this activity.</p>
            <div className="download-file small-button">
              <a style={{textDecoration: 'none'}} href={download_url}>Download training file</a>
            </div>
          </div>
        );
      }

      var detach_button;
      if (this.props.athlete_id == xhale_meta_data.user_id || hasBetaAccess('coach_import_athlete_training')) { // TODO When we get rid of the 'coach_import_athlete_training' we need a better way of checking permission here.
        var detach_button = (
          <div style={{display: 'block', color: 'white'}}>
            <p style={{margin: '0 0 5px 0'}}>If this activity was imported to the wrong training session press this button to remove it.</p>
            <div className="detach-activity-button small-button">
              <span onClick={this.handleDetachActivityClicked}>Delete activity from this session</span>
            </div>
          </div>
        );
      }

      return (
        <div style={{margin: '15px', color: 'black'}}>
          {download_button}
          {detach_button}
        </div>
      );
    }

  }
}

/**
 * React component representing the section to download the original training file.
 */
class ZonesSection extends React.Component {

  colors = ['#686868', COLOURS.intensity[1], COLOURS.intensity[2], COLOURS.intensity[3], COLOURS.intensity[4], COLOURS.intensity[5], '#686868'];
  has_heart_rate = null;
  has_power = null;
  has_zone_data = null;

  componentDidMount = () => {
    this.createChart('heart-rate', 'duration_heart_rate');
    this.createChart('power', 'duration_power');
  }
 
  hasZoneData = (metric) => {

    let key_min = metric + '_min';
    let key_max = metric + '_max';

    if (this.has_zone_data == null) {
      this.has_zone_data = false;

      let sport;
      if (this.props.discipline == 'cycling') {
        sport = 'bike';
      }
      else if (this.props.discipline == 'running') {
        sport = 'run';
      }
      else {
        this.has_zone_data = false;
        return this.has_zone_data;
      }

      for (let i = 0; i < this.props.athlete_data.training_zones[sport].zones.length; i++) {
        if (this.props.athlete_data.training_zones[sport].zones[i][key_min] && this.props.athlete_data.training_zones[sport].zones[i][key_max]) {
          this.has_zone_data = true;
          return this.has_zone_data;
        }
      }
    }
    return this.has_zone_data;
  }

  hasHeartRate = () => {
    if (this.has_heart_rate == null) {
      this.has_heart_rate = false;
      Object.keys(this.props.activity_data.time_in_zones).forEach((zone_id) => {
        if (this.props.activity_data.time_in_zones[zone_id].duration_heart_rate > 0) {
          this.has_heart_rate = true;
          return;
        }
      });
    }
    return this.has_heart_rate;
  }

  hasPower = () => {
    if (this.has_power == null) {
      this.has_power = false;
      Object.keys(this.props.activity_data.time_in_zones).forEach((zone_id) => {
        if (this.props.activity_data.time_in_zones[zone_id].duration_power > 0) {
          this.has_power = true;
          return;
        }
      });
    }
    return this.has_power;
  }

  createChart = (classname, metric) => {

    let container_id = '#activity-' + this.props.activity_id + '-' + classname + '-zones-chart';

    let margin = {top: 10, right: 32, bottom: 35, left: 40};
    let width = $(container_id).width() - margin.left - margin.right;
    const FULL_HEIGHT = 225;
    let height = FULL_HEIGHT - margin.top - margin.bottom;

    let sport;
    if (this.props.discipline == 'cycling') {
      sport = 'bike';
    }
    else if (this.props.discipline == 'running') {
      sport = 'run';
    }

    let max_value = 0;
    let total_duration = 0;
    let data = [];

    let zone_below = (this.props.activity_data.time_in_zones.below[metric]) ? this.props.activity_data.time_in_zones.below[metric] : 0;
    data.push({ key: 'below', value: zone_below });
    if (zone_above > max_value) {
      max_value = zone_below;
    }
    total_duration += zone_below; 

    for (let i = 0; i < this.props.athlete_data.training_zones[sport].zones.length; i++) {
      let zone = this.props.athlete_data.training_zones[sport].zones[i];
      let value = (this.props.activity_data.time_in_zones[zone.id][metric]) ? this.props.activity_data.time_in_zones[zone.id][metric] : 0;
      data.push({ key: zone.zone_id, value: value });
      total_duration += value;
      if (value > max_value) {
        max_value = value;
      }
    }

    let zone_above = (this.props.activity_data.time_in_zones.above[metric]) ? this.props.activity_data.time_in_zones.above[metric] : 0;
    data.push({ key: 'above', value: zone_above });
    total_duration += zone_above;
    if (zone_above > max_value) {
      max_value = zone_above;
    }

    // Scales.
    let x_scale = scaleLinear()
      .range([0, width])
      .domain([0, max_value])

    let y_scale = scaleBand()
      .domain(['below', 1, 2, 3, 4, 5, 'above'])
      .range([height, 0])

    // Set up the empty chart.
    let xAxis = axisBottom()
      .scale(x_scale)
      .ticks(calculateNumberOfTicks(width, 55))
      .tickFormat(formatHoursMinutesFromSeconds);

    let yAxis = axisLeft()
      .scale(y_scale)

    let bar_height = height / 7

    let chart_container = select(container_id + ' .chart')
      .attr("width", width + margin.left + margin.right);

    let chart = chart_container.append("g")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    chart.selectAll('rect')
      .data(data)
      .enter().append('rect')
        .style('fill', (d, i) => { return this.colors[i] })
        .classed('class', 'zone') 
        .attr('class', function(d) { return 'zone-' + d.key })
        .attr('width', function(d) { return x_scale(d.value) })
        .attr('x', function(d, i) { return 0 })
        .attr('y', (d, i) => { return y_scale(d.key) })
        .attr('height', bar_height - 1);

    chart.selectAll(".value-as-time")
      .data(data)
      .enter().append("text")
        .text(function(d) { return formatHoursMinutesFromSeconds(d.value) })
        .style("fill", "currentColor")
        .attr("class", function(d, i) { return "value-as-time value-as-time-" + d.key})
        .attr("x", function(d, i) { return x_scale(d.value) + 3 })
        .attr('y', function(d, i) { return y_scale(d.key) + 9 })

    chart.selectAll(".value-as-percent")
      .data(data)
      .enter().append("text")
        .text(function(d) {
          return decimalPlaces((d.value / total_duration) * 100, 1, 'zero') + '%';
        })
        .style("fill", "currentColor")
        .attr("class", function(d, i) { return "value-as-percent value-as-percent-" + d.key})
        .attr("x", function(d, i) { return x_scale(d.value) + 3 })
        .attr('y', function(d, i) { return y_scale(d.key) + 22})

    chart.append("g")
      .attr("transform", "translate(0," + height + ")")
      .call(xAxis);

    chart.append("g")
      .style("color", "#EEEEEE")
      .call(yAxis);

    chart.append("g")
      .append("text")
        .attr("class", "x-axis-label")
        .text('Duration')
        .style("font-size", "11px")
        .style("fill", "currentColor")
        .attr("dx", width / 2)
        .attr("dy", FULL_HEIGHT - 10)
        .style("text-anchor", "middle")

    chart.append("g")
      .append("text")
        .attr("class", "y-axis-label")
        .text('Zone')
        .style("font-size", "11px")
        .style("fill", "currentColor")
        .attr("transform", "rotate(270)")
        .style("text-anchor", "middle")
        .attr("dx", 0 - (height / 2))
        .attr("dy", 10 - margin.left);

  }

  render() {

    let heart_rate_chart;
    if (this.hasHeartRate()) {
      heart_rate_chart = (
        <div
          id={'activity-' + this.props.activity_id + '-heart-rate-zones-chart'}
          className="heart-rate-zones-chart"
          style={{float: 'left', height: '230px', width: '49.5%', margin: '0 1% 0 0', position: 'relative'}}
        >
          <h3 style={{ fontWeight: 'normal', margin: 0 }}>Time in Heart Rate Zones</h3>
          <svg style={{height: '100%'}} className="chart"></svg>
        </div>
      );
    }

    let power_chart;
    if (this.hasPower()) {
      power_chart = (
        <div
          id={'activity-' + this.props.activity_id + '-power-zones-chart'}
          className="power-zones-chart"
          style={{float: 'left', height: '230px', width: '49.5%', margin: '0', position: 'relative'}}
        >
          <h3 style={{ fontWeight: 'normal', margin: 0 }}>Time in Power Zones</h3>
          <svg style={{height: '100%'}} className="chart"></svg>
        </div>
      );
    }

    let empty_text;
    let missing_power_zones;
    let missing_heart_rate_zones;
    if (!this.hasPower() && !this.hasHeartRate()) {
      empty_text = <p>No time in heart rate or power zones to display.</p>
      if (!this.hasZoneData('power')) {
        missing_heart_rate_zones = <p>Heart rate zones for {this.props.discipline} have not been entered in your account settings.</p>
        missing_power_zones = <p>Power zones for {this.props.discipline} have not been entered in your account settings.</p>
      }
    }

    return (
      <div style={{margin: '15px', color: '#eee'}}>
        {heart_rate_chart}
        {power_chart}
        {empty_text}{missing_heart_rate_zones}{missing_power_zones}
      </div>
    );

  }

}


/**
 * React component: Display data comparing sessions.
 */
class CompareSessionsDataView extends React.Component {

  setCurrentSet = (lapset_id) => {
    var chart_display_mode = (lapset_id == 'overall_activity' && this.props.visible_lapset_activities.length == 0) ? 'normal' : 'compare_activities';
    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({
      chart_display_mode: chart_display_mode,
      current_lapset_id: lapset_id
    });
  }

  /**
   * Get a list of sets related to activities in this session.
   */
  getSetChoices = () => {
    let set_choices = {};
    Object.keys(this.props.lapsets).forEach((set_id) => {
      const lap_set = this.props.lapsets[set_id];
      set_choices[lap_set.id] = {
        id: lap_set.id,
        name: lap_set.name
      };
    });
    return set_choices;
  }

  /**
   * Work out which charts are supported based on whether certain data
   * was recorded.
   */
  getSupportedMetrics = () => {

    // Initially set everything not being supported.
    var supported_metrics = {
      'lap_count': false,
      'distance': false,
      'duration': false,
      'speed': false,
      'height_climbed': false,
      'heart_rate': false,
      'cadence': false,
      'normalised_power': false,
      'average_power': false,
      'num_lengths': false,
      'has_lengths': false,
      'swim_stroke': false
    }

    var discipline = this.props.activity_data[this.props.base_activity_id].discipline;

    // Cycle through each lapset activity summary and determine if the metric
    // exists in any of them for the currently visible set.
    for (var activity_index = 0; activity_index < this.props.activity_summaries.length; activity_index++) {

      var current_activity_id = this.props.activity_summaries[activity_index].activity_id;
      var current_lapset_activity_summaries = this.props.lap_set_activity_summaries[current_activity_id];

      if (!current_lapset_activity_summaries) {
        continue;
      }

      var lapset_activity_summary = current_lapset_activity_summaries[this.props.current_lapset_id];

      if (!lapset_activity_summary) {
        continue;
      }

      if (lapset_activity_summary.lap_count) {
        supported_metrics.lap_count = true;
      }

      if (lapset_activity_summary.distance_meters) {
        supported_metrics.distance = true;
      }

      if (lapset_activity_summary.duration_seconds) {
        supported_metrics.duration = true;
      }

      if (lapset_activity_summary.distance_meters && lapset_activity_summary.duration_seconds) {
        supported_metrics.speed = true;
      }

      if (lapset_activity_summary.total_climbed_meters && discipline != 'swimming') {
        supported_metrics.height_climbed = true;
      }

      if (lapset_activity_summary.average_heart_rate_bpm) {
        supported_metrics.heart_rate = true;
      }

      if (lapset_activity_summary.average_cadence_rpm && discipline != 'swimming') {
        supported_metrics.cadence = true;
      }

      if (lapset_activity_summary.normalized_power_watts) {
        supported_metrics.normalized_power = true;
      }

      if (lapset_activity_summary.average_power_watts) {
        supported_metrics.average_power = true;
      }

    }

    return supported_metrics;
  }

  deleteActivityFromSelection = (activity) => {
    this.props.compare_sessions_current_selection.unsaved_session_ids = removeFromArrayIfExists(this.props.compare_sessions_current_selection.unsaved_session_ids, activity.training_session_id)
    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({
      compare_sessions_current_selection: this.props.compare_sessions_current_selection
    });
  }

  addActivityToSelection = (activity) => {
    this.props.compare_sessions_current_selection.unsaved_session_ids = addToArrayIfNotExists(this.props.compare_sessions_current_selection.unsaved_session_ids, activity.training_session_id)
    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({
      compare_sessions_current_selection: this.props.compare_sessions_current_selection
    });
  }

  toggleDisplayActivityOnChart = (activity_id, callback) => {

    var currently_visible = activities_section.refs['activity_section_' + this.props.base_activity_id].state.visible_lapset_activities.indexOf(parseInt(activity_id)) > -1 ? true : false;

    if (currently_visible) {
      this.hideActivityOnChart(activity_id);
    }
    else {
      this.displayActivityOnChart(activity_id, callback);
    }

  }

  displayActivityOnChart = (activity_id, callback) => {

    const displayActivity = () => {
      var visible_lapset_activities = activities_section.refs['activity_section_' + this.props.base_activity_id].state.visible_lapset_activities.slice();

      // Ensure this activity's data is marked as visible.
      if (activity_id != this.props.base_activity_id) {
        addToArrayIfNotExists(visible_lapset_activities, parseInt(activity_id));
      }

      // Reassign colours to the extra activities.
      var visible_lapset_activity_colours = this.assignColours(visible_lapset_activities, activities_section.refs['activity_section_' + this.props.base_activity_id].state.visible_lapset_activity_colours);

      activities_section.refs['activity_section_' + this.props.base_activity_id].setState({
        visible_lapset_activities: visible_lapset_activities,
        visible_lapset_activity_colours: visible_lapset_activity_colours,
        chart_display_mode: 'compare_activities',
        lapset_edit_mode: {active: false},
      }, () => {
        if (callback) {
          callback();
        }
      });
    }

    // If the data doesn't exist, load it before trying to display it.
    // Otherwise display it straight away.
    if (typeof(this.props.activity_data[activity_id].trackpoints) == 'undefined') {

      ajaxRequest({
        url: '/coach/load-activity-data/' + activity_id + '/',
        method: 'GET',
        error: handleLoggedOutError,
        expected_response_type: 'json',
        success: (data) => {
          storeActivityData(this.props.base_activity_id, activity_id, data[0]);
          displayActivity();
        }
      });

    }
    else {
      displayActivity();
    }

  }

  hideActivityOnChart = (activity_id) => {

    // Ensure this activity's data is marked as hidden.
    var visible_lapset_activities = activities_section.refs['activity_section_' + this.props.base_activity_id].state.visible_lapset_activities.slice();
    var activity_index = visible_lapset_activities.indexOf(activity_id);
    visible_lapset_activities.splice(activity_index, 1);

    var visible_lapset_activity_colours = this.assignColours(visible_lapset_activities, activities_section.refs['activity_section_' + this.props.base_activity_id].state.visible_lapset_activity_colours);

    var chart_display_mode = (visible_lapset_activities.length > 0) ? 'compare_activities' : 'normal';

    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({
      visible_lapset_activity_colours: visible_lapset_activity_colours,
      visible_lapset_activities: visible_lapset_activities,
      chart_display_mode: chart_display_mode,
      lapset_edit_mode: {active: false},
    });

  }

  assignColours = (visible_lapset_activities, current_colours) => {

    var new_activity_colours = {};

    var discipline = this.props.activity_data[this.props.base_activity_id].discipline;

    // Ensure colour stack is big enough for the number of activities.
    var colour_stack = [];
    while (visible_lapset_activities.length > colour_stack.length) {
      colour_stack = colour_stack.concat(highlight_colours);
    }

    // For running and cycling remove the first red from the stack as this is
    // too similar to the main activity colour. For swimming remove the blue.
    // Then set the appropriate colour for the base activity.
    if (discipline == 'running' || discipline == 'cycling') {
      colour_stack.splice(colour_stack.indexOf('#DF0000'), 1);
      if (discipline == 'running') {
        new_activity_colours[this.props.base_activity_id] = xhale.variables.colours.run;
      }
      else if (discipline == 'cycling') {
        new_activity_colours[this.props.base_activity_id] = xhale.variables.colours.bike;
      }
    }
    else if (discipline == 'swimming') {
      colour_stack.splice(colour_stack.indexOf('#5757FF'), 1);
      new_activity_colours[this.props.base_activity_id] = xhale.variables.colours.swim;
    }
    else if (discipline == 'gym') {
      new_activity_colours[this.props.base_activity_id] = xhale.variables.colours.gym;
    }
    else {
      new_activity_colours[this.props.base_activity_id] = xhale.variables.colours.other;
    }

    // Make sure all activities have a colour assigned to them.
    for (var i = 0; i < visible_lapset_activities.length; i++) {
      if (typeof current_colours[visible_lapset_activities[i]] != 'undefined') {

        // Reapply the existing colour to this activity.
        new_activity_colours[visible_lapset_activities[i]] = current_colours[visible_lapset_activities[i]];

        // Take this colour off the colour stack so it isn't applied twice.
        var colour_index = colour_stack.indexOf(current_colours[visible_lapset_activities[i]]);
        if (colour_index >= 0) {
          colour_stack.splice(colour_index, 1);
        }
      }
      else {
        // The activity has not yet got a colour applied, so add one from the colour stack.
        new_activity_colours[visible_lapset_activities[i]] = colour_stack.shift();
      }
    }

    return new_activity_colours;

  }

  handleSelectedSessionSaved = (data, status) => {
    var collection = data.session_collection;
    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({
      compare_sessions_current_selection: new SessionCollection(collection),
    });
  }

  updateActivitySection = (newStateValues) => {
    activities_section.refs['activity_section_' + this.props.base_activity_id].setState(newStateValues);
  }

  handleTableButtonClicked = () => {
    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({compare_sessions_current_view: 'table'});
  }

  handleChartsButtonClick = () => {
    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({compare_sessions_current_view: 'charts'});
  }

  openSaveSelectionForm = (selection_to_save, base_activity_id, selectedSessionSavedCallback) => {

    var save_selection_form = document.createElement("div");
    document.body.appendChild(save_selection_form);

    var closeSaveSelectionForm  = function () {
      ReactDOM.unmountComponentAtNode(save_selection_form);
      document.body.removeChild(save_selection_form);
    }

    ReactDOM.render(
      <SaveSelectionForm
        selection={selection_to_save}
        base_activity_id={base_activity_id}
        csrf_token={this.props.csrf_token}
        handleSelectedSessionSaved={selectedSessionSavedCallback}
        closeSessionCollectionForm={closeSaveSelectionForm}
      />, save_selection_form
    );

  }

  /**
   * Implement React's render.
   */
  render() {

    // Wait for the activity data to load befor rendering this section as we.
    // need to at least know the discipline.
    if (!this.props.activity_data[this.props.base_activity_id]) {
      return null;
    }

    var discipline = this.props.activity_data[this.props.base_activity_id].discipline;

    // The options to switch between lap sets.
    var set_choices = this.getSetChoices();
    var set_choice_buttons = [];
    Object.keys(set_choices).forEach((set_id) => {
      set_choice_buttons.push(
        <SetButton
          key={set_id}
          lapset={set_choices[set_id]}
          current_lapset_id={this.props.current_lapset_id}
          setCurrentSet={this.setCurrentSet}
        />
      );
    });

    set_choice_buttons.push(
      <NewSetButton
        key='new'
        lapset={{name: 'Add a new selection of laps', id: 'create_new_lapset'}}
        current_lapset_id={this.props.current_lapset_id}
        base_activity_id={this.props.base_activity_id}
        discipline={this.props.activity_data[this.props.base_activity_id].discipline}
        athlete_id={this.props.athlete_id}
        setCurrentSet={this.setCurrentSet}
      />
    );

    // The tabs to choose the current display of data.
    var supported_metrics = this.getSupportedMetrics();

    var current_view_tabs = [];
    // TODO Refactor this so the buttons are in its own component.
    if (this.props.compare_sessions_current_view == 'charts') {
      current_view_tabs.push(<CompareChartOption key="chart_speed" name="Speed" metric="speed" current_view={this.props.compare_sessions_current_view} currently_selected={this.props.compare_sessions_selected_charts} activity_id={this.props.base_activity_id} />);

      if (supported_metrics.cadence) {
        current_view_tabs.push(<CompareChartOption key="chart_cadence" name="Cadence" metric="cadence" current_view={this.props.compare_sessions_current_view} currently_selected={this.props.compare_sessions_selected_charts} activity_id={this.props.base_activity_id} />);
      }
      if (supported_metrics.heart_rate) {
        current_view_tabs.push(<CompareChartOption key="chart_heart_rate" name="Heart rate" metric="heart_rate" current_view={this.props.compare_sessions_current_view} currently_selected={this.props.compare_sessions_selected_charts} activity_id={this.props.base_activity_id} />);
      }
      if (supported_metrics.average_power) {
        current_view_tabs.push(<CompareChartOption key="chart_power" name="Average power" metric="average_power" current_view={this.props.compare_sessions_current_view} currently_selected={this.props.compare_sessions_selected_charts} activity_id={this.props.base_activity_id} />);
      }
    }

    var current_view_buttons = (
      <div className="chart-buttons" style={{float: 'right'}}>
      {current_view_tabs}
      </div>
    );

    // The current display of data.
    var view;

    if (this.props.compare_sessions_current_view == 'charts') {
      view = [];

      for (var i = 0; i < this.props.compare_sessions_selected_charts.length; i++) {

        var metric = this.props.compare_sessions_selected_charts[i];

        switch (metric) {
          case 'speed':
            if (discipline == 'running') {
              var speed_metric = (this.props.distance_unit == 'km') ? 'min/km' : 'min/mile';
            }
            else {
              var speed_metric = (this.props.distance_unit == 'km') ? 'kph' : 'mph';
            }
            var label_text_rows = ['Speed', speed_metric];
            break;
          case 'cadence':
            var label_text_rows = ['Cadence', 'RPM'];
            break;
          case 'average_power':
            var label_text_rows = ['Average', 'Power', 'Watts'];
            break;
          case 'heart_rate':
            var label_text_rows = ['Heart rate', 'BPM'];
            break;
        }

        view.push( 
          <ComparisionChart
            key={this.props.compare_sessions_selected_charts[i]}
            activity_summaries={this.props.activity_summaries}
            laps={this.props.laps}
            lapsets={this.props.lapsets}
            current_lapset_id={this.props.current_lapset_id}
            metric={metric}
            label_text_rows={label_text_rows}
            position={i + 1}
            total_active_metrics={this.props.compare_sessions_selected_charts.length}
            discipline={discipline}
            distance_unit={this.props.distance_unit}
            base_activity_id={this.props.base_activity_id}
            toggleDisplayActivityOnChart={this.toggleDisplayActivityOnChart}
            visible_lapset_activities={this.props.visible_lapset_activities}
            visible_lapset_activity_colours={this.props.visible_lapset_activity_colours}
            lap_set_activity_summaries={this.props.lap_set_activity_summaries}
            compare_sessions_current_selection={this.props.compare_sessions_current_selection}
          />
        );

      }

    }
    else if (this.props.activity_summaries.length > 0 && this.props.compare_sessions_current_view == 'table') {
      view = <SummaryTable
        updateActivitySection={this.updateActivitySection}
        distance_unit={this.props.distance_unit}
        discipline={discipline}
        base_activity_id={this.props.base_activity_id}
        activity_summaries={this.props.activity_summaries}
        laps={this.props.laps}
        lapsets={this.props.lapsets}
        lap_set_activity_summaries={this.props.lap_set_activity_summaries}
        current_lapset_id={this.props.current_lapset_id}
        supported_metrics={supported_metrics}
        visible_lapset_activities={this.props.visible_lapset_activities}
        visible_lapset_activity_colours={this.props.visible_lapset_activity_colours}
        csrf_token={this.props.csrf_token}
        compare_sessions_current_selection={this.props.compare_sessions_current_selection}
        toggleDisplayActivityOnChart={this.toggleDisplayActivityOnChart}
        displayActivityOnChart={this.displayActivityOnChart}
        deleteActivityFromSelection={this.deleteActivityFromSelection}
        addActivityToSelection={this.addActivityToSelection}
        handleOpenCollection={this.props.handleOpenCollection}
        openSaveSelectionForm={this.openSaveSelectionForm}
        handleSelectedSessionSaved={this.handleSelectedSessionSaved}
      />
    }

    return (
      <div>
        <h3 style={{float:'left', margin: '6px'}}>Compare</h3> <div style={{float: 'left'}} className="set-buttons">{set_choice_buttons}</div>
        {current_view_buttons}
        {view}
      </div>
    );

  }
}

class SaveSelectionForm extends React.Component {

  constructor(props) {

    super(props);

    var self = this;

    this.state = {
      name: this.props.selection.name,
      currently_saving: false
    }

  }

  handleNameChange = (event) => {
    this.setState({name: event.target.value});
  }

  handleSelectionFormSubmit = (event) => {

    this.setState({currently_saving: true});

    event.preventDefault();

    var post_data = {
      name: this.state.name,
      athlete_id: this.props.selection.athlete_id,
      session_ids_to_add: this.props.selection.unsaved_session_ids,
      csrfmiddlewaretoken: this.props.csrf_token,
    }

    ajaxRequest({
      url: '/coach/save-session-collection/',
      data: post_data,
      method: 'POST',
      success: this.sessionCollectionSavedCallback,
      send_as_form_values: false,
      error: handleLoggedOutError,
      expected_response_type: 'json',
    });

  }

  sessionCollectionSavedCallback = (data, status) => {
    if (status == 200) {
      this.props.handleSelectedSessionSaved(data, status)
      this.props.closeSessionCollectionForm();
    }
  }

  handleCancelClick = () => {
    this.props.closeSessionCollectionForm();
  }

  render() {

    var container_css = {
      position: 'fixed',
      top: 0,
      bottom: 0,
      right: 0,
      left: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      zIndex: 200
    }

    var popup_inner_css = {
      background: 'white',
      width: 500,
      margin: '60px auto 0',
      padding: '1px 15px 15px',
      top: 50,
    }

    if (this.state.currently_saving) {
      var save_button_text = "Saving...";
    }
    else {
      var save_button_text = "Save selection";
    }

    return (
      <div id="lap-collection-form-container" style={container_css}>
        <div className="lap-collection-form-inner" style={popup_inner_css}>
          <a className="close" href="#" title="Close" onClick={this.handleCancelClick}>Close</a>
          <h3>Save your collection of {this.props.selection.unsaved_session_ids.length} session{this.props.selection.unsaved_session_ids.length > 1 && 's'}</h3>
          <p>Once saved you can continue to add and remove more sessions.</p>
          <form>
            <div className="field-wrapper">
              <label htmlFor="id_collection_name">Selection name:</label> <input type="text" name="selection_name" id="id_selection_name" value={this.state.name} onChange={this.handleNameChange}/>
            </div>
            <div className="button-wrapper">
              <input type="submit" id="save-selection" name="save-selection" value={save_button_text} onClick={this.handleSelectionFormSubmit} className="standard-button" />
            </div>
          </form>
        </div>
      </div>
    );
  }
}

class CompareChartOption extends React.Component {

  handleClick = () => {
    if (this.props.current_view == 'charts') {
      toggleInArray(this.props.currently_selected, this.props.metric);
    }
    activities_section.refs['activity_section_' + this.props.activity_id].setState({compare_sessions_current_view: 'charts', compare_sessions_selected_charts: this.props.currently_selected});
  }

  render() {

    var button_style = {
      padding: '2px 6px',
      display: 'inline-block',
      cursor: 'pointer',
      fontSize: '12px',
      borderRadius: '5px',
      margin: '0px 2px',
    }

    if (this.props.currently_selected.indexOf(this.props.metric) > -1) {
      var button_class = "set-button active"
    }
    else {
      var button_class = "set-button inactive"
    }

    return (<div className={button_class} style={button_style} onClick={this.handleClick}>{this.props.name}</div>);
  }
}

class CompareViewButton extends React.Component {

  handleClick = () => {
    activities_section.refs['activity_section_' + this.props.activity_id].setState({compare_sessions_current_view: this.props.machine_name});
  }

  render() {

    var button_style = {
      border: '1px solid silver',
      padding: '4px',
      margin: '0 7px',
      display: 'inline-block',
      cursor: 'pointer'
    }

    if (this.props.current_view == this.props.machine_name) {
      button_style.background = 'silver';
      button_style.color = 'black';
    }

    return (<div className="set-button" style={button_style} onClick={this.handleClick}>{this.props.name}</div>);
  }
}

/**
 * A popup dialogue component
 *
 * This opens when pressing on the 'Add a new selection of laps' button.
 */
class NewLapSetForm extends React.Component {

  state = {
    name: '',
    existing_set_id: null,
    existing_sets: [],
    existing_set_order: [],
    currently_saving: false
  }

  componentDidMount = () => {
    this.getLapsets();
  }

  getLapsets = () => {
    ajaxRequest({
      url: '/training-logs/get-lap-sets/',
      method: 'GET',
      data: {discipline: this.props.discipline, athlete_id: this.props.athlete_id},
      success: this.getLapsetSuccessCallback,
      expected_response_type: 'json',
    });
  }

  getLapsetSuccessCallback = (response, status) => {

    let existing_sets = {};
    let existing_set_order = [];
    for (let i = 0; i < response.lapsets.length; i++) {
      existing_sets[response.lapsets[i].id] = response.lapsets[i];
      existing_set_order.push(response.lapsets[i].id);
    }

    this.setState({
      existing_sets: existing_sets,
      existing_set_order: existing_set_order,
    });

  }

  handleNameChange = (event) => {
    this.setState({name: event.target.value});
  }

  handleLapSetFormSubmit = (event) => {
    event.preventDefault();
    this.activateNewLapSet(new LapSet('new_lapset', this.state.name), 'new_lapset');
  }

  handleChooseSelection = (lapset_id) => {
    this.activateNewLapSet(this.state.existing_sets[lapset_id], lapset_id);
  }

  activateNewLapSet = (lapset, lapset_id) => {

    activities_section.refs['activity_section_' + this.props.base_activity_id].state.lapsets[lapset_id] = lapset;

    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({
      lapsets: activities_section.refs['activity_section_' + this.props.base_activity_id].state.lapsets,
      current_lapset_id: lapset_id,
    });

    this.props.closeLapSetForm();

  }

  handleCancelClick = () => {
    this.props.closeLapSetForm();
  }

  render() {

    var container_css = {
      position: 'fixed',
      top: 0,
      bottom: 0,
      right: 0,
      left: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      zIndex: 200
    }

    var popup_inner_css = {
      background: 'white',
      width: 500,
      margin: '60px auto 0',
      padding: '1px 15px 15px',
      top: 50,
    }

    var existing_sets_field;
    if (this.state.existing_set_order.length > 0) {
      var existing_lap_set_options = [];
      for (var i = 0; i < this.state.existing_set_order.length; i++) {
        existing_lap_set_options.push(
          <div className="lap-selection-option"
            key={this.state.existing_set_order[i]}
            onClick={this.handleChooseSelection.bind(null, this.state.existing_set_order[i])}
          >
            {this.state.existing_sets[this.state.existing_set_order[i]].name}
          </div>
        );
      }

      existing_sets_field = (
        <div>{existing_lap_set_options}</div>
      );

    }

    let save_button_text = (this.state.currently_saving) ? "Saving..." : "Save";

    return (
      <div id="lap-set-form-container" style={container_css}>
        <div className="lap-set-form-inner" style={popup_inner_css}>
          <a className="close" href="#" title="Close" onClick={this.handleCancelClick}>Close</a>
          <h2>Name your selection</h2>
          <form>
            {existing_sets_field}
            <div className="new-lap-selection" style={{padding: '5px'}}>
              <label htmlFor="id_set_name">New selection name</label> <input type="text" name="set_name" id="id_set_name" value={this.state.name} onChange={this.handleNameChange}/>
              <input type="submit" id="save-lap-set" name="save-set-name" value={save_button_text} onClick={this.handleLapSetFormSubmit} style={{margin: '0 0 0 5px'}} className="standard-button" />
            </div>
          </form>
        </div>
      </div>
    );
  }
}

class NewSetButton extends React.Component {

  handleClick = () => {
    this.openNewLapSetForm();
  }

  openNewLapSetForm = () => {
    this.popup_container = document.createElement("div");
    document.body.appendChild(this.popup_container);
    this.popup = ReactDOM.render(
      <NewLapSetForm
        base_activity_id={this.props.base_activity_id}
        discipline={this.props.discipline}
        athlete_id={this.props.athlete_id}
        closeLapSetForm={this.closeNewLapSetForm}
      />, this.popup_container
    );
  }

  closeNewLapSetForm = () => {
    ReactDOM.unmountComponentAtNode(this.popup_container);
    document.body.removeChild(this.popup_container);
  }

  render() {

    var button_style = {
      border: '1px solid silver',
      padding: '1px 10px',
      borderRadius: '8px',
      margin: '8px 4px',
      display: 'inline-block',
      cursor: 'pointer',
      fontSize: '12px',
    }

    if (this.props.current_lapset_id == this.props.lapset.id) {
      button_style.background = 'silver';
      button_style.color = 'black';
    }

    return (<div className="set-button" style={button_style} onClick={this.handleClick}>{this.props.lapset.name}</div>);
  }
}

class SetButton extends React.Component {

  handleClick = () => {
    this.props.setCurrentSet(this.props.lapset.id);
  }

  render() {

    var button_style = {
      border: '1px solid silver',
      padding: '1px 10px',
      borderRadius: '8px',
      margin: '8px 4px',
      display: 'inline-block',
      cursor: 'pointer',
      fontSize: '12px',
    }

    if (this.props.current_lapset_id == this.props.lapset.id) {
      button_style.background = 'silver';
      button_style.color = 'black';
    }

    return (<div className="set-button" style={button_style} onClick={this.handleClick}>{this.props.lapset.name}</div>);
  }
}

function createOverallActivityLap(activity) {

  var lap = new Lap({
    activity_id: activity.id,
    id: 'overall-' + activity.id,
    distance_meters: activity.total_distance_km * 1000,
    duration_seconds: activity.total_time_seconds,
    lapsets: ['overall_activity']
  });

  if (activity.trackpoints && activity.trackpoints.length > 1) {
    lap.first_trackpoint_id = activity.trackpoints[0].id;
    lap.last_trackpoint_id = activity.trackpoints[activity.trackpoints.length - 1].id;
  }

  if (activity.average_cadence_rpm) {
    lap.average_cadence_rpm = activity.average_cadence_rpm;
  }

  if (activity.average_heart_rate_bpm) {
    lap.average_heart_rate_bpm = activity.average_heart_rate_bpm;
  }

  if (activity.height_climbed_meters) {
    lap.height_climbed_meters = activity.height_climbed_meters;
  }

  if (activity.average_watts) {
    lap.average_power_watts = activity.average_watts;
  }

  return lap;
}


function storeActivityData(base_activity_id, activity_id, data, callback) {

  const MAX_TRACKPOINTS = 600;

  // Update main activity data.
  var data = process_activity_data_trackpoints(data);
  var existing_data = activities_section.state.activity_data[activity_id];
  var updated_activity_data = activities_section.state.activity_data[activity_id] = Object.assign({}, existing_data, data);
  if (typeof(data.trackpoints) != 'undefined') {
    // TODO I need a better way of storing this really. Updating this data is going to be a pain otherwise.
    // Can I store reduced trackpoints in a React component? Or better yet, in the Redux store.
    activities_section.state.reduced_trackpoints[activity_id] = reduceData(activity_id, updated_activity_data.trackpoints, MAX_TRACKPOINTS);
    activities_section.state.reduced_trackpoints[activity_id] = filterElevationOutlierData(activities_section.state.reduced_trackpoints[activity_id]);
  }

  // Update lap sets.
  if (data.lapsets) {
    Object.keys(data.lapsets).forEach((lapset_id) => {
      activities_section.refs['activity_section_' + base_activity_id].state.lapsets[lapset_id] = new LapSet(parseInt(lapset_id), data.lapsets[lapset_id].name);
    });
  }

  // Update laps.
  if (data.laps) {
    Object.keys(data.laps).forEach((lap_id) => {
      activities_section.state.laps[lap_id] = new Lap(data.laps[lap_id]);
    });
  }

  // Create an overall lap.
  var overall_lap = createOverallActivityLap(updated_activity_data);
  activities_section.state.laps[overall_lap.id] = overall_lap;

  // Add lap set activity summaries.
  if ('lapset_activity_summaries' in data) {
    Object.keys(data.lapset_activity_summaries).forEach((lapset_activity_summary_id) => {
      let lapset_activity_summary = data.lapset_activity_summaries[lapset_activity_summary_id];

      if (typeof(activities_section.refs['activity_section_' + base_activity_id].state.lap_set_activity_summaries[lapset_activity_summary.activity_id]) == 'undefined') {
        activities_section.refs['activity_section_' + base_activity_id].state.lap_set_activity_summaries[lapset_activity_summary.activity_id] = {};
      }

      activities_section.refs['activity_section_' + base_activity_id].state.lap_set_activity_summaries[lapset_activity_summary.activity_id][lapset_activity_summary.lapset_id] = lapset_activity_summary;
    });
  }

  // Determine if activity should display charts.
  updated_activity_data.supported_charts = determineSupportedChartsFromData(updated_activity_data);

  // Update the React component state
  activities_section.state.activity_data[activity_id] = updated_activity_data;
  activities_section.setState({
    activity_data: activities_section.state.activity_data,
    laps: activities_section.state.laps,
    lapsets: activities_section.state.lapsets,
    lap_set_activity_summaries: activities_section.state.lap_set_activity_summaries,
    reduced_trackpoints: activities_section.state.reduced_trackpoints,
  }, function () {
    if (callback) {
      callback();
    }
  });

}

function determineSupportedChartsFromData(activity_data) {

  let supported_charts = {
    power: false,
    heart_rate: false,
    elevation: false,
    cadence: false,
    speed: false,
    distance: false,
    any_to_display: false,
  }

  if (activity_data.total_distance_km > 0.5) {
    supported_charts.distance = true;
  }

  let elevation_scale_max = (activity_data.trackpoints) ? max(activity_data.trackpoints, function (d) { return d.altitude_meters }) : null;
  if (supported_charts.distance && activity_data.discipline != 'swimming' && !(typeof elevation_scale_max == "undefined" || elevation_scale_max == 0)) {
    supported_charts.elevation = true;
    supported_charts.any_to_display = true;
  }

  let speed_scale_max = (activity_data.trackpoints) ? max(activity_data.trackpoints, function (d) { return d.speed_meters_per_second }) : null;
  if (!(typeof speed_scale_max == "undefined" || speed_scale_max < 2) && activity_data.discipline != 'swimming') {
    supported_charts.speed = true;
    supported_charts.any_to_display = true;
  }

  let heart_rate_scale_max = (activity_data.trackpoints) ? max(activity_data.trackpoints, function (d) { return d.heart_rate_bpm }) : null;
  if (!(typeof heart_rate_scale_max == "undefined" || heart_rate_scale_max == 0)) {
    supported_charts.heart_rate = true;
    supported_charts.any_to_display = true;
  }

  let power_scale_max = (activity_data.trackpoints) ? max(activity_data.trackpoints, function (d) { return d.power_watts }) : null;
  if (!(typeof power_scale_max == "undefined" || power_scale_max == 0)) {
    supported_charts.power = true;
    supported_charts.any_to_display = true;
  }

  let cadence_scale_max = (activity_data.trackpoints) ? max(activity_data.trackpoints, function (d) { return d.cadence_rpm }) : null;
  if (!(typeof cadence_scale_max == "undefined" || cadence_scale_max == 0)) {
    supported_charts.cadence = true;
    supported_charts.any_to_display = true;
  }

  return supported_charts;

}

function activitiesLoaded(data, base_activity_id) {
  for (var i = 0; i < data.length; i++) {
    var activity = data[i];
    storeActivityData(base_activity_id, activity.id, activity);
  }
}

class CompareSessionsSection extends React.Component {

  state = { display_open_collection: false }

  activitiesFetchedCallback = (data) => {
    activitiesLoaded(data, this.props.base_activity_id);
  }

  collectionLoadingCallback = (collection) => {

    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({
      compare_sessions_current_selection: collection,
    });

    // Update the URL fragment.
    history.pushState(null, '', xhale.helper.add_or_alter_url_parameter(window.location.search, 'selection', collection.id));

    // Close the open sessions dialogue.
    this.handleCancelOpenCollection();

  }

  handleOpenCollection = () => {
    this.setState({display_open_collection: true});
  }

  handleCancelOpenCollection = () => {
    this.setState({display_open_collection: false});
  }

  componentDidMount = () => {
    if (!this.props.compare_sessions_current_selection) {
      this.handleOpenCollection();
    }
  }

  /**
   * Get the base activity object.
   */
  getBaseActivity = () => {
    for (var i = 0; i < this.props.activity_data.length; i++) {
      if (this.props.activity_data[i].id == this.props.base_activity_id) {
        return this.props.activity_data[i];
      }
    }
  }

  /**
   * Add an activity to the currently open collection or create a brand new
   * collection if one is not already open.
   */
  addActivityToCurrentCollection = (activity_to_add) => {

    if (!this.props.compare_sessions_current_selection) {

      // Create a new session collection that includes the base session automatically.
      var base_activity = this.props.activity_data[this.props.base_activity_id];

      var session_collection = new SessionCollection({
        athlete_id: this.props.athlete_id,
        session_ids: [],
        race_ids: [],
      });

      if (base_activity.training_session_id) {
          session_collection.unsaved_session_ids = addToArrayIfNotExists(session_collection.unsaved_session_ids, base_activity.training_session_id);
      }
      else if (base_activity.race_id) {
          session_collection.unsaved_race_ids = addToArrayIfNotExists(session_collection.unsaved_race_ids, base_activity.race_id);
      }
    }
    else {
      var session_collection = this.props.compare_sessions_current_selection;
    }

    // Add the chosen session.
    if (activity_to_add.training_session) {
      session_collection.unsaved_session_ids = addToArrayIfNotExists(session_collection.unsaved_session_ids, activity_to_add.training_session.id);
    }
    else if (activity_to_add.race) {
      session_collection.unsaved_race_ids = addToArrayIfNotExists(session_collection.unsaved_race_ids, activity_to_add.race.id);
    }

    // Update the selection in the main React component.
    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({
      compare_sessions_current_selection: session_collection,
    });

    // Fetch the lapset data.
    fetchLapSetActivitySummariesBySessionIDs(session_collection.unsaved_session_ids, session_collection.unsaved_race_ids, this.activitiesFetchedCallback);

    // Close the open sessions dialogue.
    this.handleCancelOpenCollection();
  }

  getRelevantActivities = () => {

    var activities = [];

    if (!this.props.compare_sessions_current_selection || typeof(this.props.activity_data[this.props.base_activity_id]) == 'undefined') {
      return activities;
    }

    var base_activity_discipline = this.props.activity_data[this.props.base_activity_id].discipline;

    // Cycle through the available activities and if the sessions match those
    // in the active session collection, add them to the list to display.

    Object.keys(this.props.activity_data).forEach((activity_id) => {

      let activity = this.props.activity_data[activity_id];

      if (activity.discipline != base_activity_discipline) {
        return;
      }

      if (activity.training_session_id) {
        var in_unsaved_selection = this.props.compare_sessions_current_selection.unsaved_session_ids.indexOf(activity.training_session_id) > -1;
        var in_saved_selection = this.props.compare_sessions_current_selection.saved_session_ids.indexOf(activity.training_session_id) > -1;
      }
      else if (activity.race_id) {
        var in_unsaved_selection = this.props.compare_sessions_current_selection.unsaved_race_ids.indexOf(activity.race_id) > -1;
        var in_saved_selection = this.props.compare_sessions_current_selection.saved_race_ids.indexOf(activity.race_id) > -1;
      }

      if (in_unsaved_selection || in_saved_selection) {
        activities.push(activity);
      }

    });

    return activities;

  }

  render() {

    var activities = this.getRelevantActivities();

    if (this.state.display_open_collection || activities.length == 0) {

      var base_training_session_id;
      var base_race_id;
      if (typeof(this.props.activity_data[this.props.base_activity_id]) != 'undefined') {
        base_training_session_id = this.props.activity_data[this.props.base_activity_id].training_session_id;
        base_race_id = this.props.activity_data[this.props.base_activity_id].race_id;
      }

      var contents = (
        <OpenCollection
          athlete_id={(typeof(athlete_data) != 'undefined') ? athlete_data.id : null}
          base_activity_id={this.props.base_activity_id}
          base_training_session_id={base_training_session_id}
          base_race_id={base_race_id}
          individual_related_sessions={this.props.individual_related_sessions}
          activitiesFetchedCallback={this.activitiesFetchedCallback}
          closeOpenCollectionPopup={this.handleCancelOpenCollection}
          collectionLoadingCallback={this.collectionLoadingCallback}
          addActivityToCurrentCollection={this.addActivityToCurrentCollection}
          existing_collections={this.props.existing_collections}
        />
      )
    }
    else {
      var activity_summaries = getActivitySummaries(activities);

      var contents = (
        <CompareSessionsDataView
          activity_section={this.props.activity_section}
          base_activity_id={this.props.base_activity_id}
          athlete_id={this.props.athlete_id}
          laps={this.props.laps}
          lapsets={this.props.lapsets}
          current_lapset_id={this.props.current_lapset_id}
          activity_data={this.props.activity_data}
          activity_summaries={activity_summaries}
          distance_unit={this.props.distance_unit}
          visible_lapset_activities={this.props.visible_lapset_activities}
          visible_lapset_activity_colours={this.props.visible_lapset_activity_colours}
          csrf_token={this.props.csrf_token}
          compare_sessions_current_view={this.props.compare_sessions_current_view}
          compare_sessions_selected_charts={this.props.compare_sessions_selected_charts}
          compare_sessions_current_selection={this.props.compare_sessions_current_selection}
          lap_set_activity_summaries={this.props.lap_set_activity_summaries}
          display_compared_laps_only={this.props.display_compared_laps_only}
          handleOpenCollection={this.handleOpenCollection}
        />
      );
    }

    return (
      <div className="compare-sessions-section-container" style={{height: '100%', overflow: 'auto'}}>
        {contents}
      </div>
    );

  }
}

class OpenCollection extends React.Component {

  componentDidMount = () => {
    this.loadExistingCollections();
    this.loadRelatedActivities();
  }

  loadExistingCollections = () => {
    ajaxRequest({
      url: '/coach/get-session-collections/',
      method: 'GET',
      data: {athlete_id: this.props.athlete_id},
      success: this.getSessionCollectionsSuccessCallback,
      expected_response_type: 'json',
    });
  }

  loadRelatedActivities = () => {
    if (!this.props.individual_related_sessions) {
      ajaxRequest({
        url: '/coach/load-related-activities/' + this.props.base_activity_id + '/',
        method: 'GET',
        data: {athlete_id: this.props.athlete_id},
        success: this.loadRelatedActivitiesSuccessCallback,
        expected_response_type: 'json',
      });
    }
  }

  loadRelatedActivitiesSuccessCallback = (response, status) => {
    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({individual_related_sessions: response});
  }

  getSessionCollectionsSuccessCallback = (response, status) => {
    let collections = [];
    for (let i = 0; i < response.session_collections.length; i++) {
      collections.push(new SessionCollection(response.session_collections[i]));
    }
    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({existing_collections: collections});
  }

  render() {

    var already_attached_session_selections = [];
    var other_session_selections = [];
    for (var i = 0; i < this.props.existing_collections.length; i++) {

      var collection_row = <CollectionRow
        key={this.props.existing_collections[i].id}
        collection={this.props.existing_collections[i]}
        base_training_session_id={this.props.base_training_session_id}
        base_race_id={this.props.base_race_id}
        activitiesFetchedCallback={this.props.activitiesFetchedCallback}
        collectionLoadingCallback={this.props.collectionLoadingCallback}
      />

      if (this.props.existing_collections[i].unsaved_session_ids.indexOf(this.props.base_training_session_id) > -1) {
        already_attached_session_selections.push(collection_row);
      }
      else {
        other_session_selections.push(collection_row);
      }

    }

    if (already_attached_session_selections.length == 0) {
      var already_attached_session_selections = <p style={{marginLeft: '7px'}}>You have not yet stored any collections of training sessions.</p>
    }

    if (other_session_selections.length == 0) {
      var other_session_selections = <p style={{marginLeft: '7px'}}>You have not yet stored any collections of training sessions.</p>
    }

    var individual_sessions = [];
    var session_ids = [];
    var race_ids = [];
    if (!this.props.individual_related_sessions) {
      individual_sessions = <p style={{marginLeft: '7px'}}>Loading...</p>
    }
    else if (this.props.individual_related_sessions.length > 0) {
      for (var i = 0; i < this.props.individual_related_sessions.length; i++) {

        if (this.props.individual_related_sessions[i].training_session) {

          var session_id = this.props.individual_related_sessions[i].training_session.id;
          if (session_ids.indexOf(session_id) == -1) {
            session_ids.push(session_id);
            individual_sessions.push(
              <IndividualSessionRow
                key={this.props.individual_related_sessions[i].training_session.id}
                activity_data={this.props.individual_related_sessions[i]}
                addActivityToCurrentCollection={this.props.addActivityToCurrentCollection}
              />
            );
          }

        }
        else if (this.props.individual_related_sessions[i].race) {

          var race_id = this.props.individual_related_sessions[i].race.id;
          if (race_ids.indexOf(race_id) == -1) {
            race_ids.push(session_id);
            individual_sessions.push(
              <IndividualRaceRow
                key={this.props.individual_related_sessions[i].race.id}
                activity_data={this.props.individual_related_sessions[i]}
                addActivityToCurrentCollection={this.props.addActivityToCurrentCollection}
              />
            );
          }

        }

      }
    }
    else {
      individual_sessions = <p style={{marginLeft: '7px'}}>You have not yet saved any related sessions.</p>
    }

    return (
      <div>
        <a className="close" href="#" title="Close" onClick={this.props.closeOpenCollectionPopup}>Close</a>
        <h2>Compare to other training sessions</h2>
        <h3>Selections containing this training session</h3>
        {already_attached_session_selections}
        <h3>Other selections</h3>
        {other_session_selections}
        <h3>Open an individual session</h3>
        {individual_sessions}
      </div>
    );
  }
}

class IndividualSessionRow extends React.Component {

  handleClick = () => {
    this.props.addActivityToCurrentCollection(this.props.activity_data)
  }

  render = () => {
    return (
      <div onClick={this.handleClick} style={{cursor: 'pointer', textDecoration: 'underline'}}>
        {this.props.activity_data.training_session.discipline_display} on {this.props.activity_data.training_session.date}
      </div>
    );
  }

}

class IndividualRaceRow extends React.Component {

  handleClick = () => {
    this.props.addActivityToCurrentCollection(this.props.activity_data)
  }

  render = () => {
    return (
      <div onClick={this.handleClick} style={{cursor: 'pointer', textDecoration: 'underline'}}>
        {this.props.activity_data.race.race_name} on {this.props.activity_data.race.date}
      </div>
    );
  }

}

class LapTables extends React.Component {

  setCurrentSet = (lapset_id) => {
    var chart_display_mode = ((lapset_id == 'overall_activity' && this.props.visible_lapset_activities.length == 0) || this.props.lapset_edit_mode.active) ? 'normal' : 'compare_activities';
    activities_section.refs['activity_section_' + this.props.activity_id].setState({
      chart_display_mode: chart_display_mode,
      current_lapset_id: lapset_id,
    });
  }

  render() {

    var lapsets;
    if (xhale.helper.has_beta_access('compare_sessions')) {

      // Create options to select between lap sets.
      var lap_set_list = [];
      Object.keys(this.props.lapsets).forEach((set_id) => {
        if (set_id != 'overall_activity') {
          lap_set_list.push(
            <SetButton
              key={set_id}
              lapset={this.props.lapsets[set_id]}
              current_lapset_id={this.props.current_lapset_id}
              setCurrentSet={this.setCurrentSet}
            />
          );
        }
      });

      lap_set_list.push(<SetButton key='all' lapset={{name: 'Show all laps', id: 'overall_activity'}} current_lapset_id={this.props.current_lapset_id} setCurrentSet={this.setCurrentSet} />);

      lap_set_list.push(
        <NewSetButton
          key='new'
          lapset={{name: 'Add a new selection of laps', id: 'create_new_lapset'}}
          current_lapset_id={this.props.current_lapset_id}
          base_activity_id={this.props.activity_id}
          discipline={this.props.discipline}
          athlete_id={this.props.athlete_id}
          setCurrentSet={this.setCurrentSet}
        />
      );

      var lapsets = (
        <div>
          <div style={{float: 'left', margin: '8px'}}>Filter laps by selection:</div>
          <div style={{}} className="tags">{lap_set_list}</div>
        </div>
      );

    }

    // If selected, filter the laps by the currently selected set.
    if (this.props.current_lapset_id && this.props.current_lapset_id != 'overall_activity') {

      var filtered_laps = [];

      Object.keys(this.props.laps).forEach((lap_id) => {
        let lap = this.props.laps[lap_id];
        var lap_is_in_current_lapset = lap.lapsets.indexOf(this.props.current_lapset_id) > -1;
        var lap_needed_for_editing_lapset = this.props.lapset_edit_mode.active && this.props.current_lapset_id == this.props.lapset_edit_mode.editing_lapset && lap.activity_id == this.props.lapset_edit_mode.editing_activity;
        if (lap_is_in_current_lapset || lap_needed_for_editing_lapset) {
          filtered_laps.push(lap)
        }
      })

    }
    else {
      var filtered_laps = this.props.laps;
    }

    // Sort the laps by whether they were record by a device, by the athlete
    // or by a coach. 
    var lap_tables_data = {'device': [], 'athlete': [], 'coach': [], 'any': []};
    Object.keys(filtered_laps).forEach((lap_id) => {
      let lap = filtered_laps[lap_id];
      if (lap.creator) {
        if (this.props.current_lapset_id == 'overall_activity') {
          lap_tables_data[lap.creator].push(lap);
        }
        else {
          lap_tables_data.any.push(lap);
        }
      }
    });

    // Create the lap tables (device, athlete and coach), depending on which
    // laps are present.
    var lap_tables = [];
    Object.keys(lap_tables_data).forEach((creator) => {
      let laps = lap_tables_data[creator];
      if (laps.length > 0 || (this.props.current_lapset_id != 'overall_activity' && creator == 'any')) {
        lap_tables.push(
          <LapTable
            key={creator}
            athlete_id={this.props.athlete_id}
            creator={creator}
            laps={laps}
            selected_lap_ids={this.props.selected_lap_ids}
            current_lapset={this.props.lapsets[this.props.current_lapset_id]}
            base_activity_id={this.props.activity_id}
            activity_data={this.props.activity_data}
            discipline={this.props.discipline}
            highlighted_lap={this.props.highlighted_lap}
            highlightable={this.props.highlightable}
            distance_unit={this.props.distance_unit}
            csrf_token={this.props.csrf_token}
            visible_lapset_activities={this.props.visible_lapset_activities}
            visible_lapset_activity_colours={this.props.visible_lapset_activity_colours}
            current_lapset_id={this.props.current_lapset_id}
            lapset_edit_mode={this.props.lapset_edit_mode}
            x_scale_mode={this.props.x_scale_mode}
            setSwimLapMouseHover={this.props.setSwimLapMouseHover}
          />
        );
      }
    });

    // Provide an empty alternative.
    if (lap_tables.length == 0) {
      lap_tables = 'No laps have been stored.';
    }

    return (
      <div className="lap-tables">
        {lapsets}
        {lap_tables}
      </div>
    );

  }
}

class TooltipRow extends React.Component {

  state = {
    elevation_visible: true,
    speed_visible: true,
    power_visible: true,
    cadence_visible: true,
    heart_rate_visible: true
  }

  durationClicked = () => {
    setChartsToDuration();
  }

  distanceClicked = () => {
    setChartsToDistance();
  }

  elevationClicked = () => {
    if (this.state.elevation_visible) {
      this.setState({elevation_visible: false});
      $('#activity-' + this.props.activity_id + ' .charts-container .elevation-chart-container').slideUp();
    }
    else {
      this.setState({elevation_visible: true});
      $('#activity-' + this.props.activity_id + ' .charts-container .elevation-chart-container').slideDown();
    }
  }

  speedClicked = () => {
    if (this.state.speed_visible) {
      this.setState({speed_visible: false});
      $('#activity-' + this.props.activity_id + ' .charts-container .speed-chart-container').slideUp();
    }
    else {
      this.setState({speed_visible: true});
      $('#activity-' + this.props.activity_id + ' .charts-container .speed-chart-container').slideDown();
    }
  }

  heartRateClicked = () => {
    if (this.state.heart_rate_visible) {
      this.setState({heart_rate_visible: false});
      $('#activity-' + this.props.activity_id + ' .charts-container .heart-rate-chart-container').slideUp();
    }
    else {
      this.setState({heart_rate_visible: true});
      $('#activity-' + this.props.activity_id + ' .charts-container .heart-rate-chart-container').slideDown();
    }
  }

  cadenceClicked = () => {
    if (this.state.cadence_visible) {
      this.setState({cadence_visible: false});
      $('#activity-' + this.props.activity_id + ' .charts-container .cadence-chart-container').slideUp();
    }
    else {
      this.setState({cadence_visible: true});
      $('#activity-' + this.props.activity_id + ' .charts-container .cadence-chart-container').slideDown();
    }
  }

  powerClicked = () => {
    if (this.state.power_visible) {
      this.setState({power_visible: false});
      $('#activity-' + this.props.activity_id + ' .charts-container .power-chart-container').slideUp();
    }
    else {
      this.setState({power_visible: true});
      $('#activity-' + this.props.activity_id + ' .charts-container .power-chart-container').slideDown();
    }
  }

  render = () => {

    if (this.props.trackpoint) {
      var duration = xhale.helper.format_time_seconds(this.props.trackpoint.cumulative_duration_seconds);
      var elevation_meters = xhale.helper.decimal_places(this.props.trackpoint.altitude_meters);
      var elevation_feet = xhale.helper.decimal_places(xhale.helper.meters_to_feet(this.props.trackpoint.altitude_meters));
      var distance_km = xhale.helper.format_km_from_meters(this.props.trackpoint.distance_meters, 2);
      var distance_miles = xhale.helper.format_miles_from_meters(this.props.trackpoint.distance_meters, 2);
      var heart_rate_bpm = xhale.helper.decimal_places(this.props.trackpoint.heart_rate_bpm, 0, 'zero');
      var power_watts = xhale.helper.zero_pad(xhale.helper.decimal_places(this.props.trackpoint.power_watts, 0, 'zero'), 3);
      var watts_per_kg = (athlete_weight) ? xhale.helper.decimal_places((this.props.trackpoint.power_watts / athlete_weight), 2, 'zero', true) : null;
      if (this.props.discipline == 'running') {
        var pace_minutes_per_km = formatSecondsAsPace(xhale.helper.meters_per_second_as_seconds_per_km(this.props.trackpoint.speed_meters_per_second));
        var pace_minutes_per_mile = formatSecondsAsPace(xhale.helper.meters_per_second_as_seconds_per_mile(this.props.trackpoint.speed_meters_per_second));
        var cadence_rpm = xhale.helper.decimal_places(this.props.trackpoint.cadence_rpm * 2, 0, 'zero');
      }
      else {
        var speed_kph = xhale.helper.decimal_places(xhale.helper.meters_per_second_to_kph(this.props.trackpoint.speed_meters_per_second), 2, 'zero', true);
        var speed_mph = xhale.helper.decimal_places(xhale.helper.meters_per_second_to_mph(this.props.trackpoint.speed_meters_per_second), 2, 'zero', true);
        var cadence_rpm = xhale.helper.decimal_places(this.props.trackpoint.cadence_rpm, 0, 'zero');
      }
    }
    else {
      var power_watts = '---';
      var duration = '--:--:--';
      var distance_km = '--.--';
      var distance_miles = '--.--';
      var elevation_meters = '--';
      var elevation_feet = '--';
      if (this.props.discipline == 'running') {
        var pace_minutes_per_km = '--:--';
        var pace_minutes_per_mile = '--:--';
      }
      else {
        var speed_kph = '--.--';
        var speed_mph = '--.--';
      }
      var heart_rate_bpm = '---';
      var cadence_rpm = '--';
    }

    var elevation_visibility = this.state.elevation_visible ? 'visible' : 'invisible';
    var speed_visibility = this.state.speed_visible ? 'visible' : 'invisible';
    var heart_rate_visibility = this.state.heart_rate_visible ? 'visible' : 'invisible';
    var cadence_visibility = this.state.cadence_visible ? 'visible' : 'invisible';
    var power_visibility = this.state.power_visible ? 'visible' : 'invisible';

    if (this.props.speed_supported) {
      if (this.props.discipline == 'running') {
        var speed = (
         <div className={'data pace ' + speed_visibility} style={{WebkitBoxFlex: 1.4, MozBoxFlex: 1.4, WebkitFlex: 1.4, msFlex: 1.4, flex: 1.4}} onClick={this.speedClicked}>
            <h4>Pace</h4> <span className="min-km">{pace_minutes_per_km}</span><span className="unit">min/km</span> / <span className="min-mile">{pace_minutes_per_mile}</span><span className="unit">min/mile</span>
          </div>
        );
      }
      else {
        var speed = (
          <div className={'data speed ' + speed_visibility} style={{WebkitBoxFlex: 1.2, MozBoxFlex: 1.2, WebkitFlex: 1.2, msFlex: 1.2, flex: 1.2}} onClick={this.speedClicked}>
            <h4>Speed</h4> <span className="kph">{speed_kph}</span><span className="unit">kph</span> <span className="mph">{speed_mph}</span><span className="unit">mph</span>
          </div>
        );
      }
    }

    var power;
    if (this.props.power_supported) {
      var watts_per_kg_jsx;
      if (watts_per_kg) {
        watts_per_kg_jsx = (<span className="watts-weight-ratio"><span className="value">{watts_per_kg}</span><span className="unit">w/kg</span></span>);
      }
      power = (
        <div className={'data power ' + power_visibility} style={{WebkitBoxFlex: 1.1, MozBoxFlex: 1.1, WebkitFlex: 1.1, msFlex: 1.1, flex: 1.1}} onClick={this.powerClicked}>
          <h4>Power</h4> <span className="watts">{power_watts}</span><span className="unit">watts</span> {watts_per_kg_jsx}
        </div>
      );
    }

    var heart_rate;
    if (this.props.heart_rate_supported) {
      heart_rate = (
        <div className={'data heart-rate ' + heart_rate_visibility} style={{WebkitBoxFlex: 0.85, MozBoxFlex: 0.85, WebkitFlex: 0.85, msFlex: 0.85, flex: 0.85}} onClick={this.heartRateClicked}>
          <h4>Heart rate</h4> <span className="bpm">{heart_rate_bpm}</span><span className="unit">bpm</span>
        </div>
      );
    }

    var cadence;
    if (this.props.cadence_supported) {
      var unit = (this.props.discipline == 'running') ? 'spm' : 'rpm';
      cadence = (
        <div className={'data cadence ' + cadence_visibility} style={{WebkitBoxFlex: 0.7, MozBoxFlex: 0.7, WebkitFlex: 0.7, msFlex: 0.7, flex: 0.7}} onClick={this.cadenceClicked}>
          <h4>Cadence</h4> <span className={unit}>{cadence_rpm}</span><span className="unit">{unit}</span>
        </div>
      );
    }

    var elevation;
    if (this.props.elevation_supported) {
      elevation = (
        <div className={'data elevation ' + elevation_visibility} style={{WebkitBoxFlex: 1.1, MozBoxFlex: 1.1, WebkitFlex: 1.1, msFlex: 1.1, flex: 1.1}} onClick={this.elevationClicked}>
          <h4>Elevation</h4> <span className="meters">{elevation_meters}</span><span className="unit">m</span> <span className="feet">{elevation_feet}</span><span className="unit">feet</span>
        </div>
      );
    }

    var distance;
    if (this.props.distance_supported) {
      var distance_selected = this.props.x_scale_mode == 'distance' ? 'selected' : 'not-selected';
      distance = (
        <div className={'data distance ' + distance_selected} style={{WebkitBoxFlex: 1.2, MozBoxFlex: 1.2, WebkitFlex: 1.2, msFlex: 1.2, flex: 1.2}} onClick={this.distanceClicked}>
          <h4>Distance</h4> <span className="km">{distance_km}</span><span className="unit">km</span> <span className="miles">{distance_miles}</span><span className="unit">miles</span>
        </div>
      );
    }

    var duration_selected = this.props.x_scale_mode == 'duration' ? 'selected' : 'not-selected';
    var duration = (
      <div className={'data time ' + duration_selected} style={{WebkitBoxFlex: 0.8, MozBoxFlex: 0.8, WebkitFlex: 0.8, msFlex: 0.8, flex: 0.8}} onClick={this.durationClicked}>
        <h4>Time</h4> <span className="time hms">{duration}</span>
      </div>
    );

    return (
      <div className="compare-tooltip-row" style={{color: this.props.colour}}>
        {duration}
        {distance}
        {elevation}
        {speed}
        {heart_rate}
        {power}
        {cadence}
      </div>
    );

  }
}

class LapSetForm extends React.Component {

  state = {
    name: '',
    existing_set_id: null,
    existing_sets: [],
    currently_saving: false
  }

  componentDidMount = () => {
    this.getLapsets();
  }

  getLapsets = () => {
    ajaxRequest({
      url: '/training-logs/get-lap-sets/',
      method: 'GET',
      data: {discipline: this.props.discipline, athlete_id: this.props.athlete_id},
      success: this.getLapsetSuccessCallback,
      expected_response_type: 'json',
    });
  }

  getLapsetSuccessCallback = (response, status) => {
    this.setState({existing_sets: response.lapsets});
  }

  handleNameChange = (event) => {
    this.setState({name: event.target.value});
  }

  handleLapSetFormSubmit = (event) => {

    this.setState({currently_saving: true});

    event.preventDefault();

    var post_data = {
      id: null,
      existing_set_id: this.state.existing_set_id,
      name: this.state.name,
      discipline: this.props.discipline,
      athlete_id: this.props.athlete_id,
      lap_ids_to_add: this.props.lap_ids,
    }

    ajaxRequest({
      url: '/training-logs/save-lap-set/',
      method: 'POST',
      data: post_data,
      success: this.lapSetSavedCallback,
      expected_response_type: 'json',
    });

  }

  lapSetSavedCallback = (response, status) => {
    activities_section.state.activity_data[this.props.base_activity_id].lapsets[response.lap_set.id] = response.lap_set;
    activities_section.forceUpdate();
    this.props.closeLapSetForm();
    window.location.reload(false);
  }

  handleExistingLapSetChange = (event) => {
    this.setState({existing_set_id: event.target.value});
  }

  handleCancelClick = () => {
    this.props.closeLapSetForm();
  }

  render() {

    var container_css = {
      position: 'fixed',
      top: 0,
      bottom: 0,
      right: 0,
      left: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      zIndex: 200
    }

    var popup_inner_css = {
      background: 'white',
      width: 500,
      margin: '60px auto 0',
      padding: '1px 15px 15px',
      top: 50,
    }

    var existing_sets_field;
    if (this.state.existing_sets.length > 0) {
      var existing_lap_set_options = [];
      for (var i = 0; i < this.state.existing_sets.length; i++) {
        existing_lap_set_options.push(
          <label key={this.state.existing_sets[i].id} style={{display: 'block'}}>
            <input type="radio" value={this.state.existing_sets[i].id} name="existing_set" defaultChecked={this.state.existing_set_id == this.state.existing_sets[i].id} />
            {this.state.existing_sets[i].name}
          </label>
        );
      }

      existing_sets_field = (
        <div onChange={this.handleExistingLapSetChange}>
          {existing_lap_set_options}
        </div>
      );
    }

    if (this.state.currently_saving) {
      var save_button_text = "Saving...";
    }
    else {
      var save_button_text = "Save lap set";
    }

    return (
      <div id="lap-set-form-container" style={container_css}>
        <div className="lap-set-form-inner" style={popup_inner_css}>
          <a className="close" href="#" title="Close" onClick={this.handleCancelClick}>Close</a>
          <h2>Save {this.props.lap_ids.length} lap{this.props.lap_ids.length > 1 && 's'} to a set</h2>
          <form>
            {existing_sets_field}
            <div className="field-wrapper">
              <label htmlFor="id_set_name">Set name:</label> <input type="text" name="set_name" id="id_set_name" value={this.state.name} onChange={this.handleNameChange}/>
            </div>
            <div className="button-wrapper">
              <input type="submit" id="save-lap-set" name="save-set-name" value={save_button_text} onClick={this.handleLapSetFormSubmit} className="standard-button" />
            </div>
          </form>
        </div>
      </div>
    );
  }
}

/*
 * A React component used to display a summary of laps that are being compared.
 */
class CompareSummary extends React.Component {

  /*
   * Toggle between overlaying the selected laps and separating them again.
   */
  toggleOverlayLaps = () => {
    activities_section.refs['activity_section_' + this.props.activity_id].toggleCompareLapOverlays();
  }

  /*
   * Toggle between showing an average line for these laps and showing the
   * normal highlighted line of trackpoints.
   */
  toggleAverage = () => {
    activities_section.refs['activity_section_' + this.props.activity_id].toggleLapAverage();
  }

  /*
   * Bring up a dialogue box for saving the set of laps.
   */
  saveLapSetButtonPressed = () => {
    this.openLapSetForm();
  }

  openLapSetForm = () => {
    this.popup_container = document.createElement("div");
    document.body.appendChild(this.popup_container);
    this.popup = ReactDOM.render(
      <LapSetForm
        base_activity_id={this.props.activity_id}
        lap_ids={this.props.selected_lap_ids}
        discipline={this.props.discipline}
        athlete_id={this.props.athlete_id}
        closeLapSetForm={this.closeLapSetForm}
      />, this.popup_container
    );
  }

  closeLapSetForm = () => {
    ReactDOM.unmountComponentAtNode(this.popup_container);
    document.body.removeChild(this.popup_container);
  }

  /*
   * Implement React render.
   */
  render() {

    var laps = [];
    for (var i = 0; i < this.props.selected_lap_ids.length; i++) {
      laps.push(this.props.laps[this.props.selected_lap_ids[i]])
    }

    var average_power_watts = getAveragePower(laps);
    var average_power_section;
    if (average_power_watts) {

      if (athlete_weight) {
        var average_watts_per_kg = xhale.helper.decimal_places(average_power_watts / athlete_weight, 2, 'zero', true);
        var average_power_to_weight_jsx = (
          <span className="power-to-weight">/ <span className="value">{average_watts_per_kg}</span><span className="unit">w/kg</span></span>
        );
      }

      var average_power_section = (
        <div className="power section">
          <h4>Power</h4>
          <div id="average-power-watts">
            <span className="value">{xhale.helper.decimal_places(average_power_watts, 0, 'zero', true)}</span><span className="unit">watts average</span> {average_power_to_weight_jsx}
          </div>
        </div>
      );
    }

    var average_heart_rate = getAverageHeartRate(laps);
    var average_heart_rate_section;
    if (average_heart_rate) {
      var average_heart_rate_section = (
        <div className="heart-rate section">
          <h4>Heart rate</h4>
          <div id="average-heart-rate-bpm">
            <span className="value">{xhale.helper.decimal_places(average_heart_rate, 0, 'zero', true)}</span><span className="unit">bpm</span>
          </div>
        </div>
      );
    }

    var average_cadence = (this.props.discipline == 'running') ? getAverageCadence(laps) * 2 : getAverageCadence(laps);
    var cadence_unit = (this.props.discipline == 'running') ? 'spm' : 'rpm';
    var average_cadence_section;
    if (average_cadence) {
      var average_cadence_section = (
        <div className="cadence section">
          <h4>Cadence</h4>
          <div id="average-cadence-rpm">
            <span className="value">{xhale.helper.decimal_places(average_cadence, 0, 'zero', true)}</span><span className="unit">{cadence_unit}</span>
          </div>
        </div>
      );
    }
    
    var average_speed_meters_per_second = getAverageSpeedMetersPerSecond(laps);
    var average_speed_section;
    if (average_speed_meters_per_second) {
      if (this.props.discipline == 'running') {
        average_speed_section = (
          <div className="average-pace section">
            <h4>Pace</h4>
            <div id="pace-min-km">
              <span className="value">{formatSecondsAsPace(xhale.helper.meters_per_second_as_seconds_per_km(average_speed_meters_per_second))}</span><span className="unit">min/km</span>
            </div>
            <div id="pace-min-mile">
              <span className="value">{formatSecondsAsPace(xhale.helper.meters_per_second_as_seconds_per_mile(average_speed_meters_per_second))}</span><span className="unit">min/mile</span>
            </div>
          </div>
        );
      }
      else {
        average_speed_section = (
          <div className="average-speed section">
            <h4>Speed</h4>
            <div id="speed-kph">
              <span className="value">{xhale.helper.decimal_places(xhale.helper.meters_per_second_to_kph(average_speed_meters_per_second), 2, 'zero', true)}</span><span className="unit">kph</span>
            </div>
            <div id="speed-mph">
              <span className="value">{xhale.helper.decimal_places(xhale.helper.meters_per_second_to_mph(average_speed_meters_per_second), 2, 'zero', true)}</span><span className="unit">mph</span>
            </div>
          </div>
        );
      }
    }

    var overlay_button_text = (activities_section.refs['activity_section_' + this.props.activity_id].overlaid) ? 'Separate laps' : 'Overlay laps';
    var average_button_text = (activities_section.refs['activity_section_' + this.props.activity_id].showing_average) ? 'Hide average' : 'Show average';

    var compare_buttons;
    if (this.props.selected_lap_ids.length > 0) {

      var save_set_button;
      if (xhale.helper.has_beta_access('compare_sessions')) {
        save_set_button = (
          <div className="activity-summary__button activity-summary__save-set-button" onClick={this.saveLapSetButtonPressed}>Add to set of laps</div>
        );
      }

      compare_buttons = (
        <div className="activity-summary__buttons" style={{ textAlign: 'right', margin: '0 7px 12px 0', fontSize: '12px' }}>
          <div className="activity-summary__button activity-summary__compare-toggle-button" onClick={this.toggleOverlayLaps}>{overlay_button_text}</div>
          <div className="activity-summary__button activity-summary__show-average-button" onClick={this.toggleAverage}>{average_button_text}</div>
          {save_set_button}
        </div>
      );
    }

    if (this.props.selected_lap_ids.length == 0) {
      var content = (
        <div className="activity-summary__help-text">
          <p>No laps have yet been selected to compare.</p>
          <p>You can overlay laps on top of one another in the chart and on the map by pressing the 'select' button in the laps table.</p>
        </div>
      );
    }
    else {
      var content = (
        <div>
          {compare_buttons}
          <div className="lap-count section"><h4>Laps selected</h4><span className="value">{this.props.selected_lap_ids.length}</span></div>
          {average_speed_section}
          {average_cadence_section}
          {average_heart_rate_section}
          {average_power_section}
        </div>
      );
    }

    return (
      <div className="activity-summary__section activity-summary__compare-section" style={{clear: 'both'}}>
        {content}
      </div>
    );
  }
}

class OverallSummary extends React.Component {
  render() {

    var distance_section;
    if (this.props.activity_data.total_distance_km) {

      if (this.props.activity_data.discipline == 'swimming') {
        var distance_metric = <span className="km"><span className="value">{xhale.helper.format_km(this.props.activity_data.total_distance_km * 1000, 0)}</span><span className="unit">m</span></span>
      }
      else {
        var distance_metric = <span className="km"><span className="value">{xhale.helper.format_km(this.props.activity_data.total_distance_km, 2)}</span><span className="unit">km</span></span>
      }

      distance_section = (
        <div className="distance section">
          <h4>Distance</h4>
          <div className="details">
            {distance_metric}
            <span className="separator"> / </span>
            <span className="miles"><span className="value">{xhale.helper.format_miles(this.props.activity_data.total_distance_miles, 2)}</span><span className="unit">miles</span></span>
          </div>
        </div>
      );
    }

    var elapsed_time;
    if (this.props.activity_data.elapsed_seconds) {
      elapsed_time = (
        <div className="elapsed-time section">
          <h4>Elapsed time</h4>
          <span className="value">{xhale.helper.format_time_seconds(this.props.activity_data.elapsed_seconds)}</span>
        </div>
      );
    }

    var speed;
    if (this.props.activity_data.discipline == 'running' && this.props.activity_data.km_pace) {
      speed = (
        <div className="average-pace section">
          <h4>Pace</h4>
          <div id="pace-min-km"><span className="value">{this.props.activity_data.km_pace}</span><span className="unit">min/km</span></div>
          <div id="pace-min-mile"><span className="value">{this.props.activity_data.mile_pace}</span><span className="unit">min/mile</span></div>
        </div>
      );
    }
    else if (this.props.activity_data.discipline == 'cycling' && this.props.activity_data.speed_km_hr) {
      speed = (
        <div className="average-speed section">
          <h4>Speed</h4>
          <div id="speed-kph"><span className="value">{this.props.activity_data.speed_km_hr}</span><span className="unit">kph</span></div>
          <div id="speed-mph"><span className="value">{this.props.activity_data.speed_miles_hr}</span><span className="unit">mph</span></div>
        </div>
      );
    }
    else if (this.props.activity_data.discipline == 'swimming' && this.props.activity_data.hectometer_pace) {
      speed = (
        <div className="average-pace section ">
          <h4>Average pace</h4>
          <div id="pace-min-100m">{this.props.activity_data.hectometer_pace}<span className="unit">min/100m</span></div>
        </div>
      );
    }

    var elevation_gain;
    if (this.props.activity_data.height_climbed_meters) {
      let examine_climbing_button;
      if (this.props.current_user_meta_data.user_id == 1) {
        examine_climbing_button = <div className="small-button" onClick={this.props.toggleDisplayClimbingAnalsisChart}>examine</div>
      }
      elevation_gain = (
        <div className="height-climbed section">
          <h4>Climbed</h4>
          <div id="height-climbed-meters"><span className="value">{xhale.helper.decimal_places(this.props.activity_data.height_climbed_meters, 0)}</span><span className="unit">m</span></div>
          <div id="height-climbed-feet"><span className="value">{xhale.helper.decimal_places(this.props.activity_data.height_climbed_feet, 0)}</span><span className="unit">ft</span></div>
          {examine_climbing_button}
        </div>
      );
    }

    var cadence;
    if (this.props.activity_data.average_cadence_rpm && this.props.activity_data.discipline != 'swimming') {
      if (this.props.activity_data.discipline == 'running') {
        var cadence_value = this.props.activity_data.average_cadence_rpm * 2;
        var cadence_unit = 'spm';
      }
      else {
        var cadence_value = this.props.activity_data.average_cadence_rpm;
        var cadence_unit = 'rpm';
      }
      cadence = (
        <div id="average-cadence" className="average-cadence section">
          <h4>Cadence</h4>
          <span className="value">{xhale.helper.decimal_places(cadence_value, 0)}</span><span className="unit">{cadence_unit}</span>
        </div>
      );
    }

    var heart_rate;
    if (this.props.activity_data.average_heart_rate_bpm) {
      heart_rate = (
        <div className="average-heart-rate section">
          <h4>Heart rate</h4>
          <div id="average-heart-rate-bpm"><span className="value">{xhale.helper.decimal_places(this.props.activity_data.average_heart_rate_bpm, 0)}</span><span className="unit">bpm</span></div>
          <div id="max-heart-rate-bpm"><span className="value">{xhale.helper.decimal_places(this.props.activity_data.max_heart_rate_bpm, 0)}</span><span className="unit">bpm max</span></div>
        </div>
      );
    }

    var watts;
    if (this.props.activity_data.normalized_watts || this.props.activity_data.average_watts) {

      var normalized_watts;
      var average_watts;

      if (this.props.activity_data.normalized_watts) {

        var normalized_power_to_weight;
        if (this.props.activity_data.normalized_power_to_weight) {
          normalized_power_to_weight = <span>/ <span className="watts-weight-ratio">{xhale.helper.decimal_places(this.props.activity_data.normalized_power_to_weight, 2)}</span><span className="unit">w/kg</span></span>
        }

        normalized_watts = (
          <div id="normalized-power-watts">
            <span className="value">{xhale.helper.decimal_places(this.props.activity_data.normalized_watts, 0)}</span><span className="unit">watts normalised </span>
            {normalized_power_to_weight}
          </div>
        );

      }

      if (this.props.activity_data.average_watts) {

        var average_power_to_weight;
        if (this.props.activity_data.average_power_to_weight) {
          average_power_to_weight = <span>/ <span className="watts-weight-ratio">{xhale.helper.decimal_places(this.props.activity_data.average_power_to_weight, 2)}</span><span className="unit">w/kg</span></span>
        }

        average_watts = (
          <div id="average-power-watts">
            <span className="value">{xhale.helper.decimal_places(this.props.activity_data.average_watts, 0)}</span><span className="unit">watts average</span>
            {average_power_to_weight}
          </div>
        );

      }

      watts = (
        <div className="power section">
          <h4>Power</h4>
          {normalized_watts}
          {average_watts}
        </div>
      );
    }

    var start_time_section;
    if (this.props.activity_data.local_start_time) {
      var start_time = xhale.helper.new_local_datetime(this.props.activity_data.local_start_time);
      var start_time_section = (
        <div className="start-time section">
          <h4>Start time</h4>
          <div id="start-time"><span className="value">{start_time.format("g:ia")}</span></div>
        </div>
      );
    }

    return (
      <div className="activity-summary__section activity-summary__overall-section" style={{ clear: 'both' }}>
        <div className="duration section">
          <h4>Time</h4>
          <div className="time">{this.props.activity_data.total_time}</div>
        </div>
        {distance_section}
        {elapsed_time}
        {start_time_section}
        {speed}
        {elevation_gain}
        {cadence}
        {heart_rate}
        {watts}
      </div>
    );
  }
}

class HighlightSummary extends React.Component {

  state = {
    zoomed_in_first_trackpoint_id: null,
    zoomed_in_last_trackpoint_id: null,
  }

  chart_is_zoomed_in = () => {
    return (this.state.zoomed_in_first_trackpoint_id == this.props.lap_data.first_trackpoint.id && this.state.zoomed_in_last_trackpoint_id == this.props.lap_data.last_trackpoint.id) ? true : false;
  }

  zoom_in_clicked = () => {
    var self = this;
    if (this.chart_is_zoomed_in()) {
      // Zoom out.
      var first_trackpoint = activities_section.state.reduced_trackpoints[this.props.activity_id][0];
      var last_trackpoint = activities_section.state.reduced_trackpoints[this.props.activity_id][activities_section.state.reduced_trackpoints[this.props.activity_id].length-1];
      activities_section.refs['activity_section_' + this.props.base_activity_id].setState({chart_display_mode: 'transitioning'});
      this.zoomTo(this.props.activity_id, first_trackpoint, last_trackpoint, () => {
        activities_section.refs['activity_section_' + this.props.base_activity_id].setState({chart_display_mode: 'normal', selected_laps_visible: true}, () => {
          activities_section.refs['activity_section_' + this.props.base_activity_id].displayLapOverlays();
        });
      });
      this.setState({zoomed_in_first_trackpoint_id: first_trackpoint.id, zoomed_in_last_trackpoint_id: last_trackpoint.id});
    }
    else {
      // Zoom in.
      activities_section.refs['activity_section_' + this.props.base_activity_id].setState({chart_display_mode: 'transitioning', selected_laps_visible: false}, () => {
        activities_section.refs['activity_section_' + this.props.base_activity_id].displayLapOverlays();
      });
      this.zoomTo(this.props.activity_id, this.props.lap_data.first_trackpoint, this.props.lap_data.last_trackpoint, () => {
        activities_section.refs['activity_section_' + this.props.base_activity_id].setState({chart_display_mode: 'zoom'});
      });
      this.setState({zoomed_in_first_trackpoint_id: this.props.lap_data.first_trackpoint.id, zoomed_in_last_trackpoint_id: this.props.lap_data.last_trackpoint.id});
    }
  }

  zoomTo = (activity_id, first_trackpoint, last_trackpoint, callback) => {

    function zoomMatchesHighlighted(highlighted_trackpoints) {
      if (!highlighted_trackpoints.first || !highlighted_trackpoints.last) {
        return false
      }
      else if
        (
          (
            first_trackpoint.reduced_array_index == highlighted_trackpoints.first.reduced_array_index ||
            first_trackpoint.reduced_array_index == highlighted_trackpoints.last.reduced_array_index
          )
          &&
          (
            last_trackpoint.reduced_array_index == highlighted_trackpoints.last.reduced_array_index ||
            last_trackpoint.reduced_array_index == highlighted_trackpoints.first.reduced_array_index
          )
        )
      {
        return true;
      }
      else {
        return false;
      }
    }

    let duration = 1000;
    var first_trackpoint_index = first_trackpoint.reduced_array_index;
    var last_trackpoint_index = last_trackpoint.reduced_array_index;
    var trackpoints = activities_section.state.reduced_trackpoints[activity_id].slice(parseInt(first_trackpoint_index), parseInt(last_trackpoint_index) + 1);

    // Calculate new min and max for scales.
    var zoom_distance_scale_min = min(trackpoints, function (d) { return d.distance_meters; });
    var zoom_distance_scale_max = max(trackpoints, function (d) { return d.distance_meters; });
    var updated_distance_scale = scaleLinear().range([0, chart_width]).domain([zoom_distance_scale_min, zoom_distance_scale_max]);

    var zoom_duration_scale_min = min(trackpoints, function (d) { return d.cumulative_duration_seconds; });
    var zoom_duration_scale_max = max(trackpoints, function (d) { return d.cumulative_duration_seconds; });
    var updated_duration_scale = scaleLinear().range([0, chart_width]).domain([zoom_duration_scale_min, zoom_duration_scale_max]);

    // If the highlighted section covers the same area as the whole
    // chart, hide the highlight. Otherwise show it again.
    if (zoomMatchesHighlighted(this.props.highlighted_trackpoints)) {
      $('#activity-' + activity_id + ' .highlight-box').fadeOut(duration, callback);
    }
    else {
      var zoomed_in_trackpoints = select("#activity-" + activity_id + " .chart .trackpoint-line").datum();

      if (this.props.x_scale_mode == 'distance') {
        var first_trackpoint_x_scale_position = updated_distance_scale(zoomed_in_trackpoints[0].distance_meters);
        var last_trackpoint_x_scale_position = updated_distance_scale(zoomed_in_trackpoints[zoomed_in_trackpoints.length-1].distance_meters);
      }
      else {
        var first_trackpoint_x_scale_position = updated_duration_scale(zoomed_in_trackpoints[0].cumulative_duration_seconds);
        var last_trackpoint_x_scale_position = updated_duration_scale(zoomed_in_trackpoints[zoomed_in_trackpoints.length-1].cumulative_duration_seconds);
      }
      var left_position = first_trackpoint_x_scale_position + margin.left;
      var right_position = last_trackpoint_x_scale_position + margin.left;
      var width = right_position - left_position;
      let highlight_box = select("#activity-" + activity_id + " .highlight-box");
      highlight_box.style("left", left_position + 'px');
      highlight_box.style("width", width + 'px');
      $('#activity-' + activity_id + ' .highlight-box').fadeIn(duration, callback);

    }

    var chart_types = [];
    var chart_css_ids = [];

    if (distance_line.elevation[activity_id]) {
      chart_types.push('elevation');
      chart_css_ids.push('elevation');
    }
    if (distance_line.speed[activity_id]) {
      chart_types.push('speed');
      chart_css_ids.push('speed');
    }
    if (distance_line.heart_rate[activity_id]) {
      chart_types.push('heart_rate');
      chart_css_ids.push('heart-rate');
    }
    if (distance_line.power[activity_id]) {
      chart_types.push('power');
      chart_css_ids.push('power');
    }
    if (distance_line.cadence[activity_id]) {
      chart_types.push('cadence');
      chart_css_ids.push('cadence');
    }

    // First redraw the line and area so the area surrounding it disappears.
    // At this point the data does not stretch out. This makes it visually
    // clearer to the user which section will stretch out in the transition.
    for (var index = 0; index < chart_types.length; index++) {
      var chart_type = chart_types[index];
      var chart_css_id = chart_css_ids[index];

      if (this.props.x_scale_mode == 'distance') {
        select("#activity-" + activity_id + " ." + chart_css_id + "-chart .trackpoint-line").datum(trackpoints).attr("d", distance_line[chart_type][activity_id]);
        select("#activity-" + activity_id + " ." + chart_css_id + "-chart .data-area").datum(trackpoints).attr("d", distance_area[chart_type][activity_id]);
      }
      else {
        select("#activity-" + activity_id + " ." + chart_css_id + "-chart .trackpoint-line").datum(trackpoints).attr("d", duration_line[chart_type][activity_id]);
        select("#activity-" + activity_id + " ." + chart_css_id + "-chart .data-area").datum(trackpoints).attr("d", duration_area[chart_type][activity_id]);
      }
    }

    // After the first redraw, change the scale. If this is done before the
    // first redraw the new scale will have applied itself immediately.
    x_distance_scale[activity_id] = updated_distance_scale;
    x_duration_scale[activity_id] = updated_duration_scale;

    // Apply the new scale to the data and slowly stretch it out.
    for (var index = 0; index < chart_types.length; index++) {
      var chart_type = chart_types[index];
      var chart_css_id = chart_css_ids[index];

      // Set the new scale on the axis.
      x_distance_axis[activity_id].scale(x_distance_scale[activity_id]);
      x_duration_axis[activity_id]
        .tickValues(calculateDurationTickValues(x_duration_scale[activity_id]))
        .scale(x_duration_scale[activity_id]);

      // Stretch the axis, line and area to fill the width of the graph.
      select("#activity-" + activity_id + " .axis.x.distance." + chart_css_id).transition().duration(duration).call(x_distance_axis[activity_id]);
      select("#activity-" + activity_id + " .axis.x.duration." + chart_css_id).transition().duration(duration).call(x_duration_axis[activity_id]);

      if (this.props.x_scale_mode == 'distance') {
        select("#activity-" + activity_id + " ." + chart_css_id + "-chart .trackpoint-line").transition().duration(duration).attr("d", distance_line[chart_type][activity_id]);
        select("#activity-" + activity_id + " ." + chart_css_id + "-chart .data-area").transition().duration(duration).attr("d", distance_area[chart_type][activity_id]);
      }
      else {
        select("#activity-" + activity_id + " ." + chart_css_id + "-chart .trackpoint-line").transition().duration(duration).attr("d", duration_line[chart_type][activity_id]);
        select("#activity-" + activity_id + " ." + chart_css_id + "-chart .data-area").transition().duration(duration).attr("d", duration_area[chart_type][activity_id]);
      }
    }
  }

  /**
   * Save button was clicked.
   */
  handleSaveLapButtonClick = () => {
    var temporary_id = xhale.helper.get_unique_temporary_id('lap');
    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({currently_saving_temporary_lap_id: temporary_id});
    this.save_highlighted_section(temporary_id);
  }

  save_highlighted_section = (temporary_id) => {
    var post_data = {}

    post_data.temporary_id = temporary_id;
    post_data.activity_id = this.props.activity_id;
    post_data.first_trackpoint_id = this.props.lap_data.first_trackpoint.id;
    post_data.last_trackpoint_id = this.props.lap_data.last_trackpoint.id;
    post_data.moving_duration_seconds = this.props.lap_data.moving_duration_seconds;
    post_data.distance_meters = this.props.lap_data.distance_meters;
    post_data.height_climbed_meters = this.props.lap_data.height_climbed_meters;
    post_data.average_cadence_rpm = this.props.lap_data.average_cadence_rpm;
    post_data.average_power_watts = this.props.lap_data.average_power_watts;
    post_data.average_heart_rate_bpm = this.props.lap_data.average_heart_rate_bpm;
    post_data.max_heart_rate_bpm = (this.props.lap_data.max_heart_rate_bpm) ? this.props.lap_data.max_heart_rate_bpm : null;

    // Add to lap set.
    if (this.props.lapset_edit_mode.active && this.props.lapset_edit_mode.editing_lapset != 'overall_activity') {
      post_data.lapset_id = this.props.lapset_edit_mode.editing_lapset;
    }

    ajaxRequest({
      url: '/training-logs/save-lap/',
      data: post_data,
      method: 'POST',
      success: this.lapSaved,
      send_as_form_values: true,
      expected_response_type: 'json',
    });

  }

  lapSaved = (response, status) => {
    let laps = activities_section.state.laps;

    // Update the local store of laps.
    let lap = new Lap(response.lap);
    let activity_id = lap.activity_id
    laps[lap.id] = lap;

    // If temporary_id is the same as the one currently set as being saved,
    // update the view of it.
    if (activities_section.refs['activity_section_' + this.props.base_activity_id].state.currently_saving_temporary_lap_id == response.lap.temporary_id) {
      activities_section.refs['activity_section_' + this.props.base_activity_id].updateHighlightedLap(activity_id, lap.id);
    }

    var lapset_activity_summaries = activities_section.refs['activity_section_' + this.props.base_activity_id].state.lap_set_activity_summaries;
    if ('lapset' in response) {
      addToArrayIfNotExists(lap.lapsets, response.lapset.id);
      for (var i = 0; i < response.lapset_activity_summaries.length; i++) {
        var activity_summary = response.lapset_activity_summaries[i];
        lapset_activity_summaries[activity_summary.activity_id][activity_summary.lapset_id] = activity_summary;
      }
    }

    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({
      laps: laps,
      lap_set_activity_summaries: lapset_activity_summaries,
      active_extras_tab: 'laps',
    });

    // If the laps table is visible, scroll the new lap can be seen.
    var extras_element = $('#activity-' + activity_id + ' .top-section-extras .lap-tables-container .lap-tables');
    if (extras_element.length > 0) {
      var scroll_position = $('#lap-' + lap.id).offset().top - extras_element.offset().top + extras_element.scrollTop();
      extras_element.animate({ scrollTop: scroll_position });
    }
  }

  delete_lap_clicked = () => {
    activateConfirmationBox({
      message: "Are you sure you want to delete this lap?",
      confirm_callback: this.deleteLapCallback
    });
  }

  deleteLapCallback = () => {
    ajaxRequest({
      url: '/training-logs/delete-lap/' + this.props.lap_data.lap_id + '/',
      method: 'POST',
      success: this.lapDeletedSuccessCallback,
      expected_response_type: 'json',
    });
  }

  lapDeletedSuccessCallback = (response, status) => {
    let lap_row = $('#lap-' + response.lap_id)
    let other_rows = $(lap_row).siblings();

    let removeLap = function () {
      let laps = Object.assign({}, activities_section.state.laps);
      delete(laps[response.lap_id]);
      activities_section.refs['activity_section_' + response.activity_id].setState({laps: laps});
    }

    if (other_rows.length > 0) {
      lap_row.fadeOut('10000', removeLap);
    }
    else {
      $(lap_row).parents('.laps-container').fadeOut('10000', removeLap);
    }

  }

  alter_highlight_size = (first_or_last, delta) => {
    var metric = (this.props.x_scale_mode == 'distance') ? 'distance_meters' : 'cumulative_duration_seconds';
    var scale = (this.props.x_scale_mode == 'distance') ? x_distance_scale[this.props.activity_id] : x_duration_scale[this.props.activity_id];

    // Ensure start and end of trackpoints are in the expected order.
    if (this.props.highlighted_trackpoints.first.index > this.props.highlighted_trackpoints.last.index) {
      var first_trackpoint = this.props.highlighted_trackpoints.first;
      var last_trackpoint = this.props.highlighted_trackpoints.last;
      this.props.highlighted_trackpoints.last = first_trackpoint;
      this.props.highlighted_trackpoints.first = last_trackpoint;
    }

    var current_trackpoint_index = parseInt(this.props.highlighted_trackpoints[first_or_last].unfiltered_array_index);
    // Change trackpoint unless this would lead to heading off the edge of the chart.
    if (typeof(this.props.activity_data[this.props.activity_id].trackpoints[current_trackpoint_index + delta]) != 'undefined') {
      this.props.highlighted_trackpoints[first_or_last] = this.props.activity_data[this.props.activity_id].trackpoints[current_trackpoint_index + delta];
      this.props.highlighted_trackpoints[first_or_last].x_scale_position = scale(this.props.highlighted_trackpoints[first_or_last][metric]);
      activities_section.refs['activity_section_' + this.props.base_activity_id].updateHighlightedLap(this.props.activity_id);
    }
  }

  minus_one_trackpoint_from_start_clicked = (event) => {
    this.alter_highlight_size('first', -1);
  }

  plus_one_trackpoint_from_start_clicked = (event) => {
    this.alter_highlight_size('first', 1);
  }

  minus_one_trackpoint_from_end_clicked = (event) => {
    this.alter_highlight_size('last', -1);
  }

  plus_one_trackpoint_from_end_clicked = (event) => {
    this.alter_highlight_size('last', 1);
  }

  render () {

    if (!this.props.lap_data.first_trackpoint) {
      return (
        <div className="activity-summary__section activity-summary__highlighted-section" style={{clear: 'both'}}>
          <div className="activity-summary__help-text">
            <p>Highlight a section of the charts below or click on a lap to see a summary of that segment.</p>
          </div>
        </div>
      )
    }

    var moving_duration = xhale.helper.format_time_seconds(this.props.lap_data.moving_duration_seconds);
    var elapsed_time = xhale.helper.format_time_seconds(this.props.lap_data.elapsed_duration_seconds);
    var distance_km = xhale.helper.format_km_from_meters(this.props.lap_data.distance_meters, 2);
    var distance_miles = xhale.helper.format_miles_from_meters(this.props.lap_data.distance_meters, 2);
    var speed_kph = xhale.helper.format_km(this.props.lap_data.kph, 2);
    var speed_mph = xhale.helper.format_miles(this.props.lap_data.mph, 2);
    var pace_min_km = formatSecondsAsPace(this.props.lap_data.pace_min_km);
    var pace_min_mile = formatSecondsAsPace(this.props.lap_data.pace_min_mile);
    var height_climbed_meters = xhale.helper.decimal_places(this.props.lap_data.height_climbed_meters, 0);
    var height_climbed_feet = xhale.helper.decimal_places(this.props.lap_data.height_climbed_feet, 0);
    var average_heart_rate_bpm = xhale.helper.decimal_places(this.props.lap_data.average_heart_rate_bpm, 0, 'zero');
    var max_heart_rate_bpm = xhale.helper.decimal_places(this.props.lap_data.max_heart_rate_bpm, 0, 'zero');
    var average_power_watts = xhale.helper.decimal_places(this.props.lap_data.average_power_watts, 0, 'zero');
    var start_time = xhale.helper.format_time_seconds(this.props.lap_data.first_trackpoint.cumulative_duration_seconds);
    var end_time = xhale.helper.format_time_seconds(this.props.lap_data.last_trackpoint.cumulative_duration_seconds);

    if (this.props.discipline == 'running') {
      var average_cadence_rpm = xhale.helper.decimal_places(this.props.lap_data.average_cadence_rpm * 2, 0, 'zero');
      var cadence_unit = 'spm';
    }
    else {
      var average_cadence_rpm = xhale.helper.decimal_places(this.props.lap_data.average_cadence_rpm, 0, 'zero');
      var cadence_unit = 'rpm';
    }

    if (this.props.distance_unit == 'km') {
      var start_distance = <span className="start-distance"><span className="value">{xhale.helper.format_km_from_meters(this.props.lap_data.first_trackpoint.distance_meters, 2)}</span><span className="unit">km</span></span>;
      var end_distance = <span className="end-distance"><span className="value">{xhale.helper.format_km_from_meters(this.props.lap_data.last_trackpoint.distance_meters, 2)}</span><span className="unit">km</span></span>;
    }
    else if (this.props.distance_unit == 'miles') {
      var start_distance = <span className="start-distance"><span className="value">{xhale.helper.format_miles_from_meters(this.props.lap_data.first_trackpoint.distance_meters, 2)}</span><span className="unit">mi</span></span>;
      var end_distance = <span className="end-distance"><span className="value">{xhale.helper.format_miles_from_meters(this.props.lap_data.last_trackpoint.distance_meters, 2)}</span><span className="unit">mi</span></span>;
    }

    var average_power_to_weight_jsx;
    if (athlete_weight) {
      var average_watts_per_kg = xhale.helper.decimal_places(this.props.lap_data.average_power_watts / athlete_weight, 2, 'zero', true);
      var average_power_to_weight_jsx = (
        <span className="power-to-weight">/ <span className="value">{average_watts_per_kg}</span><span className="unit">w/kg</span></span>
      );
    }

    if (this.props.discipline == 'running') {
      var average_speed_section = (
        <div className="average-pace section">
          <h4>Pace</h4>
          <div id="pace-min-km">
            <span className="value">{pace_min_km}</span><span className="unit">min/km</span>
          </div>
          <div id="pace-min-mile">
            <span className="value">{pace_min_mile}</span><span className="unit">min/mile</span>
          </div>
        </div>
      );
    }
    else if (this.props.discipline == 'cycling') {
      var average_speed_section = (
        <div className="average-speed section">
          <h4>Speed</h4>
          <div id="speed-kph">
            <span className="value">{speed_kph}</span><span className="unit">kph</span>
          </div>
          <div id="speed-mph">
            <span className="value">{speed_mph}</span><span className="unit">mph</span>
          </div>
        </div>
      );
    }

    var average_heart_rate_section;
    if (average_heart_rate_bpm || max_heart_rate_bpm) {
      average_heart_rate_section = (
        <div className="average-heart-rate section">
          <h4>Heart rate</h4>
          <div id="average-heart-rate-bpm">
            <span className="value">{average_heart_rate_bpm}</span><span className="unit">bpm</span>
          </div>
          <div id="max-heart-rate-bpm">
            <span className="value">{max_heart_rate_bpm}</span><span className="unit">bpm max</span>
          </div>
        </div>
      );
    }

    var average_cadence_section;
    if (average_cadence_rpm) {
      average_cadence_section = (
        <div id="average-cadence" className="average-cadence section">
          <h4>Cadence</h4>
          <span className="value">{average_cadence_rpm}</span><span className="unit">{cadence_unit}</span>
        </div>
      );
    }

    var average_power_section;
    if (average_power_watts) {
      average_power_section = (
        <div className="power section">
          <h4>Power</h4>
          <div id="average-power-watts">
            <span className="value">{average_power_watts}</span><span className="unit">watts average</span> {average_power_to_weight_jsx}
          </div>
        </div>
      );
    }

    var height_climbed_section;
    if (height_climbed_meters || height_climbed_feet) {
      height_climbed_section = (
        <div className="height-climbed section">
          <h4>Climbed</h4>
          <div id="height-climbed-meters">
            <span className="value">{height_climbed_meters}</span><span className="unit">m</span>
          </div>
          <div id="height-climbed-feet">
            <span className="value">{height_climbed_feet}</span><span className="unit">ft</span>
          </div>
        </div>
      );
    }

    var lap_save_delete_button;
    if (this.props.lap_data.lap_id && this.props.lap_data.has_delete_permission) {
      lap_save_delete_button = (
        <div onClick={this.delete_lap_clicked} className="activity-summary__button activity-summary__delete-lap-button">Delete lap</div>
      );
    }
    else if (!this.props.lap_data.lap_id) {
      if (this.props.currently_saving_temporary_lap_id != null) {
        var button_text = 'Saving...';
      }
      else {
        var button_text = 'Save lap';
      }
      lap_save_delete_button = (<div onClick={this.handleSaveLapButtonClick} className="activity-summary__button activity-summary__save-highlight-button">{button_text}</div>);
    }

    var minus_one_trackpoint_from_start_button = (<span onClick={this.minus_one_trackpoint_from_start_clicked} className="activity-summary__button activity-summary__button_inline no-select minus-one-trackpoint-from-start">{String.fromCharCode(8592)}</span>);
    var plus_one_trackpoint_from_start_button = (<span onClick={this.plus_one_trackpoint_from_start_clicked} className="activity-summary__button activity-summary__button_inline no-select plus-one-trackpoint-from-start">{String.fromCharCode(8594)}</span>);
    var minus_one_trackpoint_from_end_button = (<span onClick={this.minus_one_trackpoint_from_end_clicked} className="activity-summary__button activity-summary__button_inline no-select minus-one-trackpoint-from-end">{String.fromCharCode(8592)}</span>);
    var plus_one_trackpoint_from_end_button = (<span onClick={this.plus_one_trackpoint_from_end_clicked} className="activity-summary__button activity-summary__button_inline no-select plus-one-trackpoint-from-end">{String.fromCharCode(8594)}</span>);

    var zoom_button_text = (this.chart_is_zoomed_in()) ? 'Zoom out' : 'Zoom in';

    return (
      <div className="activity-summary__section activity-summary__highlighted-section" style={{clear: 'both'}}>
        <div className="duration section">
          <h4>Time</h4>
          <div className="time">{moving_duration}</div>
        </div>
        <div className="distance section">
          <h4>Distance</h4>
          <div className="details">
            <span className="km"><span className="value">{distance_km}</span><span className="unit">km</span></span> <span className="separator">/</span> <span className="miles"><span className="value">{distance_miles}</span><span className="unit">miles</span></span>
          </div>
        </div>
        <div className="activity-summary__buttons">
          {lap_save_delete_button}
          <div onClick={this.zoom_in_clicked} className="activity-summary__button activity-summary__zoom-button zoom-to-highlight">{zoom_button_text}</div>
        </div>
        <div className="elapsed-time section">
          <h4>Elapsed</h4>
          <span className="value">{elapsed_time}</span>
        </div>
        {average_speed_section}
        {height_climbed_section}
        {average_cadence_section}
        {average_heart_rate_section}
        {average_power_section}
        <div className="start-end" style={{ width: '100%', textAlign: 'center', clear: 'left', position: 'absolute', bottom: '10px' }}>
          <span className="from">{minus_one_trackpoint_from_start_button} {plus_one_trackpoint_from_start_button} <span className="start-time">{start_time}</span> / {start_distance}</span> <span style={{color: '#c1c0c0', fontStyle: 'italic', letterSpacing: '1px'}}>to</span> <span className="to"><span className="end-time">{end_time}</span> / {end_distance} {minus_one_trackpoint_from_end_button} {plus_one_trackpoint_from_end_button} </span>
       </div>
      </div>
    );

  }
}

class ChartSpeedConfigurationPopupRow extends React.Component {

  handleRowClick = () => {

    let pace_object_store_index = this.props.target_run_paces.findIndex(pace => pace.id === this.props.pace_id);

    // Toggle pace object with appropriate visibility.
    this.props.target_run_paces[pace_object_store_index].visible = (this.props.target_run_paces[pace_object_store_index].visible) ? false : true;

    // Update the React state.
    activities_section.refs['activity_section_' + this.props.activity_id].setState({target_run_paces: target_run_paces});

    // Now select each visible label element and move it to the appropriate
    // place so they don't overlap.
    rebuild_target_run_paces(this.props.target_run_paces, this.props.distance_unit, this.props.activity_id);

  }

  render() {

    var show_hide_text = (this.props.visible) ? 'hide' : 'show';
    var visibility_class = (this.props.visible) ? 'visible' : 'hidden';
    return (
      <tr onClick={this.handleRowClick } className={visibility_class}>
        <td className="description">{this.props.description}</td>
        <td className="pace-km">{this.props.pace_km}<span className='unit'>min/km</span></td>
        <td className="pace-miles">{this.props.pace_miles}<span className='unit'>min/mile</span></td>
        <td className="show-hide-button"><div className="inner">{show_hide_text}</div></td>
      </tr>
    );
  }
}

class ChartSwimPaceConfigurationPopupRow extends React.Component {

  handleRowClick = () => {

    let pace_object_store_index = this.props.target_swim_paces.findIndex(pace => pace.id === this.props.pace_id);

    // Toggle pace object with appropriate visibility.
    this.props.target_swim_paces[pace_object_store_index].visible = (this.props.target_swim_paces[pace_object_store_index].visible) ? false : true;

    // Update the React state.
    activities_section.refs['activity_section_' + this.props.activity_id].setState({target_swim_paces: target_swim_paces});

  }

  render() {

    var show_hide_text = (this.props.visible) ? 'hide' : 'show';
    var visibility_class = (this.props.visible) ? 'visible' : 'hidden';
    return (
      <tr onClick={this.handleRowClick } className={visibility_class}>
        <td className="description">{this.props.description}</td>
        <td className="pace-100m">{this.props.pace_100m}<span className='unit'>min/100m</span></td>
        <td className="show-hide-button"><div className="inner">{show_hide_text}</div></td>
      </tr>
    );
  }
}



class ChartSwimPaceConfigurationPopup extends React.Component {

  handleClose = (event) => {
    event.preventDefault();
    activities_section.refs['activity_section_' + this.props.activity_id].setState({swim_pace_configuration_popup_visible: false});
  }

  handleOuterboxClose = (event) => {
    if (event.target == $('.chart-speed-configuration-popup-overlay')[0]) {
      activities_section.refs['activity_section_' + this.props.activity_id].setState({swim_pace_configuration_popup_visible: false});
    }
  }

  render() {

    var swim_pace_rows = [];
    for (var i = 0; i < this.props.target_swim_paces.length; i++) {
      var id = this.props.target_swim_paces[i].id;
      var description = this.props.target_swim_paces[i].description;
      var pace_100m = formatSecondsAsPace(metersPerSecondAsSecondsPerHundredMeters(this.props.target_swim_paces[i].meters_per_second));
      var visible = this.props.target_swim_paces[i].visible;
      swim_pace_rows.push(
        <ChartSwimPaceConfigurationPopupRow
          key={id}
          pace_id={id}
          activity_id={this.props.activity_id}
          description={description}
          pace_100m={pace_100m}
          visible={visible}
          distance_unit={this.props.distance_unit}
          target_swim_paces={this.props.target_swim_paces}
        />
      );
    }

    var display = this.props.visible ? 'block' : 'none';

    let scale_metric = (this.props.distance_unit == 'km') ? 'min/km' : 'min/mile';

    let swim_pace_section;
    if (swim_pace_rows.length > 0) {
      swim_pace_section = (
        <div>
          <h3>Swim paces</h3>
          <div className="instructions">Click a row to display it on the speed chart.</div>
          <table>
            <tbody>
              {swim_pace_rows}
            </tbody>
          </table>
        </div>
      );
    }
    else {
      swim_pace_section = (
        <div>
          <h3>Swim paces</h3>
          <div className="instructions">Add swim paces in settings in order to display them on this chart.</div>
        </div>
      );
    }

    return (
      <div className="chart-speed-configuration-popup-overlay" onClick={this.handleOuterboxClose} style={{display: display}}>
        <div className="chart-speed-configuration-popup-inner">
          <a className="close-popup light" href="#" title="Close" onClick={this.handleClose}>Close</a>
          {swim_pace_section}
        </div>
      </div>
    );
  }
}

class ChartSpeedConfigurationPopup extends React.Component {

  handleClose = (event) => {
    event.preventDefault();
    activities_section.refs['activity_section_' + this.props.activity_id].setState({speed_configuration_popup_visible: false});
  }

  handleOuterboxClose = (event) => {
    if (event.target == $('.chart-speed-configuration-popup-overlay')[0]) {
      activities_section.refs['activity_section_' + this.props.activity_id].setState({speed_configuration_popup_visible: false});
    }
  }

  render() {

    var run_pace_rows = [];
    for (var i = 0; i < this.props.target_run_paces.length; i++) {
      var id = this.props.target_run_paces[i].id;
      var description = this.props.target_run_paces[i].description;
      var pace_km = formatSecondsAsPace(xhale.helper.meters_per_second_as_seconds_per_km(this.props.target_run_paces[i].meters_per_second));
      var pace_miles = formatSecondsAsPace(xhale.helper.meters_per_second_as_seconds_per_mile(this.props.target_run_paces[i].meters_per_second));
      var visible = this.props.target_run_paces[i].visible;
      run_pace_rows.push(
        <ChartSpeedConfigurationPopupRow
          key={id}
          pace_id={id}
          activity_id={this.props.activity_id}
          description={description}
          pace_km={pace_km}
          pace_miles={pace_miles}
          visible={visible}
          distance_unit={this.props.distance_unit}
          target_run_paces={this.props.target_run_paces}
        />
      );
    }

    var display = this.props.visible ? 'block' : 'none';

    let scale_metric = (this.props.distance_unit == 'km') ? 'min/km' : 'min/mile';

    let run_pace_section;
    if (run_pace_rows.length > 0) {
      run_pace_section = (
        <div>
          <h3>Run paces</h3>
          <div className="instructions">Click a row to display it on the speed chart.</div>
          <table>
            <tbody>
              {run_pace_rows}
            </tbody>
          </table>
        </div>
      );
    }
    else {
      run_pace_section = (
        <div>
          <h3>Run paces</h3>
          <div className="instructions">Add run paces in settings in order to display them on this chart.</div>
        </div>
      );
    }

    return (
      <div className="chart-speed-configuration-popup-overlay" onClick={this.handleOuterboxClose} style={{display: display}}>
        <div className="chart-speed-configuration-popup-inner">
          <a className="close-popup light" href="#" title="Close" onClick={this.handleClose}>Close</a>
          {run_pace_section}
          <h3>Speed Scale</h3>
          <div className="instructions">
            <DurationInput value={this.props.speed_scale_min} onChange={this.props.setSpeedScaleMin} format="MM:SS" />
            {' '}
            <span className='unit'>{scale_metric}</span>
            {' '}
            <span>to</span>
            {' '}
            <DurationInput value={this.props.speed_scale_max} onChange={this.props.setSpeedScaleMax} format="MM:SS" />
            {' '}
            <span className='unit'>{scale_metric}</span>
          </div>
        </div>
      </div>
    );
  }
}

class ChartPowerConfigurationPopupRow extends React.Component {

  handleRowClick = () => {

    let power_object_store_index = this.props.target_bike_powers.findIndex(power => power.id === this.props.power_id);

    // Toggle power object with appropriate visibility.
    this.props.target_bike_powers[power_object_store_index].visible = (this.props.target_bike_powers[power_object_store_index].visible) ? false : true;

    // Update the React state.
    activities_section.refs['activity_section_' + this.props.activity_id].setState({target_power: this.props.target_bike_powers});

    // Now select each visible label element and move it to the appropriate
    // place so they don't overlap.
    rebuild_target_power(this.props.target_bike_powers);

  }

  render() {
    var show_hide_text = (this.props.visible) ? 'hide' : 'show';
    var visibility_class = (this.props.visible) ? 'visible' : 'hidden';
    return (
      <tr onClick={this.handleRowClick } className={visibility_class}>
        <td className="description">{this.props.description}</td>
        <td className="power-watts">{this.props.watts}<span className='unit'>watts</span></td>
        <td className="show-hide-button"><div className="inner">{show_hide_text}</div></td>
      </tr>
    );
  }
}

class ChartPowerConfigurationPopup extends React.Component {

  state = { target_power: this.props.target_bike_powers }

  handleClose = (event) => {
    event.preventDefault();
    activities_section.refs['activity_section_' + this.props.activity_id].setState({power_configuration_popup_visible: false});
  }

  handleOuterboxClose = (event) => {
    if (event.target == $('.chart-power-configuration-popup-overlay')[0]) {
      activities_section.refs['activity_section_' + this.props.activity_id].setState({power_configuration_popup_visible: false});
    }
  }

  render() {

    var power_target_rows = [];
    for (var i = 0; i <  this.state.target_power.length; i++) {
      var id = this.state.target_power[i].id;
      var description = this.state.target_power[i].description;
      var watts = this.state.target_power[i].watts;
      var visible = this.state.target_power[i].visible;
      power_target_rows.push(
        <ChartPowerConfigurationPopupRow
          key={id}
          power_id={id}
          activity_id={this.props.activity_id}
          description={description}
          watts={watts}
          visible={visible}
          target_bike_powers={this.props.target_bike_powers}
        />
      );
    }

    var display = this.props.visible ? 'block' : 'none';

    let power_section;
    if (power_target_rows.length > 0) {
      power_section = (
        <div>
          <h3>Target power</h3>
          <div className="instructions">Click a row to display it on the power chart.</div>
          <table>
            <tbody>
              {power_target_rows}
            </tbody>
          </table>
        </div>
      );
    }
    else {
      power_section = (
        <div>
          <h3>Target power</h3>
          <div className="instructions">Add target powers in settings in order to display them on this chart.</div>
        </div>
      )
    }

    return (
      <div className="chart-power-configuration-popup-overlay" onClick={this.handleOuterboxClose} style={{display: display}}>
        <div className="chart-power-configuration-popup-inner">
          <a className="close-popup light" href="#" title="Close" onClick={this.handleClose}>Close</a>
          {power_section}
          <h3>Power Scale</h3>
          <div className="instructions">
            <span>From</span>
            {' '}
            <input
              type="number"
              name="power_scale_min"
              id="power_scale_min"
              min="0"
              step="1"
              value={parseInt(this.props.power_scale_min)}
              onChange={this.props.setPowerScaleMin}
            />
            {' '}
            <span>to</span>
            {' '}
            <input
              type="number"
              name="power_scale_max"
              id="power_scale_max"
              min="0"
              step="1"
              value={parseInt(this.props.power_scale_max)}
              onChange={this.props.setPowerScaleMax}
            />
          </div>
        </div>
      </div>
    );
  }
}

class ShareWidget extends React.Component {

  constructor(props) {

    super(props);

    if (typeof(race) != 'undefined') {
      this.state = {race: race}
    }
    else if (typeof(training_session) != 'undefined') {
      this.state = {training_session: training_session}
    }

  }

  render() {
    if (this.state.race) {
      var session_type = 'race';
      var path = '/race/' + this.state.race.id + '/';
      var share_text = 'Check%20out%20my%20race';
      var shared_publicly = this.state.race.shared_publicly;
    }
    else if (this.state.training_session) {
      var session_type = 'training session';
      var path = '/session/' + this.state.training_session.id + '/';
      var share_text = 'Check%20out%20my%20training%20session';
      var shared_publicly = this.state.training_session.shared_publicly;
    }

    // TODO Add Twitter link
    // var twitter_share_button = <a className="twitter-share-button" href={'https://twitter.com/intent/tweet?url=https%3A//trainxhale.com' + path + '&text=' + share_text + '&via=TrainXhale'}>Tweet</a>;
    // TODO Add option of how much is shown.
    // <div>Just the data</div>
    // <div>The data and your post {session_type} comments</div>

    if (shared_publicly) {
      var privacy_text = (<p>This {session_type} is public. Copy the URL to share.</p>);
    }
    else {
      var csrf_token = xhale.helper.getCookie('csrftoken');
      var privacy_text = (
        <div>
          <p>Press the button below to allow everyone to see this {session_type}.</p>
          <form id="share_form" action="" method="post">
            <input type="hidden" name="csrfmiddlewaretoken" value={csrf_token} />
            <div className="button-wrapper">
              <input name="share-session" type="submit" value={'Share this ' + session_type} className="standard-button" />
            </div>
          </form>
        </div>
      );
    }

    return (
      <div id="share">
        <h2>Share this {session_type}</h2>
        {privacy_text}
      </div>
    );
  }
}

class ChartSection extends React.Component {

  // Store the current chart mode so that we don't worry about doing the
  // transition between modes on every update. Modes include full_session,
  // lap_set_only and overlaid_laps.
  current_mode = 'full_session';

  define_speed_y_scales = () => {

    var lowest_values = [];
    var highest_values = [];
    Object.keys(this.props.activity_data).forEach((activity_id) => {

      if (
        typeof(activities_section.state.reduced_trackpoints[activity_id]) != 'undefined' &&
        (activity_id == this.props.activity_id || this.props.visible_lapset_activities.indexOf(parseInt(activity_id)) > -1)
      ) {
        lowest_values.push(this.props.getSpeedScaleMin(activities_section.state.reduced_trackpoints[activity_id], this.props.metric, true));
        highest_values.push(this.props.getSpeedScaleMax(activities_section.state.reduced_trackpoints[activity_id], this.props.metric, true));
      }

    });

    let speed_scale_min = min(lowest_values);
    let speed_scale_max = max(highest_values);

    if (this.props.metric == 'pace_seconds_per_km' || this.props.metric == 'pace_seconds_per_mile') {
      var speed_domain = [speed_scale_min, speed_scale_max];
    }
    else {
      var speed_domain = [speed_scale_max, speed_scale_min];
    }

    y_scale.speed[this.props.base_activity_id] = scaleLinear()
      .range([0, height])
      .domain(speed_domain);

  }

  define_y_scales = () => {

    var metric = this.props.metric;

    // Here I need to go through all the activities.
    var scale_lowest_values = [];
    var scale_highest_values = [];
    Object.keys(this.props.activity_data).forEach((activity_id) => {

      if (
        typeof(activities_section.state.reduced_trackpoints[activity_id]) != 'undefined' &&
        (activity_id == this.props.activity_id || this.props.visible_lapset_activities.indexOf(parseInt(activity_id)) > -1)
      ) {
        if (metric == 'power_watts') {
          scale_lowest_values.push(this.props.getPowerScaleMin(activities_section.state.reduced_trackpoints[activity_id], true));
          scale_highest_values.push(this.props.getPowerScaleMax(activities_section.state.reduced_trackpoints[activity_id], true));
        }
        else {
          scale_lowest_values.push(min(activities_section.state.reduced_trackpoints[activity_id], function (d) { return d[metric]; }));
          scale_highest_values.push(max(activities_section.state.reduced_trackpoints[activity_id], function (d) { return d[metric]; }));
        }
      }

    });

    var scale_lowest_value = min(scale_lowest_values);
    var scale_highest_value = max(scale_highest_values);

    // Ensure scales are of a minimum size.
    var scale_delta = scale_highest_value - scale_lowest_value;
    switch (this.props.chart_type) {
      case 'elevation':
        scale_highest_value = (scale_delta > 50) ? scale_highest_value : scale_lowest_value + 50 ;
        break;
    }

    // Run cadence should be doubled to get steps per minute.
    if (this.props.discipline == 'running' && this.props.metric == 'cadence_rpm') {
      scale_highest_value = scale_highest_value * 2;
    }

    y_scale[this.props.chart_type][this.props.base_activity_id] = scaleLinear()
      .range([0, height])
      .domain([scale_highest_value, scale_lowest_value]);
  }

  formatDistance = (meters) => {
    if (this.props.distance_unit == 'km') {
      return xhale.helper.format_km_from_meters(meters, 1) + 'km';
    }
    else if (this.props.distance_unit == 'miles') {
      return xhale.helper.format_miles_from_meters(meters, 1) + 'mi';
    }
  }

  createChart = () => {

    var self = this;
    var chart_css_id = this.props.chart_type.replace('_', '-');
    var start_datetime = min(activities_section.state.reduced_trackpoints[this.props.activity_id], function (d) { return d.datetime; });

    // Scales.

    x_distance_scale[this.props.activity_id] = scaleLinear()
      .range([0, chart_width])
      .domain([
        min(activities_section.state.reduced_trackpoints[this.props.activity_id], function (d) { return d.distance_meters; }),
        max(activities_section.state.reduced_trackpoints[this.props.activity_id], function (d) { return d.distance_meters; })
      ]);

    var start_datetime = min(activities_section.state.reduced_trackpoints[this.props.activity_id], function (d) { if (d.datetime) { return d.datetime } });
    var last_datetime = max(activities_section.state.reduced_trackpoints[this.props.activity_id], function (d) { if (d.datetime) { return d.datetime } });
    var max_duration_milliseconds = last_datetime - start_datetime;
    var max_duration_seconds = max_duration_milliseconds / 1000;

    x_duration_scale[this.props.activity_id] = scaleLinear()
      .range([0, chart_width])
      .domain([0, max_duration_seconds]);

    if (this.props.chart_type == 'speed') {
      this.define_speed_y_scales();
    }
    else {
      this.define_y_scales();
    }

    // Define 3 axis. A duration and distance one to flip between on x dimension
    // and a y axis to show what ever the metric of this chart is.
    x_distance_axis[this.props.base_activity_id] = axisBottom()
      .scale(x_distance_scale[this.props.activity_id])
      .ticks(calculateNumberOfTicks(chart_width, 55))
      .tickFormat(this.formatDistance)

    x_duration_axis[this.props.base_activity_id] = axisBottom()
      .scale(x_duration_scale[this.props.activity_id])
      .tickValues(calculateDurationTickValues(x_duration_scale[this.props.activity_id]))
      .tickFormat(xhale.helper.format_time_seconds)

    this.y_axis = axisLeft()
      .scale(y_scale[this.props.chart_type][this.props.base_activity_id])
      .ticks(5)

    // Define the width of the chart container (the part that includes labels).
    var chart_container = select('#activity-' + this.props.base_activity_id + " ." + chart_css_id + "-chart .chart")

    // Create the container for the actual chart and position it off center so
    // that there is room for margins.
    var chart = chart_container.append("g")
      .attr("class", "chart-group")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    // Stop outlier data overflowing from the chart.
    chart.append("clipPath")
      .attr("id", "clip-" + this.props.base_activity_id + "-" + chart_css_id)
      .append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", chart_width)
        .attr("height", height);

    chart.append("g")
      .attr("class", "plot-area")

    // Y axis label.
    var y_axis_label = chart.append("text")
      .attr("class", "y-axis-label " + chart_css_id)
      .style("font-size", "12px")
      .style("text-anchor", "middle")
      .text(this.props.y_axis_label)
      .attr("transform", "rotate(270)")
      .attr("dx", 0 - (height / 2))
      .attr("dy", 15 - margin.left);

    // X Axis Distance.
    chart.append("g")
      .attr("class", "x axis distance " + chart_css_id)
      .attr("transform", "translate(-1," + height + ")")
      .style("opacity", "1")
      .call(x_distance_axis[this.props.base_activity_id]);

    // X Axis Duration.
    chart.append("g")
      .attr("class", "x axis duration " + chart_css_id)
      .attr("transform", "translate(-1," + height + ")")
      .style("opacity", "0")
      .call(x_duration_axis[this.props.base_activity_id]);

    if (this.props.chart_type == 'speed') {

      var formatSeconds = function(d) { return xhale.helper.format_time(d, false, false); };

      y_speed_axis[this.props.activity_id] = axisLeft()
        .scale(y_scale.speed[this.props.base_activity_id])
        .ticks(5)

      if (this.props.metric == 'pace_seconds_per_mile' || this.props.metric == 'pace_seconds_per_km' || this.props.metric == 'pace_seconds_per_hectometer') {
        y_speed_axis[this.props.activity_id].tickFormat(formatSeconds);
      }

    }

    // Y Axis.
    chart.append("g")
      .attr("class", "y axis " + chart_css_id)
      .attr("transform", "translate(-1,0)")
      .call(this.y_axis);

    // Set up a rectangle for detecting when the mouse moves over the charts.
    chart_container.append("rect")
      .attr("width", chart_width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .attr("y", 0)
      .attr("class", "highlight-receptor")
      .style("fill", "none")
      .style("pointer-events", "all")
      .call(
        drag()
          .on('start', function dragStart(event) {

            // Mark that highlighting mode has started so that it is recognised
            // during other events.
            dragging = true;

            // Determine the current mouse position and the trackpoint under
            // the pointer.
            var scale = (self.props.x_scale_mode == 'distance') ? x_distance_scale[self.props.activity_id] : x_duration_scale[self.props.activity_id];
            var metric = (self.props.x_scale_mode == 'distance') ? 'distance_meters' : 'cumulative_duration_seconds';
            var mouse_position = event.x - margin.left;
            var trackpoint = getCurrentTrackpoint(mouse_position, activities_section.state.reduced_trackpoints[self.props.activity_id], scale, metric);

            // Record the start mouse position. This is used at the end of the
            // drag behaviour to determine how far the mouse has moved. This
            // allows us to treat very minor drag movements as a click.
            activities_section.refs['activity_section_' + self.props.base_activity_id].state.highlighted_trackpoints.start_mouse_position = mouse_position;

            // Record the start position of the current new highlight.
            // The anchor is used to distingish between the first and last
            // trackpoints in the highlighted section since these can switch
            // when the user moves the right side of the trackpoint over
            // the left side (and vice versa). So the anchor point is the point
            // that is stationary, while the other end of the hightlight follows
            // the mouse.
            activities_section.refs['activity_section_' + self.props.base_activity_id].state.highlighted_trackpoints.drag_anchor_reduced_trackpoint = trackpoint;

          })
          .on('end', function dragEnd(event) {

            // Mark that highlighting mode has ended.
            dragging = false;

            if (activities_section.refs['activity_section_' + self.props.base_activity_id].state.highlighted_trackpoints.first && activities_section.refs['activity_section_' + self.props.base_activity_id].state.highlighted_trackpoints.last) {

              // Determine the current mouse position and the trackpoint under
              // the pointer.
              var scale = (self.props.x_scale_mode == 'distance') ? x_distance_scale[self.props.activity_id] : x_duration_scale[self.props.activity_id];
              var metric = (self.props.x_scale_mode == 'distance') ? 'distance_meters' : 'cumulative_duration_seconds';
              var end_mouse_position = event.x - margin.left;
              var end_trackpoint = getCurrentTrackpoint(end_mouse_position, activities_section.state.reduced_trackpoints[self.props.activity_id], scale, metric);
              var end_reduced_trackpoint_index = end_trackpoint.reduced_array_index;

              // Remove the current highlight if the mouse has not moved more
              // than three pixels during the drag behaviour and this did not
              // occur within the bounds of the previous highlight.
              var mouse_position_change = Math.abs(activities_section.refs['activity_section_' + self.props.base_activity_id].state.highlighted_trackpoints.start_mouse_position - end_mouse_position)
              if (mouse_position_change < 3) {

                // Get the reduced array index of the first and last highlighted trackpoints.
                if (activities_section.refs['activity_section_' + self.props.base_activity_id].state.highlighted_trackpoints.first.reduced_array_index < activities_section.refs['activity_section_' + self.props.base_activity_id].state.highlighted_trackpoints.last.reduced_array_index) {
                  var first_reduced_trackpoint_index = activities_section.refs['activity_section_' + self.props.base_activity_id].state.highlighted_trackpoints.first.reduced_array_index;
                  var last_reduced_trackpoint_index = activities_section.refs['activity_section_' + self.props.base_activity_id].state.highlighted_trackpoints.last.reduced_array_index;
                }
                else {
                  var first_reduced_trackpoint_index = activities_section.refs['activity_section_' + self.props.base_activity_id].state.highlighted_trackpoints.last.reduced_array_index;
                  var last_reduced_trackpoint_index = activities_section.refs['activity_section_' + self.props.base_activity_id].state.highlighted_trackpoints.first.reduced_array_index;
                }

                if (end_reduced_trackpoint_index < first_reduced_trackpoint_index || end_reduced_trackpoint_index  > last_reduced_trackpoint_index) {

                  // Remove highlight.
                  activities_section.refs['activity_section_' + self.props.base_activity_id].state.highlighted_trackpoints = { 'first': null, 'last': null };
                  activities_section.refs['activity_section_' + self.props.base_activity_id].updateHighlightedLap(self.props.activity_id);

                }
              }
            }

          })
          .on("drag", function (event) {
            self.handleMouseMove(event);
          })
      )
      .on("touchmove mousemove", function (event) {
        self.handleMouseMove(event);
      })

    if (this.props.chart_type == 'power') {
      this.addPowerLineToChart(chart_container, chart, y_axis_label);
    }

    if (this.props.discipline == 'running' && this.props.chart_type == 'speed') {
      this.addPaceLineToChart(chart_container, chart, y_axis_label);
    }

  }

  addPaceLineToChart = (chart_container, chart, y_axis_label) => {

    let self = this;

    const pointer_starting_x_point = 5;

    // Cycle through each pace.
    for (let i = 0; i < this.props.target_run_paces.length; i++) {

      const pace_km = xhale.helper.meters_per_second_as_seconds_per_km(this.props.target_run_paces[i].meters_per_second);
      const pace_miles = xhale.helper.meters_per_second_as_seconds_per_mile(this.props.target_run_paces[i].meters_per_second);
      const pace = (this.props.distance_unit == 'km') ? pace_km : pace_miles;

      // The actual pace line.
      chart.append("line")
        .attr("x1", 0)
        .attr("x2", chart_width)
        .attr("shape-rendering", 'crispEdges')
        .attr("stroke-dasharray", '2')
        .attr("stroke-width", '2')
        .style('display', 'none')
        .attr("class", 'data-line pace-line pace-line-' + this.props.target_run_paces[i].id);

      // Label.
      // ...a white background surrounding the following line
      // to make it standout more clearly.
      chart.append("line")
        .attr("x1", pointer_starting_x_point)
        .attr("x2", pointer_starting_x_point)
        .attr("stroke-width", '5')
        .attr("stroke", 'rgba(255, 255, 255, 0.7)')
        .attr("class", 'pace-label-pointer-background pace-line-' + this.props.target_run_paces[i].id)
        .style('display', 'none')
        .attr("shape-rendering", 'crispEdges');

      // ...a line pointing to the label.
      chart.append("line")
        .attr("x1", pointer_starting_x_point)
        .attr("x2", pointer_starting_x_point)
        .attr("fill", 'none')
        .attr("stroke-width", '2')
        .attr("stroke-dasharray", '2')
        .attr("shape-rendering", 'crispEdges')
        .attr("class", 'data-line pace-label-pointer pace-line-' + this.props.target_run_paces[i].id)
        .attr("pace", this.props.target_run_paces[i].meters_per_second)
        .style('display', 'none');

      const text_km = this.props.target_run_paces[i].description + ' - ' + formatSecondsAsPace(pace_km) + ' min/km';
      const text_mile = this.props.target_run_paces[i].description + ' - ' + formatSecondsAsPace(pace_miles) + ' min/mi';

      // Speed label and label background.
      chart.append("rect")
        .attr("x", pointer_starting_x_point + 2)
        .attr("y", 0)
        .attr("height", 22)
        .attr("fill", 'white')
        .attr("class", 'pace-label-background pace-line-' + this.props.target_run_paces[i].id)
        .style('display', 'none')
        .style("opacity", "0.8");

      chart.append("text")
        .attr("class", 'pace-label pace-label-km pace-line-' + this.props.target_run_paces[i].id)
        .style("font-size", "11px")
        .style("font-weight", "normal")
        .style('display', 'none')
        .style('text-anchor', 'start')
        .text(text_km)
        .attr("dx", pointer_starting_x_point + 5)
        .attr("dy", 15);

      chart.append("text")
        .attr("class", 'pace-label pace-label-miles pace-line-' + this.props.target_run_paces[i].id)
        .style("font-size", "11px")
        .style("font-weight", "normal")
        .style('display', 'none')
        .style('text-anchor', 'start')
        .text(text_mile)
        .attr("dx", pointer_starting_x_point + 5)
        .attr("dy", 15);

    }

    // Show an extra button on running data so that we can see a popup
    // with extra options for this chart.
    const extra_button = chart_container.append("g")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    extra_button.append("rect")
      .attr("x", -51)
      .attr("y", -4)
      .attr("width", 16)
      .attr("height", 16)
      .attr("fill", '#eee')

    extra_button.append("text")
      .style("font-size", "15px")
      .style("text-anchor", "middle")
      .text("...")
      .attr("dx", -43)
      .attr("dy", 5);

    extra_button.append("rect")
      .attr("class", "show-extra-button show-extra-paces-popup")
      .attr("x", -51)
      .attr("y", -4)
      .attr("width", 16)
      .attr("height", 16)
      .attr("fill", 'transparent')
      .on("click", function () {
        const offset = $(this)[0].getBoundingClientRect().top;
        activities_section.refs['activity_section_' + self.props.base_activity_id].setState({speed_configuration_popup_visible: true});
        if (!mobile_mode) {
          const height_of_popup = $('.chart-speed-configuration-popup-inner').height();
          $('.chart-speed-configuration-popup-inner').css('top', offset - height_of_popup - 17);
        }
      });

      y_axis_label.attr("dx", -15 - (height / 2));

  }

  addPowerLineToChart = (chart_container, chart, y_axis_label) => {

    let self = this;

    // Show an extra button on power data so that we can see a popup
    // with extra options for this chart.

    const pointer_starting_x_point = 5;

    // Cycle through each power line.
    for (let i = 0; i < this.props.target_bike_powers.length; i++) {

      // The actual power line.
      chart.append("line")
        .attr("x1", 0)
        .attr("x2", chart_width)
        .attr("y1", y_scale.power[this.props.base_activity_id](this.props.target_bike_powers[i].watts))
        .attr("y2", y_scale.power[this.props.base_activity_id](this.props.target_bike_powers[i].watts))
        .attr("shape-rendering", 'crispEdges')
        .attr("stroke-dasharray", '2')
        .attr("stroke-width", '2')
        .style('display', 'none')
        .attr("class", 'data-line power-line power-line-' + this.props.target_bike_powers[i].id);

      // Label.
      // ...a white background surrounding the following line
      // to make it standout more clearly.
      chart.append("line")
        .attr("x1", pointer_starting_x_point)
        .attr("x2", pointer_starting_x_point)
        .attr("y1", 10)
        .attr("y2", y_scale.power[this.props.base_activity_id](this.props.target_bike_powers[i].watts) - 1)
        .attr("stroke-width", '5')
        .attr("stroke", 'rgba(255, 255, 255, 0.7)')
        .attr("class", 'power-label-pointer-background power-line-' + this.props.target_bike_powers[i].id)
        .style('display', 'none')
        .attr("shape-rendering", 'crispEdges');

      // ...a line pointing to the label.
      chart.append("line")
        .attr("x1", pointer_starting_x_point)
        .attr("x2", pointer_starting_x_point)
        .attr("y1", 10)
        .attr("y2", y_scale.power[this.props.base_activity_id](this.props.target_bike_powers[i].watts))
        .attr("fill", 'none')
        .attr("stroke-width", '2')
        .attr("stroke-dasharray", '2')
        .attr("shape-rendering", 'crispEdges')
        .attr("class", 'data-line power-label-pointer power-line-' + this.props.target_bike_powers[i].id)
        .style('display', 'none');

      const text = this.props.target_bike_powers[i].description + ' - ' + this.props.target_bike_powers[i].watts + ' watts';

      // Speed label and label background.
      chart.append("rect")
        .attr("x", pointer_starting_x_point + 2)
        .attr("y", 0)
        .attr("height", 22)
        .attr("fill", 'white')
        .attr("class", 'power-label-background power-line-' + this.props.target_bike_powers[i].id)
        .style('display', 'none')
        .style("opacity", "0.8");

      chart.append("text")
        .attr("class", 'power-label power-label-km power-line-' + this.props.target_bike_powers[i].id)
        .style("font-size", "11px")
        .style("font-weight", "normal")
        .style('display', 'none')
        .style('text-anchor', 'start')
        .text(text)
        .attr("dx", pointer_starting_x_point + 5)
        .attr("dy", 15);
    }

    const extra_button = chart_container.append("g")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    extra_button.append("rect")
      .attr("x", -51)
      .attr("y", -4)
      .attr("width", 16)
      .attr("height", 16)
      .attr("fill", '#eee');

    extra_button.append("text")
      .style("font-size", "15px")
      .style("text-anchor", "middle")
      .text("...")
      .attr("dx", -43)
      .attr("dy", 5);

    extra_button.append("rect")
      .attr("class", "show-extra-button show-extra-power-popup")
      .attr("x", -51)
      .attr("y", -4)
      .attr("width", 16)
      .attr("height", 16)
      .attr("fill", 'transparent')
      .on("click", function () {
        const offset = $(this)[0].getBoundingClientRect().top;
        activities_section.refs['activity_section_' + self.props.base_activity_id].setState({power_configuration_popup_visible: true});
        if (!mobile_mode) {
          const height_of_popup = $('.chart-power-configuration-popup-inner').height();
          $('.chart-power-configuration-popup-inner').css('top', offset - height_of_popup - 17);
        }
      });

    y_axis_label.attr("dx", -9 - (height / 2))

  }

  getCompareSessionsCurrentTrackpoints = (event, mouse_x_position) => {

    var chart_css_id = this.props.chart_type.replace('_', '-');
    var scale = (this.props.x_scale_mode == 'distance') ? x_distance_axis[this.props.base_activity_id].scale() : x_duration_axis[this.props.base_activity_id].scale();
    var segment_groups_data = selectAll('#activity-' + this.props.activity_id + " ." + chart_css_id + "-chart .chart .segment-group").data();
    var cumulative_metric = (this.props.x_scale_mode == 'distance') ? 'cumulative_segment_distance' : 'cumulative_segment_seconds';
    var max_metric = (this.props.x_scale_mode == 'distance') ? 'max_distance' : 'max_seconds';
    var metric = (this.props.x_scale_mode == 'distance') ? 'segment_distance_meters' : 'segment_duration_seconds';

    // This is the distance or duration along the entire scale that the cursor
    // is currently hovering over.
    var cumulative_segment_value_at_mouse_position = scale.invert(mouse_x_position);
    var scale_domain = scale.domain();

    // Cycle through each segment to find the correct segment based on the
    // cumulative segment distance or duration.
    if (cumulative_segment_value_at_mouse_position < scale_domain[0]) {
      var segment_key_under_mouse = segment_groups_data[0].segments_data.key;
      var segment_group_offset = scale(segment_groups_data[0].segments_data[cumulative_metric]);
    }
    else if (cumulative_segment_value_at_mouse_position > scale_domain[1]) {
      var segment_key_under_mouse = segment_groups_data[segment_groups_data.length - 1].segments_data.key;
      var segment_group_offset = scale(segment_groups_data[segment_groups_data.length - 1].segments_data[cumulative_metric]);
    }
    else {
      for (var i = 0; i < segment_groups_data.length; i++) {
        var smallest_value = segment_groups_data[i].segments_data[cumulative_metric];
        var largest_value = smallest_value + segment_groups_data[i].segments_data[max_metric];
        if (cumulative_segment_value_at_mouse_position >= smallest_value && cumulative_segment_value_at_mouse_position <= largest_value) {
          var segment_key_under_mouse = segment_groups_data[i].segments_data.key;
          var segment_group_offset = scale(segment_groups_data[i].segments_data[cumulative_metric]);
          break;
        }
      }
    }

    // Calculate the mouse position relative to the segment group.
    var segment_group = select('#activity-' + this.props.base_activity_id + " ." + chart_css_id + "-chart .chart .segment-group-" + segment_key_under_mouse);
    var mouse_x_position_in_segment = event.x - segment_group.node().getBoundingClientRect().left;

    // Grab the laps over this specific group.
    var reduced_trackpoints_by_activity = selectAll('#activity-' + this.props.activity_id + " ." + chart_css_id + "-chart .chart .segment-group-" + segment_key_under_mouse + " .segment-line").data();

    // Get the current trackpoint for each activity.
    var current_trackpoints = {};
    for (var i = 0; i < reduced_trackpoints_by_activity.length; i++) {
      var activity_id = reduced_trackpoints_by_activity[i][0].activity_id;
      current_trackpoints[activity_id] = getCurrentTrackpoint(mouse_x_position_in_segment, reduced_trackpoints_by_activity[i], scale, metric, true);
    }

    // Look for the activity with a trackpoint closest to our cursor and position
    // the marker over that one.
    var closest_trackpoint_activity_id;
    var closest_relative_x_position;
    Object.keys(current_trackpoints).forEach((activity_id) => {
      let trackpoint = current_trackpoints[activity_id];
      if (trackpoint) {
        var relative_position = Math.abs(trackpoint.x_scale_position - mouse_x_position_in_segment);
        if (!closest_relative_x_position || relative_position < closest_relative_x_position) {
          closest_trackpoint_activity_id = activity_id;
          closest_relative_x_position = relative_position;
        }
      }
    });
    
    return {
      segment_group_offset: segment_group_offset,
      activity_with_trackpoint_closest_to_cursor: closest_trackpoint_activity_id,
      trackpoints: current_trackpoints
    };

  }

  /*
   * Handle behaviour when the pointer is moved over the charts.
   */
  handleMouseMove = (event) => {

    var mouse_x_position = event.x - margin.left;

    var self = this;

    if (xhale_meta_data.has_premium_features && (xhale.helper.has_beta_access('compare_sessions') || xhale.helper.has_beta_access('coach_has_compare_sessions')) && this.props.chart_display_mode == 'compare_activities') {

      var current_trackpoints_by_activity = this.getCompareSessionsCurrentTrackpoints(event, mouse_x_position);

      if (current_trackpoints_by_activity.activity_with_trackpoint_closest_to_cursor) {
        global_x_scale_position = current_trackpoints_by_activity.trackpoints[current_trackpoints_by_activity.activity_with_trackpoint_closest_to_cursor].x_scale_position + margin.left + current_trackpoints_by_activity.segment_group_offset;
        $('.marker-line').show();
        activities_section.refs['activity_section_' + this.props.base_activity_id].setState({compare_trackpoints: current_trackpoints_by_activity.trackpoints});
      }

      // Pinpoint markers on the map.
      if (this.props.map_is_visible) {

        Object.keys(current_trackpoints_by_activity.trackpoints).forEach((activity_id) => {

          let trackpoint = current_trackpoints_by_activity.trackpoints[activity_id];
          if (trackpoint && trackpoint.latitude && trackpoint.longitude) {
            xhale.global_state.current_location_markers[self.props.activity_id][activity_id].setLatLng([trackpoint.latitude, trackpoint.longitude]);
          }

        });

      }

    }
    else if (xhale_meta_data.has_premium_features && activities_section.refs['activity_section_' + this.props.base_activity_id].overlaid) {

      // In overlay mode.

      // Collect the current trackpoints for each lap.
      var chart_css_id = this.props.chart_type.replace('_', '-');
      var reduced_trackpoints_by_lap = selectAll('#activity-' + this.props.activity_id + " ." + chart_css_id + "-chart .chart .chart-group .compare-lap-line").data();
      var scale = (this.props.x_scale_mode == 'distance') ? x_distance_axis[this.props.base_activity_id].scale() : x_duration_axis[this.props.base_activity_id].scale();
      var current_trackpoints = {};
      for (var i = 0; i < reduced_trackpoints_by_lap.length; i++) {
        var lap_id = reduced_trackpoints_by_lap[i][0].lap_id;
        var metric = (this.props.x_scale_mode == 'distance') ? 'lap_distance_meters' : 'lap_duration_seconds';
        current_trackpoints[lap_id] = getCurrentTrackpoint(mouse_x_position, reduced_trackpoints_by_lap[i], scale, metric, true);
      }

      // Look for the lap with a trackpoint closest to our cursor and position
      // the marker over that one.
      var closest_trackpoint_lap_id;
      var closest_relative_x_position;
      Object.keys(current_trackpoints).forEach((lap_id) => {
        let trackpoint = current_trackpoints[lap_id];
        if (trackpoint) {
          var relative_position = Math.abs(trackpoint.x_scale_position - mouse_x_position);
          if (!closest_relative_x_position || relative_position < closest_relative_x_position) {
            closest_trackpoint_lap_id = lap_id;
            closest_relative_x_position = relative_position;
          }
        }
      });

      if (closest_trackpoint_lap_id) {
        global_x_scale_position = current_trackpoints[closest_trackpoint_lap_id].x_scale_position + margin.left;
        $('.marker-line').show();
        activities_section.refs['activity_section_' + this.props.base_activity_id].setState({compare_trackpoints: current_trackpoints});
      }

      // Pinpoint markers on the map.
      if (this.props.map_is_visible) {

        Object.keys(current_trackpoints).forEach((lap_id) => {
          let trackpoint = current_trackpoints[lap_id];
          if (trackpoint && trackpoint.latitude && trackpoint.longitude) {
            xhale.global_state.current_location_markers[self.props.activity_id][lap_id].setLatLng([trackpoint.latitude, trackpoint.longitude]);
          }
        });

      }

    }
    else {

      // In normal mode.
      var chart_css_id = this.props.chart_type.replace('_', '-');
      var scale = (this.props.x_scale_mode == 'distance') ? x_distance_scale[this.props.activity_id] : x_duration_scale[this.props.activity_id];
      var metric = (this.props.x_scale_mode == 'distance') ? 'distance_meters' : 'cumulative_duration_seconds';
      var reduced_trackpoints = select('#activity-' + this.props.base_activity_id + " ." + chart_css_id + "-chart .chart .chart-group .trackpoint-line").data()[0];
      var trackpoint = getCurrentTrackpoint(mouse_x_position, reduced_trackpoints, scale, metric);

      var compare_trackpoints = {};
      compare_trackpoints[this.props.base_activity_id] = trackpoint;

      activities_section.refs['activity_section_' + this.props.base_activity_id].setState({compare_trackpoints: compare_trackpoints});

      // Set the vertical position line.
      global_x_scale_position = trackpoint.x_scale_position + margin.left;

      // If the drag behaviour is on, record the index for the last trackpoint in
      // the highlighted section.
      if (dragging && xhale_meta_data.has_premium_features) {
        $('.marker-line').hide();
        activities_section.refs['activity_section_' + this.props.base_activity_id].state.highlighted_trackpoints.first = activities_section.refs['activity_section_' + this.props.base_activity_id].state.highlighted_trackpoints.drag_anchor_reduced_trackpoint;
        activities_section.refs['activity_section_' + this.props.base_activity_id].state.highlighted_trackpoints.last = trackpoint;
        activities_section.refs['activity_section_' + this.props.base_activity_id].updateHighlightedLap(this.props.activity_id);
        activities_section.refs['activity_section_' + this.props.base_activity_id].setState({active_summary_tab: 'highlighted'});
      }
      else {
        $('.marker-line').show();
      }

      // Pinpoint marker on the map.
      if (this.props.map_is_visible && trackpoint.latitude && trackpoint.longitude && typeof(xhale.global_state.current_location_markers[this.props.base_activity_id].primary) != 'undefined') {
        xhale.global_state.current_location_markers[this.props.base_activity_id].primary.setLatLng([trackpoint.latitude, trackpoint.longitude]);
      }

    }
  }

  /*
   * Set the chart so the laps in activities are stacked on top of one another.
   */
  updateCompareActivitiesChartDisplay = () => {

    var self = this;
    var chart_css_id = this.props.chart_type.replace('_', '-');

    /*
     * Clone the trackpoints.
     *
     * We need to clone the trackpoints otherwise when we have overlapping
     * segments the lap distances interfere with one another.
     */
    function cloneTrackpoints(trackpoints) {

     var cloned_segment_trackpoints = []
      for (var i = 0; i < trackpoints.length; i++) {
        cloned_segment_trackpoints.push($.extend(false, {}, trackpoints[i]));
      }

      // Cycle through the trackpoints and calculate distance and duration relative to
      // the start of the segment.
      for (var trackpoints_index = 0; trackpoints_index < cloned_segment_trackpoints.length; trackpoints_index++) {
        cloned_segment_trackpoints[trackpoints_index].segment_distance_meters = cloned_segment_trackpoints[trackpoints_index].distance_meters - cloned_segment_trackpoints[0].distance_meters;
        cloned_segment_trackpoints[trackpoints_index].segment_duration_seconds = cloned_segment_trackpoints[trackpoints_index].cumulative_duration_seconds - cloned_segment_trackpoints[0].cumulative_duration_seconds;
      }

      return cloned_segment_trackpoints;

    }

    function get_max_seconds(trackpoint_groups) {
      var max_seconds = 0;
      for (var i = 0; i < trackpoint_groups.length; i++) {

        if (trackpoint_groups[i].length > 1) {
          var last_index = trackpoint_groups[i].length - 1;
          var seconds = trackpoint_groups[i][last_index].cumulative_duration_seconds - trackpoint_groups[i][0].cumulative_duration_seconds;
          if (seconds > max_seconds) {
            max_seconds = seconds;
          }
        }

      }
      return max_seconds;
    }

    function get_max_distance(trackpoint_groups) {
      var max_distance = 0;
      for (var i = 0; i < trackpoint_groups.length; i++) {

        if (trackpoint_groups[i].length > 1) {
          var last_index = trackpoint_groups[i].length - 1;
          var distance = trackpoint_groups[i][last_index].distance_meters - trackpoint_groups[i][0].distance_meters;
          if (distance > max_distance) {
            max_distance = distance;
          }
        }

      }
      return max_distance;
    }

    // Gather the laps we need to display on the chart.
    var lap_set = this.props.current_lapset;
    var laps_to_display_on_charts = [];
    Object.keys(this.props.laps).forEach((lap_id) => {
      let lap = this.props.laps[lap_id];
      if (lap.lapsets.indexOf(this.props.current_lapset.id) > -1) {
        laps_to_display_on_charts.push(lap_id);
      }
    });

    // Go through the laps in the set and organise them by activity.
    var laps_by_activity = {};
    for (var i = 0; i < laps_to_display_on_charts.length; i++) {
      var lap_id = laps_to_display_on_charts[i];
      var lap = this.props.laps[lap_id];

      // Don't add the lap if that activity is not currently set to display.
      if (lap.activity_id != this.props.activity_id && $.inArray(lap.activity_id, self.props.visible_lapset_activities) == -1) {
        continue;
      }

      if (!laps_by_activity[lap.activity_id]) {
        laps_by_activity[lap.activity_id] = [];
      }

      laps_by_activity[lap.activity_id].push(lap);
    }

    // Sort laps in order.
    Object.keys(laps_by_activity).forEach((activity_id) => {
      let laps = laps_by_activity[activity_id];
      laps.sort(function (a, b) {
        return a.first_trackpoint_id - b.first_trackpoint_id;
      });
    });

    // Collect the data into groups of lap trackpoints from each activity.
    var lap_groups = [];
    var activities_with_laps_remaining = Object.keys(laps_by_activity);
    var current_index = 0;
    while (activities_with_laps_remaining.length > 0) {

      var lap_group = {};

      Object.keys(laps_by_activity).forEach((activity_id) => {

        let laps = laps_by_activity[activity_id];

        // TODO Remember to check why the start time is odd on the laps table header.

        if (laps[current_index]) {
          var lap = laps[current_index];
          var first_trackpoint_index = self.props.activity_data[lap.activity_id].trackpoint_id_lookup[lap.first_trackpoint_id];
          var last_trackpoint_index = self.props.activity_data[lap.activity_id].trackpoint_id_lookup[lap.last_trackpoint_id];
          var first_trackpoint = self.props.activity_data[lap.activity_id].trackpoints[first_trackpoint_index];
          var last_trackpoint = self.props.activity_data[lap.activity_id].trackpoints[last_trackpoint_index];
          var original_trackpoints = activities_section.state.reduced_trackpoints[lap.activity_id].slice(parseInt(first_trackpoint.reduced_array_index), parseInt(last_trackpoint.reduced_array_index) + 1);
          lap_group[activity_id] = {
            lap_id: lap.id,
            trackpoints: original_trackpoints,
          };
        }
        else {
          // All the segments have been accounted for from this activity so
          // remove this activity from the list of activities with laps
          // remaining.
          var index = activities_with_laps_remaining.indexOf(activity_id);
          if (index >= 0) {
            activities_with_laps_remaining.splice(index, 1);
          }
        }

      });

      if (Object.keys(lap_group).length > 0) {
        lap_groups.push(lap_group);
      }

      current_index++;
    }

    // Fill in the gaps between laps so that we can see the entire activity if
    // needed.
    var segments = [];
    var segments_data = [];
    var segment_key = 0;

    if (lap_groups.length > 0) {

      for (var lap_index = 0; lap_index < lap_groups.length; lap_index++) {

        var before_lap_trackpoints = [];
        var current_lap_trackpoints = [];
        var after_lap_trackpoints = [];
        Object.keys(lap_groups[lap_index]).forEach((activity_id) => {

          let lap = lap_groups[lap_index][activity_id];

          var first_trackpoint_of_this_lap = lap.trackpoints[0];
          if (lap_index == 0) {
            // This is the first lap. If the lap doesn't begin at the start of
            // the activity, make the start of the activity the first segment.
            if (first_trackpoint_of_this_lap.unfiltered_array_index > 0) {
              var original_trackpoints = activities_section.state.reduced_trackpoints[first_trackpoint_of_this_lap.activity_id].slice(0, parseInt(first_trackpoint_of_this_lap.reduced_array_index) + 1);
              before_lap_trackpoints.push(cloneTrackpoints(original_trackpoints));
            }
          }
          else {
            // This is a subsequent lap so fill any gap between this and the
            // previous lap.
            var trackpoints_from_previous_lap = lap_groups[lap_index - 1][activity_id].trackpoints;
            var last_trackpoint_from_previous_lap = trackpoints_from_previous_lap[trackpoints_from_previous_lap.length - 1];
            var original_trackpoints = activities_section.state.reduced_trackpoints[activity_id].slice(last_trackpoint_from_previous_lap.reduced_array_index, parseInt(first_trackpoint_of_this_lap.reduced_array_index) + 1);
            before_lap_trackpoints.push(cloneTrackpoints(original_trackpoints));
          }

          // Add the lap data itself.

          // In the first lap, store the lap id. This is handy for finding this
          // data again later.
          var cloned_trackpoints = cloneTrackpoints(lap.trackpoints);
          cloned_trackpoints[0].lap_id = lap.lap_id;
          current_lap_trackpoints.push(cloned_trackpoints);

          if (lap_index == lap_groups.length - 1) {

            // This is the last lap. If the lap doesn't end when the activity
            // does add the remaining data as the last segment.

            var last_trackpoint_of_this_lap = lap.trackpoints[lap.trackpoints.length - 1];
            var last_trackpoint_of_this_activity_index = this.props.activity_data[activity_id].trackpoints.length - 1;
            var last_trackpoint_of_this_activity = this.props.activity_data[activity_id].trackpoints[last_trackpoint_of_this_activity_index];
            if (last_trackpoint_of_this_lap.reduced_array_index != last_trackpoint_of_this_activity.reduced_array_index) {
              var original_trackpoints = activities_section.state.reduced_trackpoints[activity_id].slice(last_trackpoint_of_this_lap.reduced_array_index, parseInt(last_trackpoint_of_this_activity.reduced_array_index) + 1);
              after_lap_trackpoints.push(cloneTrackpoints(original_trackpoints));
            }
          }

        });

        // Add any data between this lap and the previous one if there is any.
        if (before_lap_trackpoints.length > 0) {

          var max_distance = get_max_distance(before_lap_trackpoints);
          var max_seconds = get_max_seconds(before_lap_trackpoints);

          // Non-lap segments only show when that option is toggled. Also don't
          // try to show if the max distance would be zero as it will not be
          // visible.
          // TODO Support this for duration mode as well.
          if (max_distance > 0 && !this.props.display_compared_laps_only) {

           segments.push({
              trackpoints: before_lap_trackpoints,
              segments_data: {
                type: 'not-lap',
                key: segment_key,
                max_distance: max_distance,
                max_seconds: max_seconds,
                previous_segment_type: (segments.length > 0) ? segments[segments.length - 1].segments_data.type : 'none',
              }
            });

          }

          // Whether or not we are actually displaying this segment, increase
          // the key so that is always the same.
          segment_key++;

        }

        // Add the current lap as a segment.
        if (current_lap_trackpoints.length > 0) {

          segments.push({
            trackpoints: current_lap_trackpoints,
            segments_data: {
              type: 'lap',
              lap_number: lap_index + 1,
              key: segment_key,
              max_distance: get_max_distance(current_lap_trackpoints),
              max_seconds: get_max_seconds(current_lap_trackpoints),
              previous_segment_type: (segments.length > 0) ? segments[segments.length - 1].segments_data.type : 'none',
            }
          });

          segment_key++;

        }

        // If this is the last lap, we may also need to add the end of the
        // session as a segment.
        if (after_lap_trackpoints.length > 0) {

          var max_distance = get_max_distance(after_lap_trackpoints);
          var max_seconds = get_max_seconds(after_lap_trackpoints);

          // Non-lap segments only show when that option is toggled. Also don't
          // try to show if the max distance would be zero as it will not be
          // visible.
          // TODO Support this for duration mode as well.
          if (max_distance > 0 && !this.props.display_compared_laps_only) {
            segments.push({
              trackpoints: after_lap_trackpoints,
              segments_data: {
                type: 'not-lap',
                key: segment_key,
                max_distance: max_distance,
                max_seconds: max_seconds,
                previous_segment_type: segments[segments.length - 1].segments_data.type,
              }
            });

          }

          // Whether or not we are actually displaying this segment, increase
          // the key so that is always the same.
          segment_key++;

        }

      }

    }

    // Work out the domain for the distance scale. The minimum value will be zero
    // and the maximium will be the distance of
    // each segment, all added together. The distance of each segment comes
    // from the activity with the greatest distance in that segment.
    var total_distance = 0;
    for (var segment_index = 0; segment_index < segments.length; segment_index++) {
      segments[segment_index].segments_data.cumulative_segment_distance = segment_index > 0 ? segments[segment_index - 1].segments_data.cumulative_segment_distance + segments[segment_index - 1].segments_data.max_distance : 0;;
      total_distance += segments[segment_index].segments_data.max_distance;
    }
    var x_distance_scale = x_distance_axis[this.props.base_activity_id].scale();
    x_distance_scale.domain([0, total_distance])

    // Do the same for the duration scale.
    var total_seconds = 0;
    for (var segment_index = 0; segment_index < segments.length; segment_index++) {
      segments[segment_index].segments_data.cumulative_segment_seconds = segment_index > 0 ? segments[segment_index - 1].segments_data.cumulative_segment_seconds + segments[segment_index - 1].segments_data.max_seconds : 0;;
      total_seconds += segments[segment_index].segments_data.max_seconds;
    }
    var x_duration_scale = x_duration_axis[this.props.base_activity_id].scale();
    x_duration_scale.domain([0, total_seconds])

    // Redefine y scale so it fits all activities.
    if (this.props.chart_type == 'speed') {
      this.define_speed_y_scales();
    }
    else {
      this.define_y_scales();
    }

    var segment_line_path = line()
      .x(function (d) {
        var value = (self.props.x_scale_mode == 'duration') ? x_duration_scale(d.segment_duration_seconds) : x_distance_scale(d.segment_distance_meters);
        return value;
      })
      .y(function (d) {

        let value = (self.props.discipline == 'running' && self.props.metric == 'cadence_rpm') ? d.cadence_rpm * 2 : d[self.props.metric];
        if (!value) {
          return null;
        }
        else {
          return y_scale[self.props.chart_type][self.props.base_activity_id](value);
        }

      });

    var chart_container = select(ReactDOM.findDOMNode(this));
    var plot_area = chart_container.select('.plot-area');

    // Update segment group data.
    var existing_segment_groups = plot_area.selectAll(".segment-group")
      .data(segments, function (d, index) {
        // Create a unique key.
        var key = d.segments_data.type;
        if (d.segments_data.type == 'lap') {
          key += '-' + d.segments_data.lap_number;
        }
        key += '-' + d.segments_data.key;
        return key;
      })

    existing_segment_groups.exit().remove();

    var new_segment_groups = existing_segment_groups.enter()
      .append("g")
        .attr("class", function (d, i) { return "segment-group segment-group-" + d.segments_data.key})

    var all_segment_groups = new_segment_groups.merge(existing_segment_groups)

    var existing_segment_lines = all_segment_groups.selectAll(".segment-line")
      .data(
        (d) => {
          return d.trackpoints;
        },
        (d, index) => {
          return (d[0] && d[0].lap_id) ? d[0].lap_id : null;
        }
      );

    existing_segment_lines.exit().remove();

    var new_segment_lines = existing_segment_lines.enter()
      .append("path")
        .attr("class","segment-line data-line")
        .attr("clip-path", "url(#clip-" + this.props.activity_id + "-" + chart_css_id + ")")
        .style("fill","none")

    var all_segment_lines = new_segment_lines.merge(existing_segment_lines);

    all_segment_lines
      .attr("d", segment_line_path)
      .style("stroke", function (d) {

        // Set imported activities to a distinct colour unless specific laps
        // selected in which case make them the selected lap colour or grey
        // if that particular laps is not selected.
        if (self.props.selected_lap_ids.length > 0) {
          if (self.props.selected_lap_ids.indexOf(d[0].lap_id) > -1 && self.props.laps[d[0].lap_id].colour) {
            return self.props.laps[d[0].lap_id].colour;
          }
          else {
            return '#dfdfdf';
          }
        }

        if (d[0] && self.props.activity_id != d[0].activity_id) {
          return self.props.visible_lapset_activity_colours[d[0].activity_id];
        }

      })

    // Add lines to separate the segments.
    new_segment_groups.append("line")
      .attr("class", "segment-separator segment-separator-left")
      .style("stroke-width", '0.7px')
      .style("shape-rendering", 'geometricprecision')
      .attr("x1", 0)
      .attr("y1", 0)
      .attr("x2", 0)
      .attr("y2", 104);

    new_segment_groups.append("line")
      .attr("class", "segment-separator segment-separator-right")
      .style("stroke-width", '0.7px')
      .style("shape-rendering", 'geometricprecision')
      .attr("y1", 0)
      .attr("y2", 104);

    all_segment_groups.select('.segment-separator-left')
      .style("stroke", function (d, i) {
        if (d.segments_data.type == 'lap' && d.segments_data.previous_segment_type != 'lap') {
          return '#000000';
        }
        else {
          return 'none';
        }
      });

    all_segment_groups.select('.segment-separator-right')
      .style("stroke", function (d, i) {
        if (d.segments_data.type == 'lap') {
          return '#000000';
        }
        else {
          return 'none';
        }
      });


    new_segment_groups
      .append("text")
        .attr("class", "x-axis-lap-label")
        .text(function (d) { return d.segments_data.type == 'lap' ? 'lap ' + (d.segments_data.lap_number) : null})
        .style("font-size", "11px")
        .attr("dx", function (d) { return (self.props.x_scale_mode == 'duration') ? x_duration_scale(d.segments_data.max_seconds) / 2: x_distance_scale(d.segments_data.max_distance) / 2})
        .attr("dy", 105)
        .style("text-anchor", "middle")

    new_segment_groups.append("line")
      .attr("class", "segment-separator-base")
      .style("stroke-width", '0.7px')
      .style("shape-rendering", 'geometricprecision')
      .style("stroke", function (d) { return d.segments_data.type == 'lap' ? '#000000': '#bdbdbd'})
      .attr("x1", 0)
      .attr("y1", 93)
      .attr("y2", 93);

    all_segment_groups.select('.segment-separator-base')
      .attr("x2", function (d) { return (self.props.x_scale_mode == 'duration') ? x_duration_scale(d.segments_data.max_seconds) : x_distance_scale(d.segments_data.max_distance)});

    plot_area.selectAll('.segment-group')
      .attr("transform", function (d, index) {
        var offset = (self.props.x_scale_mode == 'duration') ? x_duration_scale(d.segments_data.cumulative_segment_seconds) : x_distance_scale(d.segments_data.cumulative_segment_distance);
        offset -= 0.5; // The 0.5 is to align it with the axis.
        return "translate(" + offset + ", 0.5)"
      })

    plot_area.selectAll('.segment-separator-right')
      .attr("x1", function (d) { return (self.props.x_scale_mode == 'duration') ? x_duration_scale(d.segments_data.max_seconds) : x_distance_scale(d.segments_data.max_distance)})
      .attr("x2", function (d) { return (self.props.x_scale_mode == 'duration') ? x_duration_scale(d.segments_data.max_seconds) : x_distance_scale(d.segments_data.max_distance)})

    plot_area.selectAll('.x-axis-lap-label')
      .attr("dx", function (d) { return (self.props.x_scale_mode == 'duration') ? x_duration_scale(d.segments_data.max_seconds) / 2: x_distance_scale(d.segments_data.max_distance) / 2})

    // Transition from other display modes.
    if (this.current_mode != 'lap_set_only') {

      // Hide the standard trackpoint lines and axis.
      plot_area.selectAll('.data-area').style('display', 'none');
      plot_area.selectAll(".trackpoint-line").style('display', 'none');
      plot_area.selectAll(".lapset-lap-line").style('display', 'none');
      chart_container.selectAll(".x.axis").style('display', 'none');

      // Display the segment groups again.
      chart_container.selectAll(".segment-group").style('display', 'block');

      this.current_mode = 'lap_set_only';
    }

    if (this.props.chart_display_mode == 'overlay_laps' || this.props.current_lapset.id == 'overall_activity') {
      chart_container.selectAll(".compare-lap-line").style('display', 'block');
    }
    else {
      chart_container.selectAll(".compare-lap-line").style('display', 'none');
    }

  }

  updateChart = () => {

    if (this.props.chart_type == 'speed') {

      this.define_speed_y_scales();

      x_distance_axis[this.props.base_activity_id].tickFormat(this.formatDistance);
      y_speed_axis[this.props.base_activity_id].scale(y_scale.speed[this.props.base_activity_id])

      selectAll("#activity-" + this.props.base_activity_id + " .charts-container .axis.x.distance").call(x_distance_axis[this.props.base_activity_id]);
      selectAll("#activity-" + this.props.base_activity_id + " .charts-container .axis.y.speed").call(y_speed_axis[this.props.base_activity_id]);

      if (this.props.metric == 'pace_seconds_per_km' || this.props.metric == 'speed_km_per_hour') {
        selectAll("#activity-" + this.props.base_activity_id + " .charts-container .y-axis-label.speed").text("Speed - " + y_speed_km_label_text[this.props.base_activity_id]);
      }
      else if (this.props.metric == 'pace_seconds_per_mile' || this.props.metric == 'speed_miles_per_hour') {
        selectAll("#activity-" + this.props.base_activity_id + " .charts-container .y-axis-label.speed").text("Speed - " + y_speed_miles_label_text[this.props.base_activity_id]);
      }
    }
    else {
      this.define_y_scales();
    }

    // Redraw the chart area in case of resize.
    var chart_container = select(ReactDOM.findDOMNode(this));

    var chart_css_id = this.props.chart_type.replace('_', '-');
    chart_container.select('svg')
      .attr("width", chart_width + margin.left + margin.right);

    chart_container.select("#clip-" + this.props.activity_id + "-" + chart_css_id + ' rect')
      .attr("width", chart_width)

    // Update the correct type of chart display
    if (this.props.chart_display_mode == 'normal') {
      this.updateNormalChartDisplay();
    }
    else if (this.props.chart_display_mode == 'overlay_laps') {
      this.updateOverlayLapsChartDisplay();
    }
    else if (this.props.chart_display_mode == 'compare_activities') {
      this.updateCompareActivitiesChartDisplay();
    }

  }

  updateOverlayLapsChartDisplay = () => {
    if (this.current_mode != 'overlay_laps') {

      var chart_container = select(ReactDOM.findDOMNode(this));
      chart_container.selectAll(".segment-group").style('display', 'none');
      chart_container.selectAll(".compare-lap-line").style('display', 'block');
      chart_container.selectAll(".x.axis").style('display', 'block');

      this.current_mode = 'overlay_laps';

    }
  }

  updateNormalChartDisplay = () => {

    var self = this;
    var chart_css_id = this.props.chart_type.replace('_', '-');
    var chart_container = select(ReactDOM.findDOMNode(this));
    var plot_area = chart_container.select('.plot-area');

    // Recalculate distance scale.
    x_distance_scale[this.props.activity_id] = scaleLinear()
      .range([0, chart_width])
      .domain([
        min(activities_section.state.reduced_trackpoints[this.props.activity_id], function (d) { return d.distance_meters; }),
        max(activities_section.state.reduced_trackpoints[this.props.activity_id], function (d) { return d.distance_meters; })
      ]);

    // Recalculate duration scale.
    var start_datetime = min(activities_section.state.reduced_trackpoints[this.props.activity_id], function (d) { if (d.datetime) {return d.datetime } });
    var last_datetime = max(activities_section.state.reduced_trackpoints[this.props.activity_id], function (d) { if (d.datetime) {return d.datetime } });
    var max_duration_milliseconds = last_datetime - start_datetime;
    var max_duration_seconds = max_duration_milliseconds / 1000;

    x_duration_scale[this.props.activity_id] = scaleLinear()
      .range([0, chart_width])
      .domain([0, max_duration_seconds]);

    // Define lines for distance and duration.
    var last_defined_value = 0; // Used in case of undefined data.
    var get_metric_value = function (d) {
      if (typeof d[self.props.metric] != 'undefined' && d[self.props.metric] != null) {
        var current_value = (self.props.discipline == 'running' && self.props.metric == 'cadence_rpm') ? d.cadence_rpm * 2 : d[self.props.metric];
        last_defined_value = current_value;
        return y_scale[self.props.chart_type][self.props.base_activity_id](current_value);
      }
      else {
        return y_scale[self.props.chart_type][self.props.base_activity_id](last_defined_value);
      }
    }

    distance_area[this.props.chart_type][this.props.activity_id] = area()
      .x(function(d) { return x_distance_scale[self.props.activity_id](d.distance_meters); })
      .y0(get_metric_value)
      .y1(height);

    distance_line[this.props.chart_type][this.props.activity_id] = line()
      .x(function(d) { return x_distance_scale[self.props.activity_id](d.distance_meters); })
      .y(get_metric_value);

    duration_area[this.props.chart_type][this.props.activity_id] = area()
      .x(function(d) { return x_duration_scale[self.props.activity_id](d.cumulative_duration_seconds); })
      .y0(get_metric_value)
      .y1(height);

    duration_line[this.props.chart_type][this.props.activity_id] = line()
      .x(function(d) { return x_duration_scale[self.props.activity_id](d.cumulative_duration_seconds); })
      .y(get_metric_value);

    // Redraw the axis.
    if (self.props.x_scale_mode == 'distance') {
      x_distance_axis[this.props.base_activity_id].scale(x_distance_scale[this.props.activity_id])
    }
    else if (self.props.x_scale_mode == 'duration') {

      x_duration_axis[this.props.base_activity_id] = axisBottom()
        .scale(x_duration_scale[this.props.activity_id])
        .tickValues(calculateDurationTickValues(x_duration_scale[this.props.activity_id]))
        .tickFormat(xhale.helper.format_time_seconds)

      chart_container.selectAll(".axis.x.duration").call(x_duration_axis[this.props.base_activity_id]);
    }

    if (this.props.chart_type == 'speed') {
      y_speed_axis[this.props.base_activity_id].scale(y_scale.speed[this.props.base_activity_id])
      chart_container.selectAll(".axis.y").call(y_speed_axis[this.props.base_activity_id]);
    }
    else {
      this.y_axis.scale(y_scale[self.props.chart_type][self.props.base_activity_id])
      chart_container.selectAll(".axis.y").call(this.y_axis);
    }

    // Redraw the main trackpoint line.
    var reduced_trackpoint_data = [];
    Object.keys(this.props.activity_data).forEach((activity_id) => {

      let activity_data = this.props.activity_data[activity_id];
      var activity_id = parseInt(activity_id);

      // Ensure the main activity is the first displayed.
      if (activity_id == self.props.activity_id) {
        reduced_trackpoint_data.unshift(activities_section.state.reduced_trackpoints[activity_id]);
      }

      // Only display the extra data if it is listed as being something to display.
      // TODO We have disabled showing other trackpoint lines for now but keeping
      // the code in place in case we want to add them back (perhaps in some greyed
      // out manner.)
      //else if ($.inArray(activity_id, self.props.visible_lapset_activities) > -1) {
      //  reduced_trackpoint_data.push(reduced_trackpoints[activity_id]);
      //}

    });

    var laps_in_set = [];
    if (this.props.current_lapset.id != 'overall_activity' && this.props.lapset_edit_mode.active == true) {
      Object.keys(this.props.laps).forEach((lap_id) => {
        let lap = this.props.laps[lap_id];
        if (lap.lapsets.indexOf(this.props.current_lapset.id) > -1 && lap.activity_id == this.props.activity_id) {
          laps_in_set.push(lap);
        }
      });
    }

    var lapset_lap_trackpoints = [];
    for (var index = 0; index < laps_in_set.length; index++) {
      var lap = laps_in_set[index];
      var first_trackpoint_index = this.props.activity_data[lap.activity_id].trackpoint_id_lookup[lap.first_trackpoint_id];
      var last_trackpoint_index = this.props.activity_data[lap.activity_id].trackpoint_id_lookup[lap.last_trackpoint_id];
      var first_trackpoint = this.props.activity_data[lap.activity_id].trackpoints[first_trackpoint_index];
      var last_trackpoint = this.props.activity_data[lap.activity_id].trackpoints[last_trackpoint_index];
      var original_trackpoints = activities_section.state.reduced_trackpoints[lap.activity_id].slice(parseInt(first_trackpoint.reduced_array_index), parseInt(last_trackpoint.reduced_array_index) + 1);

      // Clone the original trackpoints.
      // We need to clone the trackpoints otherwise when we have overlapping
      // segments the lap distances interfere with one another.
      var cloned_lap_trackpoints = []
      for (var i = 0; i < original_trackpoints.length; i++) {
        cloned_lap_trackpoints.push($.extend(false, {}, original_trackpoints[i]));
      }

      // Cycle through the trackpoints and calculate distance and duration relative to
      // the start of the lap. Also store the lap id for later reference.
      for (var trackpoints_index = 0; trackpoints_index < cloned_lap_trackpoints.length; trackpoints_index++) {
        cloned_lap_trackpoints[trackpoints_index].lap_distance_meters = cloned_lap_trackpoints[trackpoints_index].distance_meters - cloned_lap_trackpoints[0].distance_meters;
        cloned_lap_trackpoints[trackpoints_index].lap_duration_seconds = cloned_lap_trackpoints[trackpoints_index].cumulative_duration_seconds - cloned_lap_trackpoints[0].cumulative_duration_seconds;
        cloned_lap_trackpoints[trackpoints_index].lap_id = lap.id;
      }

      lapset_lap_trackpoints.push(cloned_lap_trackpoints);
    }

    if (this.props.x_scale_mode == 'distance') {

      var areas = plot_area.selectAll('.data-area')
        .data([reduced_trackpoint_data[0]]) // Only the first activity should highlight the area so we can see it.
        .attr("d", distance_area[this.props.chart_type][this.props.activity_id])

      var lines = plot_area.selectAll(".trackpoint-line")
        .data(reduced_trackpoint_data)
        .attr("d", distance_line[this.props.chart_type][this.props.activity_id])

      var lapset_lap_lines = plot_area.selectAll(".lapset-lap-line")
        .data(lapset_lap_trackpoints, function (d) { return d[0].lap_id} )
        .attr("d", distance_line[this.props.chart_type][this.props.activity_id])

    }
    else if (this.props.x_scale_mode == 'duration') {

      var areas = plot_area.selectAll('.data-area')
        .data([reduced_trackpoint_data[0]]) // Only the first activity should highlight the area so we can see it.
        .attr("d", duration_area[this.props.chart_type][this.props.activity_id])

      var lines = plot_area.selectAll(".trackpoint-line")
        .data(reduced_trackpoint_data)
        .attr("d", duration_line[this.props.chart_type][this.props.activity_id])

      var lapset_lap_lines = plot_area.selectAll(".lapset-lap-line")
        .data(lapset_lap_trackpoints, function (d) { return d[0].lap_id} )
        .attr("d", duration_line[this.props.chart_type][this.props.activity_id])

    }

    // Set up the plots and areas

    let distance_or_duration_line = (this.props.x_scale_mode == 'distance') ? distance_line[this.props.chart_type][this.props.activity_id] : duration_line[this.props.chart_type][this.props.activity_id];

    areas.enter().append("path")
      .attr("class", 'data-area')
      .attr("clip-path", "url(#clip-" + this.props.activity_id + "-" + chart_css_id + ")")

    lines.enter().append('path')
      .attr("fill", 'none')
      .attr("clip-path", "url(#clip-" + this.props.activity_id + "-" + chart_css_id + ")")
      .attr("d", distance_or_duration_line)
      .attr("class", 'data-line trackpoint-line');

    // Add highlighted lapset laps.
    var stroke_colour = (this.props.visible_lapset_activity_colours[this.props.activity_id]) ? this.props.visible_lapset_activity_colours[this.props.activity_id] : get_discipline_colour(this.props.activity_data[this.props.activity_id].discipline);
    lapset_lap_lines.enter().append("path")
      .attr("class", 'lapset-lap-line')
      .style("fill","none")
      .style('stroke', stroke_colour)
      .attr("d", distance_or_duration_line);

    // Exit.
    lapset_lap_lines.exit().remove();

    if (lapset_lap_trackpoints.length > 0) {
      lines.style('stroke', 'silver');
    }
    else if (this.props.visible_lapset_activity_colours[this.props.activity_id]) {
      lines.style('stroke', this.props.visible_lapset_activity_colours[this.props.activity_id]);
    }

    lines.exit().remove();

    // Rebuild the target power and pace lines
    if (this.props.chart_type == 'power') {
      rebuild_target_power(this.props.target_bike_powers);
    }

    if (this.props.chart_type == 'speed') {
      rebuild_target_run_paces(this.props.target_run_paces, this.props.distance_unit, this.props.activity_id);
    }

    // Transition from other display modes.
    if (!this.current_mode != 'full_session') {

      // Hide the standard trackpoint lines and axis.
      plot_area.selectAll('.data-area').style('display', 'block');
      plot_area.selectAll(".trackpoint-line").style('display', 'block');
      chart_container.selectAll(".compare-lap-line").style('display', 'block');
      chart_container.selectAll(".x.axis").style('display', 'block');
      chart_container.selectAll(".segment-group").style('display', 'none');

      this.current_mode = 'full_session';
    }

  }

  componentDidMount = () => {
    this.createChart();
    this.updateChart();
    window.addEventListener('resize', this.updateChart);
  }

  componentDidUpdate = () => {
    this.updateChart();
  }

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.updateChart);
  }

  render = () => {

    var chart_css_id = this.props.chart_type.replace('_', '-');

    return (
      <div className={chart_css_id + '-chart-container chart-container'}>
        <div className={chart_css_id + '-chart'} style={{height: '125px', width: '100%', clear: 'both'}}><svg style={{height: '100%'}} className="chart"></svg></div>
      </div>
    );
  }
}

class MapSection extends React.Component {

  /**
   * Collect together sets of coordinates (lines on the map).
   */
  collectCoordinates = (trackpoints) => {

    // Check if the data has loaded properly yet.
    if (typeof(activities_section.state.reduced_trackpoints[this.props.activity_id]) == 'undefined') {
      return [];
    }

    // Sets of coordinates are created to account for breaks in the tracking data.
    var coordinate_sets = [];

    var current_set_coordinates = [];
    var previous_latitude = null;
    var previous_longitude = null;
    for (var i = 0; i < trackpoints.length; i++) {

      if (trackpoints[i].latitude && trackpoints[i].longitude && (trackpoints[i].latitude != previous_latitude || trackpoints[i].longitude != previous_longitude)) {
        // If this trackpoint has trackpoints and they are different from the
        // last one, push it onto the stack. We don't want two two idential
        // coordinates in a row as this can break the the SVG line and wouldn't
        // be visible in any case.
        current_set_coordinates.push([trackpoints[i].latitude, trackpoints[i].longitude]);
        previous_latitude = trackpoints[i].latitude;
        previous_longitude = trackpoints[i].longitude;
      }
      else if (coordinate_sets.length > 0 && current_set_coordinates.length == 1) {
        // If this coordinate has just one point, add it to the previous set of
        // coordinates. A series of single points, otherwise won't show up at all.
        coordinate_sets[coordinate_sets.length - 1].push(current_set_coordinates[0]);
        current_set_coordinates = [];
      }
      else if (current_set_coordinates.length > 0) {
        // If this coordinate has more than one point, complete the previous
        // set of coordinates and start a new set.
        coordinate_sets.push(current_set_coordinates);
        current_set_coordinates = [];
      }

    }

    // Push the final set of coordinates onto the stack.
    if (current_set_coordinates.length > 0) {
      coordinate_sets.push(current_set_coordinates);
    }

    return coordinate_sets;

  }

  constructMap = () => {

    // If there is any data to add, construct the map.
    if (this.mapHasData()) {

      if (!this.map) {

        const map_container = 'shared-session-map-' + this.props.activity_id;

        // Set this up, ready for adding markers later.
        this.marker_z_index_offset = 0;

        this.active_polyline_group = L.featureGroup();

        this.map = L.map(map_container);
        this.map.attributionControl.options.prefix = '';

        const attribution = '<a href="https://www.mapbox.com/about/maps/" target="_blank">&copy; Mapbox &copy; OpenStreetMap</a> <a class="mapbox-improve-map" href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a>';

        L.tileLayer('https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}', {
          attribution: attribution,
          maxZoom: 18,
          //id: 'xhale/ck9bj3d8502yh1iplnn21w5hh', # this is the studio style which is actually just a copy of the outdoors style which we haven't yet customised.
          id: 'mapbox/outdoors-v11',
          tileSize: 512,
          zoomOffset: -1,
          accessToken: mapbox_access_token,
        }).addTo(this.map);

        // Add a placeholder for markers for each lap.
        xhale.global_state.current_location_markers[this.props.activity_id] = {};

        this.forceUpdate()
      }

    }


  }

  mapHasData = () => {

    var new_visible_activities = this.props.visible_lapset_activities.slice();
    new_visible_activities.push(this.props.activity_id);

    if (!this.existing_visible_activities || this.existing_visible_activities.length != new_visible_activities.length || this.reduced_trackpoints_not_loaded) {

      this.existing_visible_activities = new_visible_activities;
      this.has_data = false;
      this.reduced_trackpoints_not_loaded = false;

      for (let i = 0; i < new_visible_activities.length; i++) {

        if (activities_section.state.reduced_trackpoints[new_visible_activities[i]]) {
          var coordinate_sets = this.collectCoordinates(activities_section.state.reduced_trackpoints[new_visible_activities[i]]);
          if (coordinate_sets.length > 0) {
            this.has_data = true;
            return this.has_data;
          }
        }
        else {
          this.reduced_trackpoints_not_loaded = true;
        }

      }

    }

    return this.has_data;
  }

  componentDidMount = () => {
    if (!this.mapHasData() && this.props.map_is_visible) {
      activities_section.refs['activity_section_' + this.props.activity_id].setState({active_extras_tab: 'laps'});
    }
  }

  componentDidUpdate = () => {

    if (!this.mapHasData()) {
      return;
    }
    else {
      this.constructMap();
    }

    var self = this;
    var new_polyline_group = L.featureGroup()
    var grey_out_base_line = false;
    var line_colour = '#000000';
    var marker_colour = '#000000';

    Object.keys(this.props.laps).forEach((lap_id) => {
      let lap = this.props.laps[lap_id];
      if (lap.colour) {
        grey_out_base_line = true;
        return;
      }
    });

    // If any section is highlighted, show that on the map.
    if (typeof(this.props.highlighted_lap_data.first_trackpoint) == 'object' && typeof(this.props.highlighted_lap_data.last_trackpoint) == 'object') {
      var lap_trackpoints = activities_section.state.reduced_trackpoints[this.props.activity_id].slice(parseInt(this.props.highlighted_lap_data.first_trackpoint.reduced_array_index), parseInt(this.props.highlighted_lap_data.last_trackpoint.reduced_array_index) + 1);
      var coordinate_sets = this.collectCoordinates(lap_trackpoints);
      if (coordinate_sets.length >= 1) {
        var polylines = L.polyline(coordinate_sets, { weight: 10, color: 'black', opacity: 0.3, lineCap: 'butt' });
        polylines.addTo(new_polyline_group);
      }
    }

    Object.keys(this.props.activity_data).forEach((activity_id) => {

      let activity_data = this.props.activity_data[activity_id];

      // Do not show related activity data if it is not listed as visible.
      if (activity_id != self.props.activity_id && $.inArray(parseInt(activity_id), self.props.visible_lapset_activities) == -1) {
        return;
      }

      var coordinate_sets = self.collectCoordinates(activities_section.state.reduced_trackpoints[activity_id]);

      if (coordinate_sets.length > 0) {

        if (self.props.activity_id == activity_id) {
          line_colour = get_discipline_colour(activity_data.discipline);
          marker_colour = line_colour;
        }

        // The polyline gets greyed out if there are laps highlighted, but
        // the marker stays the same.
        if (grey_out_base_line == true) {
          var line_colour = '#E8E8E8';
        }

        // Add the marker icon if it isn't already set and we are in normal mode.
        if (typeof(xhale.global_state.current_location_markers) != 'undefined' && typeof(xhale.global_state.current_location_markers[self.props.activity_id]) != 'undefined' && typeof(xhale.global_state.current_location_markers[self.props.activity_id].primary) == 'undefined' && self.props.chart_display_mode == 'normal') {

          var compare_icon = L.icon({
            iconUrl: marker_icon_urls.standard[marker_colour],
            iconRetinaUrl: marker_icon_urls.retina[marker_colour],
            iconSize: [22, 37],
            iconAnchor: [11, 37],
            shadowUrl: marker_icon_urls.shadow,
            shadowSize: [41, 41],
            shadowAnchor: [13, 41]
          });

          xhale.global_state.current_location_markers[self.props.activity_id].primary = L.marker([0, 0], { icon: compare_icon }).addTo(self.map);
        }

        // Display the polyline.
        var polylines = L.polyline(coordinate_sets, { weight: 2, color: line_colour, opacity: 1, lineCap: 'butt' });
        polylines.addTo(new_polyline_group);
      }

    });

    if (this.props.chart_display_mode == 'compare_activities') {

      // Add a marker for the main activity.
      if (typeof(xhale.global_state.current_location_markers[self.props.activity_id][self.props.activity_id]) == 'undefined') {

        var icon = L.icon({
          iconUrl: marker_icon_urls.standard[marker_colour],
          iconRetinaUrl: marker_icon_urls.retina[marker_colour],
          iconSize: [22, 37],
          iconAnchor: [11, 37],
          shadowUrl: marker_icon_urls.shadow,
          shadowSize: [41, 41],
          shadowAnchor: [13, 41]
        });

        xhale.global_state.current_location_markers[self.props.activity_id][this.props.activity_id] = L.marker([0, 0], { icon: icon }).addTo(self.map);

      }

      // Add markers for comparision activities.
      for (var i = 0; i < this.props.visible_lapset_activities.length; i++) {

        if (typeof(xhale.global_state.current_location_markers[self.props.activity_id][this.props.visible_lapset_activities[i]]) == 'undefined') {

          var colour = this.props.visible_lapset_activity_colours[this.props.visible_lapset_activities[i]];

          var icon = L.icon({
            iconUrl: marker_icon_urls.standard[colour],
            iconRetinaUrl: marker_icon_urls.retina[colour],
            iconSize: [22, 37],
            iconAnchor: [11, 37],
            shadowUrl: marker_icon_urls.shadow,
            shadowSize: [41, 41],
            shadowAnchor: [13, 41]
          });

          xhale.global_state.current_location_markers[self.props.activity_id][this.props.visible_lapset_activities[i]] = L.marker([0, 0], { icon: icon }).addTo(self.map);
        }

      }

    }
    else if (typeof(xhale.global_state.current_location_markers[self.props.activity_id]) != 'undefined') {

      // Cycle through the laps and display any that are highlighted or set for
      // comparision.
      Object.keys(this.props.laps).forEach((lap_id) => {

        let lap = this.props.laps[lap_id];

        if (lap.colour) {

          var first_trackpoint_index = self.props.activity_data[lap.activity_id].trackpoint_id_lookup[lap.first_trackpoint_id];
          var last_trackpoint_index = self.props.activity_data[lap.activity_id].trackpoint_id_lookup[lap.last_trackpoint_id];
          var first_trackpoint = self.props.activity_data[lap.activity_id].trackpoints[first_trackpoint_index];
          var last_trackpoint = self.props.activity_data[lap.activity_id].trackpoints[last_trackpoint_index];
          var lap_trackpoints = activities_section.state.reduced_trackpoints[lap.activity_id].slice(parseInt(first_trackpoint.reduced_array_index), parseInt(last_trackpoint.reduced_array_index) + 1);
          var coordinate_sets = self.collectCoordinates(lap_trackpoints);

          if (coordinate_sets.length > 0) {

            // Add the polyline to the map.
            var polylines = L.polyline(coordinate_sets, { weight: 2, color: lap.colour, opacity: 1, lineCap: 'butt' });
            polylines.addTo(new_polyline_group);

            // Add a marker to the map.
            if (typeof(xhale.global_state.current_location_markers[self.props.activity_id][lap_id]) == 'undefined') {

              var compare_icon = L.icon({
                iconUrl: marker_icon_urls.standard[lap.colour],
                iconRetinaUrl: marker_icon_urls.retina[lap.colour],
                iconSize: [22, 37],
                iconAnchor: [11, 37],
                shadowUrl: marker_icon_urls.shadow,
                shadowSize: [41, 41],
                shadowAnchor: [13, 41]
              });

              // Update the offset for the marker so they don't switch back and forth.
              self.z_index_offset += 1;

              // Add a new marker to the map with the above icon.
              xhale.global_state.current_location_markers[self.props.activity_id][lap_id] = L.marker([0, 0], { icon: compare_icon, zIndexOffset: self.z_index_offset }).addTo(self.map);
            }
          }
          else if (typeof(xhale.global_state.current_location_markers[self.props.activity_id][lap_id]) != 'undefined') {
            self.map.removeLayer(xhale.global_state.current_location_markers[self.props.activity_id][lap_id]);
            delete xhale.global_state.current_location_markers[self.props.activity_id][lap_id];
          }

        }
        else if (typeof(xhale.global_state.current_location_markers[self.props.activity_id][lap_id]) != 'undefined') {
          self.map.removeLayer(xhale.global_state.current_location_markers[self.props.activity_id][lap_id]);
          delete xhale.global_state.current_location_markers[self.props.activity_id][lap_id];
        }

      });
    }

    // Remove the existing primary map marker (if it exists) when in compare mode.
    // Otherwise remove any lap markers (which is anything not keyed 'primary'.
    if (this.props.chart_display_mode != 'normal') {
      if (typeof(xhale.global_state.current_location_markers[this.props.activity_id].primary) != 'undefined') {
        self.map.removeLayer(xhale.global_state.current_location_markers[self.props.activity_id].primary);
        delete xhale.global_state.current_location_markers[self.props.activity_id].primary;
      }
    }
    else {
      Object.keys(xhale.global_state.current_location_markers[this.props.activity_id]).forEach((key) => {
        if (key != 'primary') {
          self.map.removeLayer(xhale.global_state.current_location_markers[self.props.activity_id][key]);
          delete xhale.global_state.current_location_markers[self.props.activity_id][key];
        }
      });
    }

    // Remove old set of polylines and replace with new set.
    this.map.removeLayer(this.active_polyline_group);
    this.active_polyline_group = new_polyline_group;
    this.map.addLayer(this.active_polyline_group);

    // Zoom the map appropriately.
    if (this.props.map_is_visible) {
      this.map.invalidateSize();
      if (!this.map_zoomed) {
        this.map.fitBounds(this.active_polyline_group.getBounds());
        this.map_zoomed = true;
      }
    }

  }

  render() {
    return (
      <div id={'map-container-' + this.props.activity_id} className="map-container" style={{ height: '218px' }}>
        <div id={'shared-session-map-' + this.props.activity_id} className="shared-session-map" style={{ textAlign: 'center', paddingTop: '50px'}}>
          There is no map for this session
        </div>
      </div>
    );
  }
}

class ActivitiesSection extends React.Component {

  state = {
    laps: {},
    activity_data: {},
    reduced_trackpoints: {}, // This stores trackpoints that have been smoothed out.
    distance_unit: (xhale_meta_data.distance_unit_preference == 'kilometers') ? 'km' : 'miles',
    smoothing_is_active: true,
    x_scale_mode: 'distance',
  }

  applySmoothing = (smoothing_is_active) => {

    // Currently if it is not active, we just use a really long number for the
    // maximum. But the plan is to come back to this an rewrite the smoothing
    // algorithm to allow basing it on averaging over 3 seconds etc.
    let max_trackpoints = (smoothing_is_active) ? 600 : 100000000;

    for (let i = 0; i < this.props.base_activity_ids.length; i++) {
      let activity_id = this.props.base_activity_ids[i];
      this.state.reduced_trackpoints[activity_id] = reduceData(activity_id, this.state.activity_data[activity_id].trackpoints, max_trackpoints);
      this.state.reduced_trackpoints[activity_id] = filterElevationOutlierData(this.state.reduced_trackpoints[activity_id]);
    }

    this.setState({
      reduced_trackpoints: this.state.reduced_trackpoints,
      smoothing_is_active: smoothing_is_active,
    });

  }

  componentDidMount = () => {
    this.loadTrainingData();
  }

  /**
   * Load main data set for this training session.
   *
   * The chart data gets loaded after the page to help get something on the
   * screen faster.
   */
  loadTrainingData = () => {

    // A token allows access as an alternative to being logged in.
    const direct_access_token = getURLParameter('direct_access_token');
    let data = {};
    if (direct_access_token) {
      data.direct_access_token = direct_access_token;
    }

    // Now load the extra data.
    let url;
    if (typeof(training_session_id) != 'undefined') {
      url = '/coach/session-activity-data/' + training_session_id + '/';
    }
    else if (race_id) {
      url = '/coach/race-activity-data/' + race_id + '/';
    }

    // Used for debugging and to speed up testing.
    if (getURLParameter('disable_full_data_fetch') == 'yes') {
      data.disable_full_data_fetch = 'yes';
    }

    ajaxRequest({
      url: url,
      data: data,
      method: 'GET',
      success: this.displayActivityData,
      error: (response, status, request_parameters) => {
        if (window.location.hostname == 'trainxhale.test' && status == 500) {
          // Just to help with development
          window.location.reload();
        }
      },
      expected_response_type: 'json',
    });

  }

  displayActivityData = (new_data, status) => {

    let chart_with_no_distance_exists = false;

    Object.keys(new_data).forEach((activity_id) => {

      let activity_data = new_data[activity_id];

      if (activity_data.discipline != 'transition') {
        storeActivityData(activity_id, activity_id, activity_data);
      }

      var total_distance = null;
      if (activity_data.trackpoints && activity_data.trackpoints[0] && !isNaN(activity_data.trackpoints[0].distance_meters) && activity_data.trackpoints[activity_data.trackpoints.length - 1] && !isNaN(activity_data.trackpoints[activity_data.trackpoints.length - 1].distance_meters)) {
        total_distance = activity_data.trackpoints[activity_data.trackpoints.length - 1].distance_meters - activity_data.trackpoints[0].distance_meters;
      }

      if (!total_distance || total_distance < 500) {
        chart_with_no_distance_exists = true;
      }

    });

    // If the only activity is a swim set the graph to time so that we can see
    // rest periods more easily.
    let only_a_swim_activity = false;
    if (Object.keys(new_data).length == 1) {
      Object.keys(new_data).forEach((activity_id) => {
        if (new_data[activity_id].discipline == 'swimming') {
          only_a_swim_activity = true;
        }
      });
    }

    if (chart_with_no_distance_exists || only_a_swim_activity) {
      setChartsToDuration();
    }
    else {
      setChartsToDistance();
    }

  }

  render () {

    let activities = [];
    for (let i = 0; i < this.props.base_activity_ids.length; i++) {
      activities.push(
        <Activity
          key={this.props.base_activity_ids[i]}
          ref={'activity_section_' + this.props.base_activity_ids[i]}
          base_activity_id={this.props.base_activity_ids[i]}
          activity_data={this.state.activity_data}
          training_session={this.props.training_session}
          race={this.props.race}
          laps={this.state.laps}
          target_swim_paces={this.props.target_swim_paces}
          target_run_paces={this.props.target_run_paces}
          target_bike_powers={this.props.target_bike_powers}
          user_has_premium_features={this.props.user_has_premium_features}
          athlete_id={this.props.athlete_id}
          athlete_data={this.props.athlete_data}
          csrf_token={this.props.csrf_token}
          distance_unit={this.state.distance_unit}
          smoothing_is_active={this.state.smoothing_is_active}
          applySmoothing={this.applySmoothing}
          x_scale_mode={this.state.x_scale_mode}
          current_user_meta_data={this.props.current_user_meta_data}
        />
      );
    }

    return (
      <div>
        {activities}
      </div>
    );
  }
}

class Activity extends React.Component {

  state = {
    discipline: null,
    lapsets: {},
    lap_set_activity_summaries: {},
    current_lapset_id: 'overall_activity',
    chart_display_mode: 'normal',
    active_summary_tab: 'overall',
    active_extras_tab: 'map',
    sets_current_set: null,
    compare_sessions_current_view: 'table',
    compare_sessions_selected_charts: ['speed'],
    compare_sessions_current_selection: null,
    visible_lapset_activities: [],
    visible_lapset_activity_colours: {},
    display_compared_laps_only: true,
    currently_saving_temporary_lap_id: null,
    individual_related_sessions: null,
    highlightable: xhale_meta_data.has_premium_features,
    highlighted_lap_data: {id: null},
    highlighted_trackpoints: {first: null, last: null},
    selected_lap_ids: [],
    selected_laps_visible: true,
    compare_trackpoints: {},
    power_configuration_popup_visible: false,
    speed_configuration_popup_visible: false,
    swim_pace_configuration_popup_visible: false,
    visible_lapset_activities: [], // rename to visible_activities
    visible_lapset_activity_colours: {},     // activity_colours
    existing_collections: [],
    display_climbing_analysis_chart: false,
    lapset_edit_mode: {active: false},
  }

  overlaid = false;
  showing_average = false;
  data_initialised = false;
  markers_initialised = false;

  toggleCompareLap = (lap_id) => {
    var _toggle_compare_lap = (lap_id) => {
      var compare_array_index = $.inArray(lap_id, this.state.selected_lap_ids)
      if (compare_array_index > -1) {
        // Remove lap from comparison list.
        this.state.selected_lap_ids.splice(compare_array_index, 1);
      }
      else {
        // Add lap to comparison list.
        this.state.selected_lap_ids.push(lap_id);
      }

      this.setState({selected_lap_ids: this.state.selected_lap_ids, selected_laps_visible: true});
      this.displayLapOverlays();
    }

    var lap = this.props.laps[lap_id];

    if (typeof(this.props.activity_data[lap.activity_id].trackpoints) == 'undefined') {
      $.getJSON('/coach/load-activity-data/' + lap.activity_id + '/' ).done((data) => {
        storeActivityData(this.props.base_activity_id, lap.activity_id, data[0], () => {(_toggle_compare_lap(lap_id))} );
      });
    }
    else {
      _toggle_compare_lap(lap_id);
    }
  }

  /**
   * Set the lap ID that is currently being hovered over or null.
   */
  setSwimLapMouseHover = (lap_id) => {
    this.setState({highlighted_lap_data: {lap_id: lap_id}});
  }

  selectSwimLap = (lap_id, set_state) => {
    if (set_state == 'toggle') {
      toggleInArray(this.state.selected_lap_ids, lap_id);
    }
    else if (set_state == 'select') {
      addToArrayIfNotExists(this.state.selected_lap_ids, lap_id);
    }
    else if (set_state == 'deselect') {
      removeFromArrayIfExists(this.state.selected_lap_ids, lap_id);
    }
    this.setState({selected_lap_ids: this.state.selected_lap_ids});
  }

  openSwimPacePopup = () => {
    this.setState({swim_pace_configuration_popup_visible: true});
  }

  toggleDisplayClimbingAnalsisChart = () => {
    this.setState({display_climbing_analysis_chart: !this.state.display_climbing_analysis_chart});
  }

  isMapVisible = function () {
    if (this.props.activity_data[this.props.base_activity_id]) {
      if (this.props.activity_data[this.props.base_activity_id].discipline != 'transition') {
        if (this.state.active_extras_tab == 'map') {
            return true;
        }
      }
    }
    else {
      return false;
    }
  }

  displayLapOverlays = function () {

    var selected_lap_ids = (this.state.selected_laps_visible) ? this.state.selected_lap_ids : [];
    var base_activity_id = this.props.base_activity_id;
    var self = this;

    // Ensure colour stack is big enough for the number of laps.
    var colour_stack = [];
    while (selected_lap_ids.length > colour_stack.length) {
      colour_stack = colour_stack.concat(highlight_colours);
    }

    // When a selected lap already has a colour attached to it, remove that colour
    // from the colour stack so that it isn't chosen again. If a lap has been
    // deselected remove its colour.
    Object.keys(this.props.laps).forEach((lap_id) => {
      let lap = this.props.laps[lap_id];
      var lap_selected = $.inArray(parseInt(lap_id), selected_lap_ids) > -1;
      if (lap_selected) {
        if (lap.colour) {
          // This lap is selected but already has a colour so remove this
          // colour from the stack so it isn't picked again.
          var colour_index = colour_stack.indexOf(lap.colour);
          if (colour_index >= 0) {
            colour_stack.splice(colour_index, 1);
          }
        }
      }
      else {
        // This lap isn't selected so remove its colour.
        lap.colour = null;
      }
    });

    // Fill in any selected laps with an appropriate colour.
    for (var index = 0; index < selected_lap_ids.length; index++) {
      var lap = this.props.laps[selected_lap_ids[index]];
      if (!lap.colour) {
        lap.colour = colour_stack.shift();
      }
    };

    // Display each of the overlaid laps.
    var trackpoints = [];
    for (var index = 0; index < selected_lap_ids.length; index++) {
      var lap = this.props.laps[selected_lap_ids[index]];
      var first_trackpoint_index = this.props.activity_data[lap.activity_id].trackpoint_id_lookup[lap.first_trackpoint_id];
      var last_trackpoint_index = this.props.activity_data[lap.activity_id].trackpoint_id_lookup[lap.last_trackpoint_id];
      var first_trackpoint = this.props.activity_data[lap.activity_id].trackpoints[first_trackpoint_index];
      var last_trackpoint = this.props.activity_data[lap.activity_id].trackpoints[last_trackpoint_index];
      var original_trackpoints = activities_section.state.reduced_trackpoints[lap.activity_id].slice(parseInt(first_trackpoint.reduced_array_index), parseInt(last_trackpoint.reduced_array_index) + 1);

      // Clone the original trackpoints.
      // We need to clone the trackpoints otherwise when we have overlapping
      // segments the lap distances interfere with one another.
      var cloned_lap_trackpoints = []
      for (var i = 0; i < original_trackpoints.length; i++) {
        cloned_lap_trackpoints.push($.extend(false, {}, original_trackpoints[i]));
      }

      // Cycle through the trackpoints and calculate distance and duration relative to
      // the start of the lap. Also store the lap id for later reference.
      for (var trackpoints_index = 0; trackpoints_index < cloned_lap_trackpoints.length; trackpoints_index++) {
        cloned_lap_trackpoints[trackpoints_index].lap_distance_meters = cloned_lap_trackpoints[trackpoints_index].distance_meters - cloned_lap_trackpoints[0].distance_meters;
        cloned_lap_trackpoints[trackpoints_index].lap_duration_seconds = cloned_lap_trackpoints[trackpoints_index].cumulative_duration_seconds - cloned_lap_trackpoints[0].cumulative_duration_seconds;
        cloned_lap_trackpoints[trackpoints_index].lap_id = lap.id;
      }

      trackpoints.push(cloned_lap_trackpoints);
    }

    this.recalculate_overlay_scales();

    var chart_types = [];
    var chart_css_ids = [];
    var chart_attributes = [];
    if (distance_line.elevation[base_activity_id]) {
      chart_types.push('elevation');
      chart_css_ids.push('elevation');
      chart_attributes.push('altitude_meters');
    }
    if (distance_line.speed[base_activity_id]) {
      var discipline = this.props.activity_data[base_activity_id].discipline;

      if (discipline == 'swimming') {
        var speed_unit = 'pace_seconds_per_hectometer';
      }
      else if (discipline == 'cycling') {
        var speed_unit = (this.props.distance_unit == 'km') ? 'speed_km_per_hour' : 'speed_miles_per_hour';
      }
      else if (discipline == 'running') {
        var speed_unit = (this.props.distance_unit == 'km') ? 'pace_seconds_per_km' : 'pace_seconds_per_mile';
      }
      else {
        var speed_unit = (this.props.distance_unit == 'km') ? 'speed_km_per_hour' : 'speed_miles_per_hour';
      }

      chart_types.push('speed');
      chart_css_ids.push('speed');
      chart_attributes.push(speed_unit);
    }
    if (distance_line.heart_rate[base_activity_id]) {
      chart_types.push('heart_rate');
      chart_css_ids.push('heart-rate');
      chart_attributes.push('heart_rate_bpm');
    }
    if (distance_line.power[base_activity_id]) {
      chart_types.push('power');
      chart_css_ids.push('power');
      chart_attributes.push('power_watts');
    }
    if (distance_line.cadence[base_activity_id]) {
      chart_types.push('cadence');
      chart_css_ids.push('cadence');
      chart_attributes.push('cadence_rpm');
    }

    for (var index = 0; index < chart_types.length; index++) {
      var chart_type = chart_types[index];
      var chart_css_id = chart_css_ids[index];
      var chart_attribute = chart_attributes[index];

      var last_defined_of_attribute = 0; // Used in case of undefined data.
      var get_attribute_value = function (d) {

        if (typeof d[chart_attribute] != 'undefined') {
          last_defined_of_attribute = d[chart_attribute];

          if (discipline == 'running' && chart_attribute == 'cadence_rpm') {
            last_defined_of_attribute = last_defined_of_attribute * 2;
          }

        }

        return y_scale[chart_type][base_activity_id](last_defined_of_attribute);
      }

      if (this.props.x_scale_mode == 'distance') {
        var scale = x_distance_axis[base_activity_id].scale();
        var metric = (this.overlaid) ? 'lap_distance_meters' : 'distance_meters';
        var lap_line = line()
          .x(function (d) { return scale(d[metric]) })
          .y(function (d) { return get_attribute_value(d) });
      }
      else {
        var scale = x_duration_axis[base_activity_id].scale();
        var metric = (this.overlaid) ? 'lap_duration_seconds' : 'cumulative_duration_seconds';
        var lap_line = line()
          .x(function (d) { return scale(d[metric]) })
          .y(function (d) { return get_attribute_value(d) });
      }

      var chart_container = select('#activity-' + base_activity_id + " ." + chart_css_id + "-chart .chart .chart-group");

      // Update
      var lines = chart_container.selectAll(".compare-lap-line").data(trackpoints, function (d) { return d[0].lap_id} );

      // Enter.
      lines.enter().append("path")
        .attr("class", function (d, index) {
          var lap_id = d[0].lap_id;
          return 'compare-lap-line compare-lap-line-' + lap_id;
        })
        .attr("d", lap_line)
        .style("fill","none")
        .style("display", function () {
          if (self.props.visible_lapset_activities > 0) {return 'block'} else {return 'none'}
        })
        .attr("clip-path", "url(#clip-" + base_activity_id + "-" + chart_css_id + ")")
        .style("stroke", function (d, index) {
          var lap_id = d[0].lap_id;
          return self.props.laps[lap_id].colour;
        });

      // Exit.
      lines.exit().remove();

    }

    // Highlight the laps in the table.
    let updated_laps = Object.assign({}, activities_section.state.laps, this.props.laps);
    activities_section.setState({laps: this.props.laps});

    // Grey out the main chart style so this bit shines through. Or set
    // back if no laps are now selected.
    if (selected_lap_ids.length > 0) {
      $('#charts-section-container-' + base_activity_id).addClass('compare-mode');
    }
    else {
      $('#charts-section-container-' + base_activity_id).removeClass('compare-mode');
    }

  }

  recalculate_overlay_scales = function () {

    // Create scales for when the laps are overlayed based on the lap with the
    // maximum duration and distance.
    var total_lap_distance_meters = [];
    var total_lap_duration_seconds = [];
    for (var index = 0; index < this.state.selected_lap_ids.length; index++) {
      var lap = this.props.laps[this.state.selected_lap_ids[index]];
      var first_trackpoint_index = this.props.activity_data[lap.activity_id].trackpoint_id_lookup[lap.first_trackpoint_id];
      var last_trackpoint_index = this.props.activity_data[lap.activity_id].trackpoint_id_lookup[lap.last_trackpoint_id];
      var first_trackpoint = this.props.activity_data[lap.activity_id].trackpoints[first_trackpoint_index];
      var last_trackpoint = this.props.activity_data[lap.activity_id].trackpoints[last_trackpoint_index];
      var lap_distance_meters = last_trackpoint.distance_meters - first_trackpoint.distance_meters;
      var lap_duration_seconds = last_trackpoint.cumulative_duration_seconds - first_trackpoint.cumulative_duration_seconds;
      total_lap_distance_meters.push(lap_distance_meters);
      total_lap_duration_seconds.push(lap_duration_seconds);
    }
    this.overlaid_x_distance_scale = scaleLinear()
      .range([0, chart_width])
      .domain([0, max(total_lap_distance_meters)]);

    this.overlaid_x_duration_scale = scaleLinear()
      .range([0, chart_width])
      .domain([0, max(total_lap_duration_seconds)]);
  }

  toggleCompareLapOverlays = function () {
    if (this.overlaid === true) {
      this.separateLaps();
    }
    else {
      this.overlayLaps();
    }
  }

  toggleLapAverage = function () {
    if (this.showing_average === true) {
      this.unShowLapAverage();
    }
    else {
      this.showLapAverage();
    }
  }

  rescale = function (duration_of_transition, new_x_scale_mode, callback) {

    var self = this;
    var displayed_activity_id = this.getDisplayedActivityID();

    if (!new_x_scale_mode) {
      new_x_scale_mode = this.props.x_scale_mode;
    }

    var chart_types = [];
    var chart_css_ids = [];
    var chart_attributes = [];
    if (distance_line.elevation[this.props.base_activity_id]) {
      chart_types.push('elevation');
      chart_css_ids.push('elevation');
      chart_attributes.push('altitude_meters');
    }
    if (distance_line.speed[this.props.base_activity_id]) {
      var discipline = this.props.activity_data[this.props.base_activity_id].discipline;

      if (discipline == 'swimming') {
        var speed_unit = 'pace_seconds_per_hectometer';
      }
      else if (discipline == 'cycling') {
        var speed_unit = (this.props.distance_unit == 'km') ? 'speed_km_per_hour' : 'speed_miles_per_hour';
      }
      else if (discipline == 'running') {
        var speed_unit = (this.props.distance_unit == 'km') ? 'pace_seconds_per_km' : 'pace_seconds_per_mile';
      }
      else {
        var speed_unit = (this.props.distance_unit == 'km') ? 'speed_km_per_hour' : 'speed_miles_per_hour';
      }

      chart_types.push('speed');
      chart_css_ids.push('speed');
      chart_attributes.push(speed_unit);
    }
    if (distance_line.heart_rate[this.props.base_activity_id]) {
      chart_types.push('heart_rate');
      chart_css_ids.push('heart-rate');
      chart_attributes.push('heart_rate_bpm');
    }
    if (distance_line.power[this.props.base_activity_id]) {
      chart_types.push('power');
      chart_css_ids.push('power');
      chart_attributes.push('power_watts');
    }
    if (distance_line.cadence[this.props.base_activity_id]) {
      chart_types.push('cadence');
      chart_css_ids.push('cadence');
      chart_attributes.push('cadence_rpm');
    }

    for (var index = 0; index < chart_types.length; index++) {
      var chart_type = chart_types[index];
      var chart_css_id = chart_css_ids[index];
      var chart_attribute = chart_attributes[index];

      var last_defined_of_attribute = 0; // Used in case of undefined data.
      var get_attribute_value = function (d) {

        if (typeof d[chart_attribute] != 'undefined') {
          last_defined_of_attribute = d[chart_attribute];

          if (discipline == 'running' && chart_attribute == 'cadence_rpm') {
            last_defined_of_attribute = last_defined_of_attribute * 2;
          }

        }

        return y_scale[chart_type][self.props.base_activity_id](last_defined_of_attribute);
      }

      // Set the new scale on the axis and redraw it.
      if (new_x_scale_mode == 'distance') {

        var axis = x_distance_axis[this.props.base_activity_id];

        if (this.overlaid) {
          var scale = this.overlaid_x_distance_scale;
          var metric = 'lap_distance_meters';
        }
        else {
          var scale = x_distance_scale[displayed_activity_id];
          var metric = 'distance_meters';
        }
      }
      else {

        var axis = x_duration_axis[this.props.base_activity_id];

        if (this.overlaid) {
          var scale = this.overlaid_x_duration_scale;
          var metric = 'lap_duration_seconds';
        }
        else {
          var scale = x_duration_scale[displayed_activity_id];
          var metric = 'cumulative_duration_seconds';
        }

        axis.tickValues(calculateDurationTickValues(scale));
      }

      axis.scale(scale);
      selectAll("#activity-" + this.props.base_activity_id + " .axis.x." + new_x_scale_mode + "." + chart_css_id).transition().duration(duration_of_transition).call(axis);

      var compare_lap_line = line()
        .x(function (d) { return scale(d[metric]) })
        .y(function (d) { return get_attribute_value(d) });

      var chart_container = select('#activity-' + this.props.base_activity_id + " ." + chart_css_id + "-chart .chart .chart-group");

      chart_container.selectAll(".compare-lap-line")
        .transition()
        .duration(duration_of_transition)
        .attr("d", compare_lap_line)
        .on('end', callback);

    }

  }

  overlayLaps = function () {

    this.overlaid = true;
    this.showing_average = false;

    // Set the correct mode on the extra details section so that the correct
    // markers display on the map.
    activities_section.refs['activity_section_' + this.props.base_activity_id].setState({chart_display_mode: 'overlay_laps'});

    // Hide the old line completely.
    selectAll('#activity-' + this.props.base_activity_id + " .chart .trackpoint-line").transition().duration(1000).style('opacity', 0);
    selectAll('#activity-' + this.props.base_activity_id + " .chart .data-area").transition().duration(1000).style('opacity', 0);

    // Hide the highlight box.
    $('#activity-' + this.props.activity_id + ' .highlight-box').fadeOut(1000);
    $('.lap-table table tr.highlightable.highlighted').removeClass('highlighted');

    // Alter the scale.
    this.rescale(3000, null, () => {
      var compare_lap_line = selectAll('#activity-' + this.props.base_activity_id + ' .chart .chart-group .compare-lap-line');
      compare_lap_line.classed('showing-average', false);
      compare_lap_line.classed('showing-overlaid', true);
    });

  }

  separateLaps = function () {

    var self = this;

    this.overlaid = false;
    this.showing_average = false;

    // Show the old line completely.
    selectAll('#activity-' + this.props.base_activity_id + " .chart .trackpoint-line").transition().duration(1000).style('opacity', 1);
    selectAll('#activity-' + this.props.base_activity_id + " .chart .data-area").transition().duration(1000).style('opacity', 1);

    // Show the highlight box.
    if (this.state.highlighted_trackpoints.first) {
      $('#activity-' + this.props.base_activity_id + ' .highlight-box').fadeIn(1000);
    }

    // Update the scale and then show the correct tooltip and map markers.
    this.rescale(3000, null, () => {

      if (this.state.visible_lapset_activities.length > 0) {
        this.setState({chart_display_mode: 'compare_activities'});
      }
      else {
        this.setState({chart_display_mode: 'normal'});
      }

      var compare_lap_line = selectAll('#activity-' + this.props.base_activity_id + ' .chart .chart-group .compare-lap-line');
      compare_lap_line.classed('showing-average', self.showing_average)
      compare_lap_line.classed('showing-overlaid', self.overlaid);

    });

  }

  unShowLapAverage = function () {
    this.separateLaps();
  }

  showLapAverage = function () {

    var transition_duration = this.overlaid === true ? 3000 : 1000;

    this.separateLaps();
    this.showing_average = true;

    var chart_types = [];
    var chart_css_ids = [];
    var chart_attributes = [];

    if (distance_line.speed[this.props.base_activity_id]) {
      var discipline = this.props.activity_data[this.props.base_activity_id].discipline;

      if (discipline == 'swimming') {
        var speed_unit = 'pace_seconds_per_hectometer';
      }
      else if (discipline == 'cycling') {
        var speed_unit = (this.props.distance_unit == 'km') ? 'speed_km_per_hour' : 'speed_miles_per_hour';
      }
      else if (discipline == 'running') {
        var speed_unit = (this.props.distance_unit == 'km') ? 'pace_seconds_per_km' : 'pace_seconds_per_mile';
      }
      else {
        var speed_unit = (this.props.distance_unit == 'km') ? 'speed_km_per_hour' : 'speed_miles_per_hour';
      }

      chart_types.push('speed');
      chart_css_ids.push('speed');
      chart_attributes.push(speed_unit);

    }
    if (distance_line.heart_rate[this.props.base_activity_id]) {
      chart_types.push('heart_rate');
      chart_css_ids.push('heart-rate');
      chart_attributes.push('average_heart_rate_bpm');
    }
    if (distance_line.power[this.props.base_activity_id]) {
      chart_types.push('power');
      chart_css_ids.push('power');
      chart_attributes.push('average_power_watts');
    }
    if (distance_line.cadence[this.props.base_activity_id]) {
      chart_types.push('cadence');
      chart_css_ids.push('cadence');
      chart_attributes.push('average_cadence_rpm');
    }

    for (var index = 0; index < chart_types.length; index++) {
      var chart_type = chart_types[index];
      var chart_css_id = chart_css_ids[index];
      var lap_average_property = chart_attributes[index];

      var get_attribute_value = (d) => {
        var lap = this.props.laps[d.lap_id];

        if (lap_average_property == 'speed_km_per_hour') {
          var value = xhale.helper.meters_per_second_to_kph(lap.get_speed_meters_per_second())
        }
        else if (lap_average_property == 'speed_miles_per_hour') {
          var value = xhale.helper.meters_per_second_to_mph(lap.get_speed_meters_per_second())
        }
        else if (lap_average_property == 'pace_seconds_per_km') {
          var value = xhale.helper.meters_per_second_as_seconds_per_km(lap.get_speed_meters_per_second())
        }
        else if (lap_average_property == 'pace_seconds_per_mile') {
          var value = xhale.helper.meters_per_second_as_seconds_per_mile(lap.get_speed_meters_per_second())
        }
        else {
          var value = lap[lap_average_property]
        }

        if (discipline == 'running' && lap_average_property == 'average_cadence_rpm') {
          value = value * 2;
        }

        return y_scale[chart_type][this.props.base_activity_id](value);
      }

      if (this.props.x_scale_mode == 'distance') {
        var distance_scale = x_distance_axis[this.props.base_activity_id].scale();
        var compare_line = line()
          .x(function (d) { return distance_scale(d.distance_meters) })
          .y(function (d) { return get_attribute_value(d) });
      }
      else {
        var duration_scale = x_duration_axis[this.props.base_activity_id].scale();
        var compare_line = line()
          .x(function (d) { return duration_scale(d.cumulative_duration_seconds) })
          .y(function (d) { return get_attribute_value(d) });
      }

      var compare_lap_lines = selectAll('#activity-' + this.props.base_activity_id + ' .' + chart_css_id + '-chart .chart .chart-group .compare-lap-line');

      compare_lap_lines
        .transition()
        .duration(transition_duration)
        .attr("d", compare_line)
        .on('end', function () {
          select(this).classed('showing-average', true)
        });

    }

  }

  old_mean_weighted_by_trackpoint_duration = (trackpoints, attribute_name) => {

    // This is the old version of this algorithm. We've copied this here in order to
    // temorarily keep the data in sync with what the user's have saved. But going
    // forward we want to use the updated algrorithm.
    //
    // To do this, make use of this algorithm for sessions before and on the 6th March.
    // Then use the new algorithm for sessions completed after and on 7th March.
    //
    // Then when we migrate old data we can tear out this logic to only use the new data.
    // During the migration there may be some differences between what the user has
    // previously saved and what the new system will show, but these will correct as soon
    // as the migration is complete.

    if (trackpoints.length == 0) {
      return null;
    }

    var sum_of_weighted_values = 0;
    var total_duration = 0;
    var previous_trackpoint_datetime = trackpoints[0].datetime;
    for (let i = 0; i < trackpoints.length; i++) {
      let value = trackpoints[i][attribute_name];
      if (value) {
        // Calculate the weighting of this value in seconds.
        var duration = trackpoints[i].datetime - previous_trackpoint_datetime;

        // Add up values and the weight in seconds of those values.
        sum_of_weighted_values += value * duration;
        total_duration += duration;

        // Prepare value so we can work out duration for next iteration.
        previous_trackpoint_datetime = trackpoints[i].datetime;

      }

    }

    if (sum_of_weighted_values > 0) {
      return sum_of_weighted_values / total_duration;
    }
    else {
      return null;
    }
  }

  updateHighlightedLap = (lap_activity_id, lap_id) => {

    var lap_id = (lap_id) ? lap_id : null;

    let highlight_box = select("#activity-" + this.props.base_activity_id + " .highlight-box");

    // Remove the highlighted section if the start or end highlight positions
    // are not in place.
    if (!this.state.highlighted_trackpoints.first || !this.state.highlighted_trackpoints.last) {
      highlight_box.style("display", 'none');
      this.setState({highlighted_lap_data: {lap_id: null}, active_summary_tab: 'overall' });
      return;
    }

    // Determine the index in the original array of trackpoints for the start
    // and last track points.
    var first_trackpoint_id = this.state.highlighted_trackpoints.first.id;
    var last_trackpoint_id = this.state.highlighted_trackpoints.last.id;
    this.state.highlighted_trackpoints.first.index = parseInt(this.props.activity_data[lap_activity_id].trackpoint_id_lookup[first_trackpoint_id], 10);
    this.state.highlighted_trackpoints.last.index = parseInt(this.props.activity_data[lap_activity_id].trackpoint_id_lookup[last_trackpoint_id], 10);

    // Determine if the point at the start of the drag is on the left
    // or the right.
    if (this.state.highlighted_trackpoints.first.index < this.state.highlighted_trackpoints.last.index) {
      var first_trackpoint = this.state.highlighted_trackpoints.first;
      var last_trackpoint = this.state.highlighted_trackpoints.last;
    }
    else {
      var first_trackpoint = this.state.highlighted_trackpoints.last;
      var last_trackpoint = this.state.highlighted_trackpoints.first;
    }

    // Display the highlight.
    // This has to be based on the filtered trackpoints since that is all that
    // is displayed on the actual chart.
    var first_reduced_trackpoint = activities_section.state.reduced_trackpoints[lap_activity_id][parseInt(first_trackpoint.reduced_array_index)];
    var last_reduced_trackpoint = activities_section.state.reduced_trackpoints[lap_activity_id][parseInt(last_trackpoint.reduced_array_index)];
    if (this.props.x_scale_mode == 'distance') {
      var first_trackpoint_x_scale_position = x_distance_scale[lap_activity_id](first_reduced_trackpoint.distance_meters);
      var last_trackpoint_x_scale_position = x_distance_scale[lap_activity_id](last_reduced_trackpoint.distance_meters);
    }
    else {
      var first_trackpoint_x_scale_position = x_duration_scale[lap_activity_id](first_reduced_trackpoint.cumulative_duration_seconds);
      var last_trackpoint_x_scale_position = x_duration_scale[lap_activity_id](last_reduced_trackpoint.cumulative_duration_seconds);
    }

    var left_position = first_trackpoint_x_scale_position + margin.left;
    var right_position = last_trackpoint_x_scale_position + margin.left;
    var width = right_position - left_position;

    highlight_box.style("left", left_position + 'px');
    highlight_box.style("width", width + 'px');
    highlight_box.style("display", 'block');

    // Lap data is calculated from the full trackpoint data.
    var full_highlighted_trackpoints = this.props.activity_data[lap_activity_id].trackpoints.slice(parseInt(first_trackpoint.unfiltered_array_index), parseInt(last_trackpoint.unfiltered_array_index) + 1)
    var last_index = full_highlighted_trackpoints.length - 1;
    var lap_data = {};
    lap_data.lap_id = lap_id;
    lap_data.has_delete_permission = (lap_id) ? activities_section.state.laps[lap_id].has_delete_permission : null;
    lap_data.distance_meters = full_highlighted_trackpoints[last_index].distance_meters - full_highlighted_trackpoints[0].distance_meters;
    lap_data.elapsed_duration_seconds = (full_highlighted_trackpoints[last_index].datetime - full_highlighted_trackpoints[0].datetime) / 1000;
    lap_data.moving_duration_seconds = calculateMovingTimeFromTrackpoints(full_highlighted_trackpoints);
    lap_data.speed_meters_per_second = (lap_data.distance_meters) ? lap_data.distance_meters / lap_data.moving_duration_seconds : 0;
    lap_data.kph = xhale.helper.meters_per_second_to_kph(lap_data.speed_meters_per_second);
    lap_data.mph = xhale.helper.meters_per_second_to_mph(lap_data.speed_meters_per_second);
    lap_data.pace_min_km = xhale.helper.meters_per_second_as_seconds_per_km(lap_data.speed_meters_per_second);
    lap_data.pace_min_mile = xhale.helper.meters_per_second_as_seconds_per_mile(lap_data.speed_meters_per_second);
    lap_data.height_climbed_meters = calculateElevationGainFromTrackpoints(full_highlighted_trackpoints, SMOOTH_DISTANCE, SMOOTH_HEIGHT, false);
    lap_data.height_climbed_feet = xhale.helper.meters_to_feet(lap_data.height_climbed_meters);
    lap_data.max_heart_rate_bpm = max(full_highlighted_trackpoints, function (d) { return d.heart_rate_bpm; });

    const MIGRATION_TIME = 1583539200000;
    let activity_datetime = newZuluDatetime(activities_section.refs['activity_section_' + this.props.base_activity_id].props.activity_data[this.props.base_activity_id].start_time);
    if (activity_datetime.getTime() > MIGRATION_TIME) {
      lap_data.average_heart_rate_bpm = meanWeightedByTrackpointDuration(full_highlighted_trackpoints, 'heart_rate_bpm');
      lap_data.average_cadence_rpm = meanWeightedByTrackpointDuration(full_highlighted_trackpoints, 'cadence_rpm');
      lap_data.average_power_watts = meanWeightedByTrackpointDuration(full_highlighted_trackpoints, 'power_watts', true);
    }
    else {
      // TODO GET rid of this option and only show using the above logic only
      // after using the migration script on the server to convert old data.
      // See the migrate_lap_averages_to_corrected_calculation management command
      // script.
      lap_data.average_heart_rate_bpm = this.old_mean_weighted_by_trackpoint_duration(full_highlighted_trackpoints, 'heart_rate_bpm');
      lap_data.average_cadence_rpm = this.old_mean_weighted_by_trackpoint_duration(full_highlighted_trackpoints, 'cadence_rpm');
      lap_data.average_power_watts = this.old_mean_weighted_by_trackpoint_duration(full_highlighted_trackpoints, 'power_watts', true);
    }

    // Ensure these are referenced from the full set of trackpoints and not
    // the reduced one by looking them up again.
    lap_data.first_trackpoint = this.props.activity_data[lap_activity_id].trackpoints[parseInt(first_trackpoint.unfiltered_array_index)];
    lap_data.last_trackpoint = this.props.activity_data[lap_activity_id].trackpoints[parseInt(last_trackpoint.unfiltered_array_index)];

    // Store current state.
    // As we have just changed the state of the highlighted data, it cannot
    // currently reflect what is being saved, if anything is.
    this.setState({highlighted_lap_data: lap_data, currently_saving_temporary_lap_id: null});
  }

  sessionCollectionLoadedCallback = (response, status) => {

    // Load the activities for this collection.
    fetchLapSetActivitySummariesBySessionIDs(response.session_ids, response.race_ids, (data) => {
      activitiesLoaded(data, this.props.base_activity_id);
    });

    var session_collection = new SessionCollection(response);
    if (this.props.training_session) {
      session_collection.unsaved_session_ids = xhale.helper.add_to_array_if_not_exists(session_collection.unsaved_session_ids, this.props.training_session.id);
    }
    else if (this.props.race) {
      session_collection.unsaved_race_ids = xhale.helper.add_to_array_if_not_exists(session_collection.unsaved_race_ids, this.props.race.id);
    }

    // Display the collection once loaded.
    this.setState({
      compare_sessions_current_selection: session_collection,
      active_extras_tab: 'compare_sessions'
    });
  }

  componentDidMount = () => {

    this.setState({lapsets: {
      overall_activity: new LapSet('overall_activity', 'Overall activity')
    }});

    if (xhale.helper.has_beta_access('compare_sessions')) {

      var race_ids = xhale.helper.get_url_parameter('races');
      var session_ids = xhale.helper.get_url_parameter('sessions');
      var selection_id = xhale.helper.get_url_parameter('selection');
      var tags = xhale.helper.get_url_parameter('tags');

      if (selection_id) {
        // Load the collection.
        ajaxRequest({
          url: '/coach/get-session-collection/',
          data: {collection_id: selection_id},
          method: 'GET',
          success: this.sessionCollectionLoadedCallback,
          expected_response_type: 'json',
        });
      }
      else if (race_ids || session_ids) {

        var session_ids = (session_ids) ? session_ids.split(',').map(Number) : [];
        var race_ids = (race_ids) ? race_ids.split(',').map(Number) : [];

        if (this.props.training_session) {
          xhale.helper.add_to_array_if_not_exists(session_ids, this.props.training_session.id);
        }

        if (this.props.race) {
          xhale.helper.add_to_array_if_not_exists(race_ids, this.props.race.id);
        }

        // Load the activities for this collection.
        fetchLapSetActivitySummariesBySessionIDs(session_ids, race_ids, (data) => {

          activitiesLoaded(data, this.props.base_activity_id);

          // Create a new session collection and add the base activity and the selected ones.
          var session_collection = new SessionCollection({session_ids: session_ids, race_ids: race_ids});

          // Display the collection once loaded.
          this.setState({
            compare_sessions_current_selection: session_collection,
            active_extras_tab: 'compare_sessions'
          });

        });

      }
      else if (tags) {
        var tags = tags.split(',');

        fetchLapSetActivitySummariesByTags(tags, (data) => {

          activitiesLoaded(data, this.props.base_activity_id);

          // Gather all the session IDs.
          var session_ids = [];
          for (var i = 0; i < data.length; i++) {
            session_ids.push(data[i].training_session_id);
          }

          // Ensure the current session is included in the collection.
          if (this.props.training_session) {
            xhale.helper.add_to_array_if_not_exists(session_ids, this.props.training_session.id);
          }
          if (this.props.race) {
            race_ids.push(this.props.race.id);
            xhale.helper.add_to_array_if_not_exists(race_ids, this.props.race.id);
          }

          // Create a new session collection and add the base activity and the tagged ones.
          var session_collection = new SessionCollection({session_ids: session_ids, race_ids: []});

          // Display the collection once loaded.
          this.setState({
            compare_sessions_current_selection: session_collection,
            active_extras_tab: 'compare_sessions'
          });

        });

      }

    }


  }

  componentDidUpdate = () => {

    // This is not a great way of doing this, but we need to continue a general
    // refactoring to make this better.
    if (!this.data_initialised && this.props.base_activity_id in this.props.activity_data) {

      this.data_initialised = true;
      this.displayTrainingData();

      if (!this.markers_initialised) {
        marker_line = selectAll(".marker-line");
        if (marker_line.size() > 0) {
          updateGraphPositionMarker();
          this.markers_initialised = true;
        }
      }

    }

  }

  displayTrainingData = () => {

    var activity_data = this.props.activity_data[this.props.base_activity_id];

    if (activity_data.discipline == 'transition') {
        return;
    }

    //var charts_exist = ($('#activity-' + this.props.base_activity_id + ' .chart-container:visible').length != 0) ? true : false;

    // Choose lap set if any.
    if (typeof(activity_data.lapsets) != 'undefined') {

      var lapset_keys = Object.keys(activity_data.lapsets);
      var sets_current_set = (lapset_keys.length > 0) ? parseInt(lapset_keys[0]) : null;

      var lapset_activity_summaries = {};

      // Add data to the React component.
      activities_section.setState({
        sets_current_set: sets_current_set
      });

    }

    // Hide the chart container and tooltip if there are no charts to show.
    // Also expand the height of the extras section.
    //if (!charts_exist) {
      //$('#activity-' + this.props.base_activity_id + ' .charts-container').hide();
      //$('#activity-' + this.props.base_activity_id + ' .chart-tooltip').hide();
      //$('#activity-' + this.props.base_activity_id + ' .top-section-extras').css('height', 'inherit');
    //}

  }

  /**
   * Choose which activity to display on the main chart. This is the default
   * activity unless in lapset_edit_mode in which case we need to allow
   * highlighting of a different activity in order to create laps.
   */
  getDisplayedActivityID = () => {
   if (this.state.lapset_edit_mode.active) {
      return this.state.lapset_edit_mode.editing_activity;
    }
    else {
      return this.props.base_activity_id;
    }
  }

  render() {

    let discipline = (this.props.base_activity_id in this.props.activity_data) ? this.props.activity_data[this.props.base_activity_id].discipline : '';

    var top_section = (
      <TopSection
        activity_section={this}
        discipline={discipline}
        base_activity_id={this.props.base_activity_id}
        activity_data={this.props.activity_data}
        user_has_premium_features={this.props.user_has_premium_features}
        activity_component={this}
        highlighted_trackpoints={this.state.highlighted_trackpoints}
        currently_saving_temporary_lap_id={this.state.currently_saving_temporary_lap_id}
        overlay={this.state.overlay}
        compare_sessions_current_selection={this.state.compare_sessions_current_selection}
        laps={this.props.laps}
        lapsets={this.state.lapsets}
        selected_lap_ids={this.state.selected_lap_ids}
        current_lapset_id={this.state.current_lapset_id}
        chart_display_mode={this.state.chart_display_mode}
        visible_lapset_activities={this.state.visible_lapset_activities}
        lap_set_activity_summaries={this.state.lap_set_activity_summaries}
        visible_lapset_activity_colours={this.state.visible_lapset_activity_colours}
        display_compared_laps_only={this.state.display_compared_laps_only}
        sets_current_set={this.state.sets_current_set}
        sets_current_tab={this.state.sets_current_tab}
        distance_unit={this.props.distance_unit}
        highlighted_lap_data={this.state.highlighted_lap_data}
        highlightable={this.state.highlightable}
        athlete_id={this.props.athlete_id}
        athlete_data={this.props.athlete_data}
        individual_related_sessions={this.state.individual_related_sessions}
        active_summary_tab={this.state.active_summary_tab}
        active_extras_tab={this.state.active_extras_tab}
        csrf_token={this.props.csrf_token}
        existing_collections={this.state.existing_collections}
        compare_sessions_current_view={this.state.compare_sessions_current_view}
        compare_sessions_selected_charts={this.state.compare_sessions_selected_charts}
        lapset_edit_mode={this.state.lapset_edit_mode}
        x_scale_mode={this.props.x_scale_mode}
        current_user_meta_data={this.props.current_user_meta_data}
        toggleDisplayClimbingAnalsisChart={this.toggleDisplayClimbingAnalsisChart}
        setSwimLapMouseHover={this.setSwimLapMouseHover}
      />
    );

    var charts_section = <ChartsSection
      activity_id={this.getDisplayedActivityID()}
      base_activity_id={this.props.base_activity_id}
      activity_data={this.props.activity_data}
      visible_lapset_activities={this.state.visible_lapset_activities}
      visible_lapset_activity_colours={this.state.visible_lapset_activity_colours}
      display_compared_laps_only={this.state.display_compared_laps_only}
      chart_display_mode={this.state.chart_display_mode}
      laps={this.props.laps}
      compare_trackpoints={this.state.compare_trackpoints}
      selected_lap_ids={this.state.selected_lap_ids}
      current_lapset={this.state.lapsets[this.state.current_lapset_id]}
      power_configuration_popup_visible={this.state.power_configuration_popup_visible}
      speed_configuration_popup_visible={this.state.speed_configuration_popup_visible}
      swim_pace_configuration_popup_visible={this.state.swim_pace_configuration_popup_visible}
      distance_unit={this.props.distance_unit}
      highlighted_lap_data={this.state.highlighted_lap_data}
      highlighted_trackpoints={this.state.highlighted_trackpoints}
      lapset_edit_mode={this.state.lapset_edit_mode}
      x_scale_mode={this.props.x_scale_mode}
      map_is_visible={this.isMapVisible()}
      smoothing_is_active={this.props.smoothing_is_active}
      applySmoothing={this.props.applySmoothing}
      selectSwimLap={this.selectSwimLap}
      setSwimLapMouseHover={this.setSwimLapMouseHover}
      mobile_mode={this.props.mobile_mode}
      openSwimPacePopup={this.openSwimPacePopup}
      target_swim_paces={this.props.target_swim_paces}
      target_run_paces={this.props.target_run_paces}
      target_bike_powers={this.props.target_bike_powers}
    />

    if (this.state.display_climbing_analysis_chart && this.props.base_activity_id in this.props.activity_data) {
      charts_section = <ClimbingEvaluationSection
        activity_data={this.props.activity_data[this.props.base_activity_id]}
        base_activity_id={this.props.base_activity_id}
        map_is_visible={this.isMapVisible()}
      />
    }

    return (
      <div id={'activity-' + this.props.base_activity_id} className={'activity ' + discipline} data-activity_id={this.props.base_activity_id}>
        <div id={'top-section-' + this.props.base_activity_id} className="top-section" data-activity_id={this.props.base_activity_id} style={{position: 'sticky', top: '-41px'}}>{top_section}</div>
        <div style={{clear: 'both'}}></div>
        <div id={'charts-section-container-' + this.props.base_activity_id} className="charts-section-container" data-activity_id={this.props.base_activity_id}>{charts_section}</div>
      </div>
    );
  }
}

class ClimbingEvaluationSection extends React.Component {

  chart_height = 300;

  state = {
    smooth_height: 0.9,
    smooth_distance: 10,
    marker_left_position: 0,
    current_distance: 0,
    current_elevation: 0,
  }

  formatDistance = (meters) => {
    return xhale.helper.format_km_from_meters(meters, 1) + 'km';
  }

  createChart = () => {

    let smoothed_elevation_points = getSmoothedElevationPointsByDistance(this.props.activity_data.trackpoints, this.state.smooth_distance);

    // Scales.

    this.x_distance_scale = scaleLinear()
      .range([0, chart_width])

    let scale_lowest_value = min(smoothed_elevation_points, function (d) { return d.elevation });
    let scale_highest_value = max(smoothed_elevation_points, function (d) { return d.elevation });
    let scale_delta = scale_highest_value - scale_lowest_value;
    scale_highest_value = (scale_delta > 50) ? scale_highest_value : scale_lowest_value + 50 ;

    this.y_scale = scaleLinear()
      .range([0, this.chart_height])
      .domain([scale_highest_value, scale_lowest_value])

    // Axis.
    this.x_distance_axis = axisBottom()
      .scale(this.x_distance_scale)
      .ticks(calculateNumberOfTicks(chart_width, 55))
      .tickFormat(this.formatDistance)

    this.y_axis = axisLeft()
      .scale(this.y_scale)
      .ticks(5)

    let chart_container = select('.climbing-elevation-chart .chart')

    // Create the container for the actual chart and position it off center so
    // that there is room for margins.
    let chart = chart_container.append("g")
      .attr("class", "chart-group")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    // Stop outlier data overflowing from the chart.
    chart.append("clipPath")
      .attr("id", "clip-climbing-elevation-chart")
      .append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", chart_width)
        .attr("height", this.chart_height);

    chart.append("g")
      .attr("class", "plot-area")

    let y_axis_label = chart.append("text")
      .attr("class", "y-axis-label climbing-elevation")
      .style("font-size", "12px")
      .style("text-anchor", "middle")
      .text(this.props.y_axis_label)
      .attr("transform", "rotate(270)")
      .attr("dx", 0 - (this.chart_height / 2))
      .attr("dy", 15 - margin.left);

    chart.append("g")
      .attr("class", "x axis climbing-elevation")
      .style("color", "black")
      .attr("transform", "translate(-1," + this.chart_height + ")")
      .call(this.x_distance_axis);

    // Y Axis.
    chart.append("g")
      .attr("class", "y axis climbing-elevation")
      .style("color", "black")
      .attr("transform", "translate(-1,0)")
      .call(this.y_axis);

    // Set up a rectangle for detecting when the mouse moves over the charts.
    chart_container.append("rect")
      .attr("width", chart_width + margin.left + margin.right)
      .attr("height", this.chart_height + margin.top + margin.bottom)
      .attr("y", 0)
      .attr("class", "highlight-receptor")
      .style("fill", "none")
      .style("pointer-events", "all")
      .on("touchmove mousemove", this.handleMouseMove)

  }

  handleMouseMove = (event) => {

    let mouse_x_position = event.x - margin.left;
    let elevations = select('.climbing-elevation-chart .trackpoint-line').data()[0]; // Note that this is smoothed data.
    let climbing_sections = select('.climbing-elevation-chart .climbing-line').data()[0];
    let trackpoints = this.props.activity_data.trackpoints;

    var distance = this.x_distance_scale.invert(mouse_x_position);

    var bisect_smoothed_elevations = bisector(function (d) { return d.distance }).left;
    var bisect_trackpoints = bisector(function (d) { return d.distance_meters }).left;
    var elevations_index_right = bisect_smoothed_elevations(elevations, distance, 1); // Index for the track point to the right of the value.
    var trackpoints_index_right = bisect_trackpoints(trackpoints, distance, 1); // Index for the track point to the right of the value.
    let trackpoint = trackpoints[trackpoints_index_right];

    // Pinpoint marker on the map.
    if (trackpoint && this.props.map_is_visible && trackpoint.latitude && trackpoint.longitude && typeof(xhale.global_state.current_location_markers[this.props.base_activity_id].primary) != 'undefined') {
      xhale.global_state.current_location_markers[this.props.base_activity_id].primary.setLatLng([trackpoint.latitude, trackpoint.longitude]);
    }

    // Set the vertical position line.
    if (trackpoint) {
      this.setState({
        current_distance: trackpoint.distance_meters,
        current_elevation: trackpoint.altitude_meters,
        marker_left_position: mouse_x_position + margin.left
      });
    }

  }

  componentDidMount = () => {
    this.createChart();
    this.updateChart();
    document.querySelector(".main-content-inner").scrollTo(0,document.querySelector(".main-content-inner").scrollHeight);
  }

  componentDidUpdate = () => {
    this.updateChart();
  }

  changeSmoothHeight = (event) => {
    this.setState({smooth_height: event.target.value});
  }

  changeSmoothDistance = () => {
    this.setState({smooth_distance: event.target.value});
  }

  updateChart = () => {

    let smoothed_elevation_points = getSmoothedElevationPointsByDistance(this.props.activity_data.trackpoints, this.state.smooth_distance);

    let chart_container = select(ReactDOM.findDOMNode(this));
    let plot_area = chart_container.select('.plot-area');

    this.x_distance_scale = scaleLinear()
      .range([0, chart_width])
      .domain([
        min(smoothed_elevation_points, function (d) { return d.distance }),
        max(smoothed_elevation_points, function (d) { return d.distance })
      ]);

    let distance_area = area()
      .x((d) => { return this.x_distance_scale(d.distance); })
      .y0((d) => { return this.y_scale(d.elevation)})
      .y1(this.chart_height);

    let distance_line = line()
      .x((d) => { return this.x_distance_scale(d.distance) })
      .y((d) => { return this.y_scale(d.elevation)} );

    let climbing_line = line()
      .x((d) => { return this.x_distance_scale(d.distance); })
      .y((d) => { return this.y_scale(d.elevation)});

    this.x_distance_axis.scale(this.x_distance_scale)
 
    let areas = plot_area.selectAll('.data-area')
      .data([smoothed_elevation_points])
      .attr("d", distance_area)

    let lines = plot_area.selectAll(".trackpoint-line")
      .data([smoothed_elevation_points])
      .attr("d", distance_line)

    let climbing_data = calculateElevationGainFromTrackpoints(this.props.activity_data.trackpoints, this.state.smooth_distance, this.state.smooth_height, true);

    let climbing_lines = plot_area.selectAll(".climbing-line")
      .data(climbing_data.section_plots)
      .attr("d", climbing_line)

    let climbing_data_text = plot_area.selectAll(".climbing-line-meters-text")
      .data(climbing_data.section_plots)
      .text((d, i) => { return decimalPlaces(climbing_data.section_elevation_gains[i], 2) })
      .attr('x', (d, i) => { return this.x_distance_scale(d[0].distance) })
      .attr('y', (d, i) => { return this.y_scale(d[0].elevation) + 10 })

    areas.enter().append("path")
      .attr("class", 'data-area')
      .attr("clip-path", "url(#clip-climbing-elevation-chart)")

    lines.enter().append('path')
      .attr("fill", 'none')
      .attr("clip-path", "url(#clip-climbing-elevation-chart)")
      .attr("d", distance_line)
      .style("stroke", 'silver')
      .attr("class", 'trackpoint-line');

    climbing_lines.enter().append('path')
      .attr("d", climbing_line)
      .attr("fill", 'none')
      .attr("clip-path", "url(#clip-climbing-elevation-chart)")
      .style("stroke", 'red')
      .attr("class", 'data-line climbing-line');

    climbing_data_text.enter().append('text')
      .attr("fill", 'currentColor')
      .text((d, i) => { return decimalPlaces(climbing_data.section_elevation_gains[i], 2) })
      .attr('x', (d, i) => { return this.x_distance_scale(d[0].distance) })
      .attr('y', (d, i) => { return this.y_scale(d[0].elevation) + 10 })
      .attr("class", 'data-line climbing-line-meters-text');

    lines.exit().remove();
    climbing_data_text.exit().remove();
    climbing_lines.exit().remove();

  }

  render = () => {

    let climbing_data = calculateElevationGainFromTrackpoints(this.props.activity_data.trackpoints, this.state.smooth_distance, this.state.smooth_height, true);
    let marker_left = this.state.marker_left_position + 'px'

    return (
      <div>
        <div>
          <label htmlFor="id_smooth_height">smooth height:</label> <input type="number" name="smooth_height" id="id_smooth_height" min="0" step="0.1" value={this.state.smooth_height} onChange={this.changeSmoothHeight} />{' '}
          <label htmlFor="id_smooth_distance">smooth distance:</label> <input type="number" name="smooth_distance" id="id_smooth_distance" min="0" step="0.1" value={this.state.smooth_distance} onChange={this.changeSmoothDistance} />{' '}
          <span style={{display: 'inline-block', minWidth: '150px'}}>Elevation gain: {decimalPlaces(climbing_data.total_elevation_gain, 2)}</span>{' '}
          <span style={{display: 'inline-block', minWidth: '150px'}}>Distance: {decimalPlaces((this.state.current_distance / 1000), 4)} km</span>{' '}
          <span>Elevation: {decimalPlaces(this.state.current_elevation, 2)} meters</span>
       </div>
        <div className="climbing-elevation-chart" style={{height: (this.chart_height + 40) + 'px', width: '100%', clear: 'both', background: 'white' }}>
          <div className="marker-line" style={{width: '1px', background: '#515151', position: 'absolute', top: '16px', bottom: '24px', left: marker_left, pointerEvents: 'none'}}></div>
          <svg style={{height: '100%', width: '100%'}} className="chart"></svg>
        </div>
      </div>
    );
  }
}

class TopSection extends React.Component {

  handleMapTabClick = () => {
    this.props.activity_component.setState({active_extras_tab: 'map'});
  }

  handleLapsTabClick = () => {
    this.props.activity_component.setState({active_extras_tab: 'laps'});
  }

  handleCompareSessionsTabClick = () => {
    this.props.activity_component.setState({active_extras_tab: 'compare_sessions'});
  }

  handleCompareSessionsTableTabClick = () => {
    this.props.activity_component.setState({active_extras_tab: 'compare_sessions', compare_sessions_current_view: 'table'});
  }

  handleCompareSessionsChartsTabClick = () => {
    this.props.activity_component.setState({active_extras_tab: 'compare_sessions', compare_sessions_current_view: 'charts'});
  }

  handleDownloadTrainingFileTabClick = () => {
    this.props.activity_component.setState({active_extras_tab: 'edit_activity'});
  }

  handleShareSessionTabClick = () => {
    this.props.activity_component.setState({active_extras_tab: 'share_session'});
  }

  handleZonesTabClick = () => {
    this.props.activity_component.setState({active_extras_tab: 'zones'});
  }

  handleOverallTabClick = () => {
    this.props.activity_component.setState({active_summary_tab: 'overall'});
  }

  handleHighlightedTabClick = () => {
    this.props.activity_component.setState({active_summary_tab: 'highlighted'});
  }

  handleCompareLapsTabClick = () => {
    this.props.activity_component.setState({active_summary_tab: 'compare_laps'});
  }

  render() {

    let map_tab_classes = 'map control';
    let laps_tab_classes = 'laps control';
    let compare_sessions_tab_classes = 'compare-sessions control';
    let download_training_file_tab_classes = 'edit-activity control';
    let share_session_tab_classes = 'share-session control';
    let zones_tab_classes = 'zones control';
    if (this.props.active_extras_tab == 'map') {
      map_tab_classes += ' active';
    }
    else if (this.props.active_extras_tab == 'laps') {
      laps_tab_classes += ' active';
    }
    else if (this.props.active_extras_tab == 'compare_sessions') {
      compare_sessions_tab_classes += ' active';
    }
    else if (this.props.active_extras_tab == 'edit_activity') {
      download_training_file_tab_classes += ' active';
    }
    else if (this.props.active_extras_tab == 'share_session') {
      share_session_tab_classes += ' active';
    }
    else if (this.props.active_extras_tab == 'zones') {
      zones_tab_classes += ' active';
    }

    var tabs = [];
    var overall_summary;
    var highlight_summary;
    var compare_summary;
    if (this.props.activity_data[this.props.base_activity_id]) {

      if (this.props.active_summary_tab == 'overall') {
        overall_summary = <OverallSummary
          current_user_meta_data={this.props.current_user_meta_data}
          activity_data={this.props.activity_data[this.props.base_activity_id]}
          toggleDisplayClimbingAnalsisChart={this.props.toggleDisplayClimbingAnalsisChart}
        />
      }

      if (this.props.user_has_premium_features && this.props.activity_data[this.props.base_activity_id].discipline != 'swimming') {

        var overall_classes = 'activity-summary__tab activity-summary__overall-tab';
        if (this.props.active_summary_tab == 'overall') {
          overall_classes += ' activity-summary__tab_active';
        }
        tabs.push(<h3 key="overall" className={overall_classes} onClick={this.handleOverallTabClick}>overall</h3>);

        var highlighted_classes = 'activity-summary__tab activity-summary__highlighted-tab';
        if (this.props.active_summary_tab == 'highlighted') {

          if (this.props.lapset_edit_mode.active) {
            var activity_id = this.props.lapset_edit_mode.editing_activity;
          }
          else {
            var activity_id = this.props.base_activity_id;
          }

          highlighted_classes += ' activity-summary__tab_active';
          highlight_summary = (
            <HighlightSummary
              activity_id={activity_id}
              base_activity_id={this.props.base_activity_id}
              discipline={this.props.activity_data[this.props.base_activity_id].discipline}
              lap_data={this.props.highlighted_lap_data}
              currently_saving_temporary_lap_id={this.props.currently_saving_temporary_lap_id}
              activity_data={this.props.activity_data}
              highlighted_trackpoints={this.props.highlighted_trackpoints}
              distance_unit={this.props.distance_unit}
              lapset_edit_mode={this.props.lapset_edit_mode}
              current_lapset_id={this.props.current_lapset_id}
              x_scale_mode={this.props.x_scale_mode}
            />
          );
        }
        tabs.push(<h3 key="activity-summary__tab activity-summary__highlighted-tab" className={highlighted_classes} onClick={this.handleHighlightedTabClick}>highlighted</h3>);

        var compare_classes = 'activity-summary__tab activity-summary__compare-tab';
        if (this.props.active_summary_tab == 'compare_laps') {
          compare_classes += ' activity-summary__tab_active';
          compare_summary = (
            <CompareSummary
              activity_id={this.props.base_activity_id}
              discipline={this.props.activity_data[this.props.base_activity_id].discipline}
              athlete_id={this.props.athlete_id}
              selected_lap_ids={this.props.selected_lap_ids}
              laps={this.props.laps}
            />
          );
        }
        var button_text = xhale.helper.has_beta_access('compare_sessions') ? 'selected' : 'compare';
        tabs.push(<h3 key="compare" className={compare_classes} onClick={this.handleCompareLapsTabClick}>{button_text}</h3>);

      }

    }

    var compare_table_tab;
    var compare_charts_tab;
    if (xhale.helper.has_beta_access('compare_sessions')) {
      compare_table_tab = (
        <div className={compare_sessions_tab_classes} onClick={this.handleCompareSessionsTabClick}>
          Compare
          <div className="table" style={{padding: '10px 0 0 5px'}} onClick={this.handleCompareSessionsTableTabClick}>- table</div>
          <div className="charts" style={{padding: '10px 0 0 5px'}} onClick={this.handleCompareSessionsChartsTabClick}>- charts</div>
        </div>
      );
    }

    var edit_activity_tab;
    if (xhale.helper.has_beta_access('download_training_files') || (this.props.athlete_id == xhale_meta_data.user_id || xhale.helper.has_beta_access('coach_import_athlete_training'))) {
      edit_activity_tab = <div className={download_training_file_tab_classes} onClick={this.handleDownloadTrainingFileTabClick}>File</div>;
    }

    var share_session_tab;
    if (xhale.helper.has_beta_access('share_training_session')) {
      share_session_tab = <div className={share_session_tab_classes} onClick={this.handleShareSessionTabClick}>Share</div>;
    }

    let loaded = this.props.activity_data[this.props.base_activity_id] ? true : false;

    let time_in_zones_tab;
    if (
      (this.props.discipline == 'running' || this.props.discipline == 'cycling') &&
      'time_in_zones' in this.props.activity_data[this.props.base_activity_id] &&
      Object.keys(this.props.activity_data[this.props.base_activity_id].time_in_zones).length > 0
    ) {
      time_in_zones_tab = <div className={zones_tab_classes} onClick={this.handleZonesTabClick}>Zones</div>;
    }

    return (

      <div className={"top-section__inner" + (loaded ? '' : ' loading')}>
        <div className="top-section-controls">
          <div className={map_tab_classes} onClick={this.handleMapTabClick}>Map</div>
          <div className={laps_tab_classes} onClick={this.handleLapsTabClick}>Laps</div>
          {time_in_zones_tab}
          {compare_table_tab}
          {compare_charts_tab}
          {edit_activity_tab}
          {share_session_tab}
        </div>

        <div className="top-section__extras-container">
          <TopSectionExtras
            activity_section={this.props.activity_section}
            activity_id={this.props.base_activity_id}
            activity_data={this.props.activity_data}
            active_tab={this.props.active_extras_tab}
            compare_sessions_current_selection={this.props.compare_sessions_current_selection}
            laps={this.props.laps}
            selected_lap_ids={this.props.selected_lap_ids}
            lapsets={this.props.lapsets}
            current_lapset_id={this.props.current_lapset_id}
            lap_set_activity_summaries={this.props.lap_set_activity_summaries}
            chart_display_mode={this.props.chart_display_mode}
            visible_lapset_activities={this.props.visible_lapset_activities}
            visible_lapset_activity_colours={this.props.visible_lapset_activity_colours}
            display_compared_laps_only={this.props.display_compared_laps_only}
            sets_current_set={this.props.sets_current_set}
            sets_current_tab={this.props.sets_current_tab}
            distance_unit={this.props.distance_unit}
            highlighted_lap_data={this.props.highlighted_lap_data}
            highlightable={this.props.highlightable}
            athlete_id={this.props.athlete_id}
            athlete_data={this.props.athlete_data}
            discipline={this.props.discipline}
            individual_related_sessions={this.props.individual_related_sessions}
            csrf_token={this.props.csrf_token}
            existing_collections={this.props.existing_collections}
            compare_sessions_current_view={this.props.compare_sessions_current_view}
            compare_sessions_selected_charts={this.props.compare_sessions_selected_charts}
            lapset_edit_mode={this.props.lapset_edit_mode}
            x_scale_mode={this.props.x_scale_mode}
            display_map={this.map_is_visible}
            setSwimLapMouseHover={this.props.setSwimLapMouseHover}
          />
        </div>

        <div className="activity-summary">
          <div className="activity-summary__inner">
            <h2 className="activity-summary__discipline-title" style={{ fontWeight: 100, float: 'left', textTransform: 'capitalize' }}>{loaded ? this.props.activity_data[this.props.base_activity_id].discipline : 'Loading...'}</h2>
            {tabs}
            {overall_summary}
            {highlight_summary}
            {compare_summary}
          </div>
        </div>
      </div>

    );
  }
}

/**
 * React component representing the section at the top of an activity where
 * extra details are shown such as laps and the map.
 */
class TopSectionExtras extends React.Component {

  /**
   * Implement React's render.
   */
  render() {

    var self = this;

    // No map for transition.
    var map_section;
    if (this.props.activity_data[this.props.activity_id]) {
      if (this.props.activity_data[this.props.activity_id].discipline != 'transition') {
        var map_display_css = (this.props.active_tab == 'map') ? 'block' : 'none';
        map_section = (
          <div className="map-section-container" style={{display: map_display_css, height: '265px'}}>
            <MapSection
              activity_id={this.props.activity_id}
              activity_data={this.props.activity_data}
              laps={this.props.laps}
              highlighted_lap_data={this.props.highlighted_lap_data}
              visible_lapset_activities={this.props.visible_lapset_activities}
              visible_lapset_activity_colours={this.props.visible_lapset_activity_colours}
              chart_display_mode={this.props.chart_display_mode}
              map_is_visible={this.props.active_tab == 'map' ? true : false}
            />
          </div>
        );
      }
    }

    var lap_section;
    if (this.props.active_tab == 'laps' && this.props.activity_data[this.props.activity_id]) {
      lap_section = (
        <div className="lap-tables-container">
          <LapTables
            athlete_id={this.props.athlete_id}
            activity_id={this.props.activity_id}
            laps={this.props.laps}
            lapsets={this.props.lapsets}
            current_lapset_id={this.props.current_lapset_id}
            selected_lap_ids={this.props.selected_lap_ids}
            discipline={this.props.activity_data[this.props.activity_id].discipline}
            highlighted_lap={this.props.highlighted_lap_data.lap_id}
            highlightable={this.props.highlightable}
            activity_data={this.props.activity_data}
            distance_unit={this.props.distance_unit}
            visible_lapset_activities={this.props.visible_lapset_activities}
            visible_lapset_activity_colours={this.props.visible_lapset_activity_colours}
            csrf_token={this.props.csrf_token}
            display_compared_laps_only={this.props.display_compared_laps_only}
            lapset_edit_mode={this.props.lapset_edit_mode}
            x_scale_mode={this.props.x_scale_mode}
            setSwimLapMouseHover={this.props.setSwimLapMouseHover}
          />
        </div>
      );
    }

    let zones_section;
    if (
      this.props.active_tab == 'zones' &&
      this.props.activity_data[this.props.activity_id] &&
      'time_in_zones' in this.props.activity_data[this.props.activity_id] &&
      (this.props.discipline == 'running' || this.props.discipline == 'cycling')
    ) {
      zones_section = (
        <ZonesSection
          discipline={this.props.activity_data[this.props.activity_id].discipline}
          activity_data={this.props.activity_data[this.props.activity_id]}
          activity_id={this.props.activity_id}
          athlete_data={this.props.athlete_data}
        />
      );
    }

    var compare_sessions_section;
    if (this.props.active_tab == 'compare_sessions') {
      compare_sessions_section = (
        <CompareSessionsSection
          activity_section={this.props.activity_section}
          base_activity_id={this.props.activity_id}
          laps={this.props.laps}
          lapsets={this.props.lapsets}
          current_lapset_id={this.props.current_lapset_id}
          activity_data={this.props.activity_data}
          compare_sessions_current_selection={this.props.compare_sessions_current_selection}
          distance_unit={this.props.distance_unit}
          visible_lapset_activities={this.props.visible_lapset_activities}
          visible_lapset_activity_colours={this.props.visible_lapset_activity_colours}
          display_compared_laps_only={this.props.display_compared_laps_only}
          individual_related_sessions={this.props.individual_related_sessions}
          existing_collections={this.props.existing_collections}
          compare_sessions_current_view={this.props.compare_sessions_current_view}
          compare_sessions_selected_charts={this.props.compare_sessions_selected_charts}
          lap_set_activity_summaries={this.props.lap_set_activity_summaries}
          athlete_id={this.props.athlete_id}
          discipline={this.props.discipline}
          csrf_token={this.props.csrf_token}
        />
      );
    }

    var download_training_file_section;
    if (this.props.active_tab == 'edit_activity') {
      download_training_file_section = (
        <div className="edit-activity-section-container" style={{height: '100%', overflow: 'auto'}}>
          <EditActivitySection
            athlete_id={this.props.athlete_id}
            activity_id={this.props.activity_id}
            activity_data={this.props.activity_data}
            csrf_token={this.props.csrf_token}
          />
        </div>
      );
    }

    var share_session_section;
    if (this.props.active_tab == 'share_session' && xhale.helper.has_beta_access('share_training_session')) {
      share_session_section = <ShareWidget />;
    }

    let style = {}
    if (
      this.props.activity_data[this.props.activity_id] &&
      this.props.activity_data[this.props.activity_id].supported_charts &&
      this.props.activity_data[this.props.activity_id].supported_charts.any_to_display === false
    ) {
      style.height = 'inherit';
    }

    return (
      <div className="top-section-extras" style={style}>
        {map_section}
        {lap_section}
        {zones_section}
        {compare_sessions_section}
        {download_training_file_section}
        {share_session_section}
      </div>
    );

  }
}

class SwimCharts extends React.Component {

  render() {

    let chart_containers = [];
    var km_axis_classes = 'unit km';
    var mile_axis_classes = 'unit miles';

    if (hasBetaAccess('swim_training_log_graphs')) {
      chart_containers.push(
        <ChartSectionSwimLapsPace
          key="swim-speed"
          distance_unit={this.props.distance_unit}
          lapset_edit_mode={this.props.lapset_edit_mode}
          x_scale_mode={this.props.x_scale_mode}
          activity_id={this.props.activity_id}
          base_activity_id={this.props.base_activity_id}
          activity_data={this.props.activity_data}
          laps={this.props.laps}
          target_swim_paces={this.props.target_swim_paces}
          selected_lap_ids={this.props.selected_lap_ids}
          highlighted_lap_data={this.props.highlighted_lap_data}
          chart_width={chart_width}
          mobile_mode={this.props.mobile_mode}
          selectSwimLap={this.props.selectSwimLap}
          setSwimLapMouseHover={this.props.setSwimLapMouseHover}
          openSwimPacePopup={this.props.openSwimPacePopup}
        />
      );
      /*
       * Not satsified this is good enought to go out yet. Maybe it will work better overlayed on
       * top of the pace.
      chart_containers.push(
        <ChartSectionSwimLapsStrokes
          key="swim-strokes"
          distance_unit={this.props.distance_unit}
          lapset_edit_mode={this.props.lapset_edit_mode}
          x_scale_mode={this.props.x_scale_mode}
          activity_id={this.props.activity_id}
          base_activity_id={this.props.base_activity_id}
          activity_data={this.props.activity_data}
          laps={this.props.laps}
          target_swim_paces={this.props.target_swim_paces}
          selected_lap_ids={this.props.selected_lap_ids}
          highlighted_lap_data={this.props.highlighted_lap_data}
          chart_width={chart_width}
          mobile_mode={this.props.mobile_mode}
          selectSwimLap={this.props.selectSwimLap}
          setSwimLapMouseHover={this.props.setSwimLapMouseHover}
          openSwimPacePopup={this.props.openSwimPacePopup}
        />
      );
      */
    }

    let charts_container;
    charts_container = (
      <div className="charts-container" style={{position: 'relative'}}>
        <div className="marker-line" style={{width: '1px', background: '#515151', position: 'absolute', top: '0px', bottom: '0px', left: '-50px', pointerEvents: 'none', display: 'none'}}></div>
        <div className="highlight-box" style={{display: 'none', position: 'absolute', top: '0px', bottom: '0px', left: '400px', width: '0px', background: 'rgba(0, 0, 0, 0.1)', pointerEvents: 'none'}}>
          <div className="left" onMouseDown={this.handleMouseDownOnHighlightBoxLeftSide} onMouseUp={this.handleMouseUpOnHighlightBoxLeftSide} style={{ width: '1px', backgroundColor: 'white', height: '100%', position: 'absolute', cursor: 'col-resize', pointerEvents: 'auto' }}></div>
          <div className="right" onMouseDown={this.handleMouseDownOnHighlightBoxRightSide} onMouseUp={this.handleMouseUpOnHighlightBoxRightSide} style={{ height: '100%', width: '1px', backgroundColor: 'white', position: 'absolute', right: '0px', cursor: 'col-resize', pointerEvents: 'auto' }}></div>
        </div>
        {chart_containers}
      </div>
    );

    const chart_swim_pace_configuration_popup = (
      <ChartSwimPaceConfigurationPopup
        activity_id={this.props.activity_id}
        visible={this.props.swim_pace_configuration_popup_visible}
        target_swim_paces={this.props.target_swim_paces}
      />
    );

    return (
      <div className="charts-section-container-inner">
        {chart_swim_pace_configuration_popup}
        {charts_container}
      </div>
    );

  }

}

class ChartsSection extends React.Component {

  state = {
    speed_scale_min: null,
    speed_scale_max: null,
    power_scale_min: null,
    power_scale_max: null,
    unexpectedError: false,
  }

  componentDidCatch(error, info) {
    this.setState({unexpectedError: true});
    logError(error, info)
  }

  setSpeedScaleMin = (seconds) => {
    this.setState({speed_scale_min: seconds});
  }

  setSpeedScaleMax = (seconds) => {
    this.setState({speed_scale_max: seconds});
  }

  setPowerScaleMin = (event) => {
    this.setState({power_scale_min: parseInt(event.target.value)});
  }

  setPowerScaleMax = (event) => {
    this.setState({power_scale_max: parseInt(event.target.value)});
  }

  /*
   * Implement React componentDidUpdate.
   */
  componentDidUpdate = (prevProps, prevState) => {
    // Make sure sticky wrapper is tall enough.
    if ((this.props.chart_display_mode != prevProps.chart_display_mode) || (this.props.visible_lapset_activities.length != prevProps.visible_lapset_activities.length)) {
      var height = $('#compare-tooltip-activity-container-' + this.props.activity_id).height();
      $('#compare-tooltip-activity-container-' + this.props.activity_id + '-sticky-wrapper').height(height);
    }
  }

  /*
   * Start dragging the left-hand-side of the highlight box.
   */
  handleMouseDownOnHighlightBoxLeftSide = () => {

    // Record the start position of the highlight box, which is the opposite
    // side to the one we are dragging. The first and last trackpoints switch
    // depending on the direction the user drags so we have to determine which
    // one is on the left with the if statement.
    if (this.props.highlighted_trackpoints.first.index < this.props.highlighted_trackpoints.last.index) {
      this.props.highlighted_trackpoints.drag_anchor_reduced_trackpoint = this.props.highlighted_trackpoints.last;
    }
    else {
      this.props.highlighted_trackpoints.drag_anchor_reduced_trackpoint = this.props.highlighted_trackpoints.first;
    }

    dragging = true;
  }

  /*
   * Stop dragging the left-hand-side of the highlight box.
   */
  handleMouseUpOnHighlightBoxLeftSide = () => {
    dragging = false;
  }

  /*
   * Start dragging the right-hand-side of the highlight box.
   */
  handleMouseDownOnHighlightBoxRightSide = () => {

    // Record the start position of the highlight box, which is the opposite
    // side to the one we are dragging. The first and last trackpoints switch
    // depending on the direction the user drags so we have to determine which
    // one is on the right with the if statement.
    if (this.props.highlighted_trackpoints.first.index > this.props.highlighted_trackpoints.last.index) {
      this.props.highlighted_trackpoints.drag_anchor_reduced_trackpoint = this.props.highlighted_trackpoints.last;
    }
    else {
      this.props.highlighted_trackpoints.drag_anchor_reduced_trackpoint = this.props.highlighted_trackpoints.first;
    }

    dragging = true;
  }

  /*
   * Stop dragging the right-hand-side of the highlight box.
   */
  handleMouseUpOnHighlightBoxRightSide = () => {
    dragging = false;
  }

  toggleHideUncomparedData = (event) => {
    // TODO This currently only works when in distance mode. Support this for duration mode as well.
    var display_compared_laps_only = this.props.display_compared_laps_only ? false : true;
    activities_section.refs['activity_section_' + this.props.activity_id].setState({
      chart_display_mode: 'compare_activities',
      display_compared_laps_only: display_compared_laps_only
    });
  }

  setChartsToMiles = () => {
    activities_section.setState({distance_unit: 'miles'}, () => {
      rebuild_target_run_paces(this.props.target_run_paces, this.props.distance_unit, this.props.activity_id)
    });
  }

  setChartsToKM = () => {
    activities_section.setState({distance_unit: 'km'}, () => {
      rebuild_target_run_paces(this.props.target_run_paces, this.props.distance_unit, this.props.activity_id)
    });
  }

  maxNonOutlierPace = (trackpoints, speed_unit) => {

    // First put the values into an array, getting rid of any undefined
    // values.
    var data = [];
    Object.keys(trackpoints).forEach((index) => {
      let trackpoint = trackpoints[index];
      if (trackpoint[speed_unit]) {
        data.push(trackpoint[speed_unit]);
      }
    });

    if (data.length == 0) {
        return;
    }

    // Sort it.
    data.sort( function (a, b) {return a-b });

    // Find values a quarter (q1) into the data set and 3 quarters in (q3).
    // Don't worry about getting the exact median here as we have enough
    // data that it shouldn't make a difference.
    var data_length = data.length;
    var q1_index = data_length - Math.round(data_length * 0.75);
    var q3_index = data_length - Math.round(data_length * 0.25);
    var q1_value = data[q1_index];
    var q3_value = data[q3_index];

    // Make our highest pace no more than 1.5 times the interquartile range.
    // The 1.5 value comes from John Tukey and seems to fit quite well.
    // Apparently setting a value of 3 instead was termed 'far out'. There is
    // nothing sacred about this algorithm but it seems to work better than
    // our previous method of just chopping off the top 1% which broke when
    // there were long rest periods.
    var interquartile_range = q3_value - q1_value;
    var highest_pace = q3_value + (1.5 * interquartile_range);

    return highest_pace;
  }

  getPowerScaleMin = (trackpoints, allow_override_from_state) => {
    if (this.state.power_scale_min !== null && allow_override_from_state) {
      return this.state.power_scale_min;
    }
    else if (trackpoints) {
       return min(trackpoints, function (d) { return d.power_watts; });
    }
    else {
      return null;
    }
  }

  getPowerScaleMax = (trackpoints, allow_override_from_state) => {
    if (this.state.power_scale_max !== null && allow_override_from_state) {
      return this.state.power_scale_max;
    }
    else if (trackpoints) {
      return max(trackpoints, function (d) { return d.power_watts; });
    }
    else {
      return null;
    }
  }

  getSpeedScaleMax = (trackpoints, speed_unit, allow_override_from_state) => {
    if (this.state.speed_scale_max !== null && allow_override_from_state) {
      return this.state.speed_scale_max;
    }
    else if (speed_unit == 'pace_seconds_per_km' || speed_unit == 'pace_seconds_per_mile') {
      let result = this.maxNonOutlierPace(trackpoints, speed_unit);

      // Don't attempt to display it if it is over an hour (3599) as this is
      // obviously an error.
      if (result > 3599) {
        result = 3599;
      }

      return result;

    }
    else if (trackpoints) {
      let result = max(trackpoints, function (d) { return d[speed_unit] } );
      return result;
    }
    else {
      return null;
    }
  }

  getSpeedScaleMin = (trackpoints, speed_unit, allow_override_from_state) => {

    if (this.state.speed_scale_min !== null && allow_override_from_state) {
      return this.state.speed_scale_min;
    }
    else if (speed_unit == 'speed_km_per_hour' || speed_unit == 'speed_miles_per_hour') {
      return 0;
    }
    else if (trackpoints) {

      let result = min(trackpoints, function (d) { return d[speed_unit]; });

      // Don't attempt to display it if it is over an hour (3599) as this is
      // obviously an error.
      if (result > 3599) {
        result = 3599;
      }

      return result;

    }
    else {
      return null;
    }

  }

  getToolTipRows = (discipline, supported_charts) => {

    let tooltip_rows = [];
    if (this.props.chart_display_mode == 'overlay_laps') {
      for (var i = 0; i < this.props.selected_lap_ids.length; i++) {
        var lap_id = this.props.selected_lap_ids[i];
        tooltip_rows.push(
          <TooltipRow
            key={lap_id}
            colour={this.props.laps[lap_id].colour}
            trackpoint={this.props.compare_trackpoints[lap_id]}
            discipline={discipline}
            power_supported={supported_charts.power}
            heart_rate_supported={supported_charts.heart_rate}
            cadence_supported={supported_charts.cadence}
            elevation_supported={supported_charts.elevation}
            speed_supported={supported_charts.speed}
            distance_supported={supported_charts.distance}
            activity_id={this.props.activity_id}
            x_scale_mode={this.props.x_scale_mode}
          />
        );
      }
    }
    else if (this.props.chart_display_mode == 'compare_activities') {

      var row_data = [];

      // The main activity.
      // TODO Can we instead have the main activity display in the visible lap set activities?
      var colour = get_discipline_colour(discipline);

      row_data.push({
        colour: colour,
        activity_id: this.props.activity_id,
      });

      // Add the extra activities.
      for (let i = 0; i < this.props.visible_lapset_activities.length; i++) {

        const activity_id = this.props.visible_lapset_activities[i];

        // Do not add the main activity again.
        if (activity_id == this.props.activity_id) {
          continue;
        }

        row_data.push({
          colour: this.props.visible_lapset_activity_colours[activity_id],
          activity_id: activity_id,
        });

      }

      // Sort the list by activity date.
      row_data.sort((a, b) => {
        return (this.props.activity_data[a.activity_id].start_time < this.props.activity_data[b.activity_id].start_time) ? 1 : -1;
      });

      for (let i = 0; i < row_data.length; i++) {
        tooltip_rows.push(
          <TooltipRow
            key={row_data[i].activity_id}
            colour={row_data[i].colour}
            trackpoint={this.props.compare_trackpoints[row_data[i].activity_id]}
            discipline={discipline}
            power_supported={supported_charts.power}
            heart_rate_supported={supported_charts.heart_rate}
            cadence_supported={supported_charts.cadence}
            elevation_supported={supported_charts.elevation}
            speed_supported={supported_charts.speed}
            distance_supported={supported_charts.distance}
            activity_id={row_data[i].activity_id}
            x_scale_mode={this.props.x_scale_mode}
          />
        );
      }

    }
    else {
      tooltip_rows.push(
        <TooltipRow
          key={'activity-' + this.props.activity_id}
          colour="#333"
          trackpoint={this.props.compare_trackpoints[this.props.base_activity_id]}
          discipline={discipline}
          power_supported={supported_charts.power}
          heart_rate_supported={supported_charts.heart_rate}
          cadence_supported={supported_charts.cadence}
          elevation_supported={supported_charts.elevation}
          speed_supported={supported_charts.speed}
          distance_supported={supported_charts.distance}
          activity_id={this.props.activity_id}
          x_scale_mode={this.props.x_scale_mode}
        />
      );
    }

    return tooltip_rows;

  }

  /*
   * Implement React render.
   */
  render() {

    if (this.state.unexpectedError) {
      return <div style={{margin: '15px', lineHeight: '1.3em'}}>Sorry, there was a problem rendering the charts due to a bug. If this problem continues please let us know so we can fix it.</div>;
    }

    if (!this.props.activity_id || typeof(this.props.activity_data[this.props.activity_id]) == 'undefined' || typeof(this.props.activity_data[this.props.activity_id].trackpoints) == 'undefined') {
      return null;
    }

    var discipline = this.props.activity_data[this.props.activity_id] ? this.props.activity_data[this.props.activity_id].discipline : null;

    var power_supported = false;
    var heart_rate_supported = false;
    var elevation_supported = false;
    var cadence_supported = false;
    var speed_supported = false;
    var distance_supported = false;

    let show_swim_charts_by_lap = false;
    if (discipline == 'swimming' && hasBetaAccess('swim_training_log_graphs') && Object.keys(this.props.laps).length > 0) {
      return (
        <SwimCharts
          distance_unit={this.props.distance_unit}
          lapset_edit_mode={this.props.lapset_edit_mode}
          x_scale_mode={this.props.x_scale_mode}
          activity_id={this.props.activity_id}
          base_activity_id={this.props.base_activity_id}
          activity_data={this.props.activity_data}
          laps={this.props.laps}
          selected_lap_ids={this.props.selected_lap_ids}
          highlighted_lap_data={this.props.highlighted_lap_data}
          target_swim_paces={this.props.target_swim_paces}
          selectSwimLap={this.props.selectSwimLap}
          setSwimLapMouseHover={this.props.setSwimLapMouseHover}
          mobile_mode={this.props.mobile_mode}
          swim_pace_configuration_popup_visible={this.props.swim_pace_configuration_popup_visible}
          openSwimPacePopup={this.props.openSwimPacePopup}
        />
      );
    }

    var chart_containers = [];

    var has_data_to_display = false;
    if (this.props.current_lapset.id == 'overall_activity' || this.props.chart_display_mode == 'normal') {
      if (this.props.activity_data[this.props.activity_id]) {
        has_data_to_display = true;
      }
    }
    else {
      Object.keys(this.props.laps).forEach((lap_id) => {
        let lap = this.props.laps[lap_id];
        if (lap.lapsets.indexOf(this.props.current_lapset.id) > -1 && (this.props.activity_id == lap.activity_id || this.props.visible_lapset_activities.indexOf(lap.activity_id) > -1)) {
          has_data_to_display = true;
          return;
        }
      });
    }

    if (has_data_to_display) {

      if (this.props.activity_data[this.props.activity_id].total_distance_km > 0.5) {
        distance_supported = true;
      }

      var elevation_scale_max = max(this.props.activity_data[this.props.activity_id].trackpoints, function (d) { return d.altitude_meters; });
      if (this.props.activity_data[this.props.activity_id].total_distance_km > 0.5 && discipline != 'swimming' && !(typeof elevation_scale_max == "undefined" || elevation_scale_max == 0)) {
        elevation_supported = true;
        chart_containers.push(
          <ChartSection
            key="elevation"
            chart_type="elevation"
            activity_id={this.props.activity_id}
            base_activity_id={this.props.base_activity_id}
            visible_lapset_activities={this.props.visible_lapset_activities}
            visible_lapset_activity_colours={this.props.visible_lapset_activity_colours}
            display_compared_laps_only={this.props.display_compared_laps_only}
            activity_data={this.props.activity_data}
            highlighted_trackpoints={this.props.highlighted_trackpoints}
            metric="altitude_meters"
            y_axis_label="Elevation - m"
            chart_display_mode={this.props.chart_display_mode}
            laps={this.props.laps}
            current_lapset={this.props.current_lapset}
            selected_lap_ids={this.props.selected_lap_ids}
            distance_unit={this.props.distance_unit}
            lapset_edit_mode={this.props.lapset_edit_mode}
            x_scale_mode={this.props.x_scale_mode}
            map_is_visible={this.props.map_is_visible}
            getSpeedScaleMin={this.getSpeedScaleMin}
            getSpeedScaleMax={this.getSpeedScaleMax}
          />
        );
      }

      var km_speed_unit = (discipline == 'running') ? 'pace_seconds_per_km' : 'speed_km_per_hour';
      var mile_speed_unit = (discipline == 'running') ? 'pace_seconds_per_mile' : 'speed_miles_per_hour';
      var speed_scale_km_max = this.getSpeedScaleMax(this.props.activity_data[this.props.activity_id].trackpoints, km_speed_unit, false);
      if (!(typeof speed_scale_km_max == "undefined" || speed_scale_km_max < 2) && discipline != 'swimming') {
        speed_supported = true;

        if (discipline == 'swimming') {
          y_speed_km_label_text[this.props.activity_id] = 'min/100';
          y_speed_miles_label_text[this.props.activity_id] = 'min/100';
        }
        else if (discipline == 'cycling') {
          y_speed_km_label_text[this.props.activity_id] = 'kph';
          y_speed_miles_label_text[this.props.activity_id] = 'mph';
        }
        else if (discipline == 'running') {
          y_speed_km_label_text[this.props.activity_id] = 'min/km';
          y_speed_miles_label_text[this.props.activity_id] = 'min/mile';
        }
        else {
          y_speed_km_label_text[this.props.activity_id] = 'kph';
          y_speed_miles_label_text[this.props.activity_id] = 'mph';
        }

        if (this.props.distance_unit == 'km') {
          var y_axis_label = "Speed - " + y_speed_km_label_text[this.props.activity_id];
          var metric = km_speed_unit;
        }
        else {
          var y_axis_label = "Speed - " + y_speed_miles_label_text[this.props.activity_id];
          var metric = mile_speed_unit;
        }

        chart_containers.push(
          <ChartSection
            key="speed"
            chart_type="speed"
            activity_id={this.props.activity_id}
            base_activity_id={this.props.base_activity_id}
            activity_data={this.props.activity_data}
            laps={this.props.laps}
            display_compared_laps_only={this.props.display_compared_laps_only}
            visible_lapset_activities={this.props.visible_lapset_activities}
            visible_lapset_activity_colours={this.props.visible_lapset_activity_colours}
            metric={metric}
            discipline={discipline}
            y_axis_label={y_axis_label}
            chart_display_mode={this.props.chart_display_mode}
            current_lapset={this.props.current_lapset}
            selected_lap_ids={this.props.selected_lap_ids}
            distance_unit={this.props.distance_unit}
            lapset_edit_mode={this.props.lapset_edit_mode}
            x_scale_mode={this.props.x_scale_mode}
            map_is_visible={this.props.map_is_visible}
            target_run_paces={this.props.target_run_paces}
            getSpeedScaleMin={this.getSpeedScaleMin}
            getSpeedScaleMax={this.getSpeedScaleMax}
          />
        );
      }

      var heart_rate_scale_max = max(this.props.activity_data[this.props.activity_id].trackpoints, function (d) { return d.heart_rate_bpm; });
      if (!(typeof heart_rate_scale_max == "undefined" || heart_rate_scale_max == 0)) {
        heart_rate_supported = true;
        chart_containers.push(
          <ChartSection
            key="heart_rate"
            chart_type="heart_rate"
            activity_id={this.props.activity_id}
            base_activity_id={this.props.base_activity_id}
            activity_data={this.props.activity_data}
            visible_lapset_activities={this.props.visible_lapset_activities}
            visible_lapset_activity_colours={this.props.visible_lapset_activity_colours}
            metric="heart_rate_bpm"
            y_axis_label="Heart rate - bpm"
            chart_display_mode={this.props.chart_display_mode}
            laps={this.props.laps}
            display_compared_laps_only={this.props.display_compared_laps_only}
            current_lapset={this.props.current_lapset}
            selected_lap_ids={this.props.selected_lap_ids}
            distance_unit={this.props.distance_unit}
            lapset_edit_mode={this.props.lapset_edit_mode}
            x_scale_mode={this.props.x_scale_mode}
            map_is_visible={this.props.map_is_visible}
            getSpeedScaleMin={this.getSpeedScaleMin}
            getSpeedScaleMax={this.getSpeedScaleMax}
          />
        );
      }

      var power_scale_max = max(this.props.activity_data[this.props.activity_id].trackpoints, function (d) { return d.power_watts; });
      if (!(typeof power_scale_max == "undefined" || power_scale_max == 0)) {
        power_supported = true;
        chart_containers.push(
          <ChartSection
            key="power"
            chart_type="power"
            activity_id={this.props.activity_id}
            base_activity_id={this.props.base_activity_id}
            activity_data={this.props.activity_data}
            visible_lapset_activities={this.props.visible_lapset_activities}
            visible_lapset_activity_colours={this.props.visible_lapset_activity_colours}
            metric="power_watts"
            y_axis_label="Power - watts"
            chart_display_mode={this.props.chart_display_mode}
            laps={this.props.laps}
            display_compared_laps_only={this.props.display_compared_laps_only}
            current_lapset={this.props.current_lapset}
            selected_lap_ids={this.props.selected_lap_ids}
            distance_unit={this.props.distance_unit}
            lapset_edit_mode={this.props.lapset_edit_mode}
            x_scale_mode={this.props.x_scale_mode}
            map_is_visible={this.props.map_is_visible}
            target_bike_powers={this.props.target_bike_powers}
            getPowerScaleMin={this.getPowerScaleMin}
            getPowerScaleMax={this.getPowerScaleMax}
            getSpeedScaleMin={this.getSpeedScaleMin}
            getSpeedScaleMax={this.getSpeedScaleMax}
          />
        );
      }

      // TODO Trying out a refactoring that determines what charts to display further upstream. Add this to the other charts too if we don't see problems.
      //var cadence_scale_max = max(this.props.activity_data[this.props.activity_id].trackpoints, function (d) { return d.cadence_rpm; });
      //if (discipline != 'swimming' && !(typeof cadence_scale_max == "undefined" || cadence_scale_max == 0)) {
      if (this.props.activity_data[this.props.activity_id].supported_charts.cadence) {
        cadence_supported = true;
        var cadence_unit = (discipline == 'running') ? 'spm' : 'rpm';
        chart_containers.push(
          <ChartSection
            key="cadence"
            chart_type="cadence"
            activity_id={this.props.activity_id}
            base_activity_id={this.props.base_activity_id}
            activity_data={this.props.activity_data}
            laps={this.props.laps}
            display_compared_laps_only={this.props.display_compared_laps_only}
            visible_lapset_activities={this.props.visible_lapset_activities}
            visible_lapset_activity_colours={this.props.visible_lapset_activity_colours}
            discipline={discipline}
            metric="cadence_rpm"
            y_axis_label={'Cadence - ' + cadence_unit }
            chart_display_mode={this.props.chart_display_mode}
            current_lapset={this.props.current_lapset}
            selected_lap_ids={this.props.selected_lap_ids}
            distance_unit={this.props.distance_unit}
            lapset_edit_mode={this.props.lapset_edit_mode}
            x_scale_mode={this.props.x_scale_mode}
            map_is_visible={this.props.map_is_visible}
            getSpeedScaleMin={this.getSpeedScaleMin}
            getSpeedScaleMax={this.getSpeedScaleMax}
          />
        );
      }

    }

    let tooltip_rows = this.getToolTipRows(discipline, this.props.activity_data[this.props.activity_id].supported_charts);

    // Set up chart configuration popups.
    var chart_speed_configuration_popup;
    if (speed_supported) {

      let speed_unit = (this.props.distance_unit == 'km') ? km_speed_unit : mile_speed_unit;
      let speed_scale_min = this.getSpeedScaleMin(activities_section.state.reduced_trackpoints[this.props.activity_id], speed_unit, true);
      let speed_scale_max = this.getSpeedScaleMax(activities_section.state.reduced_trackpoints[this.props.activity_id], speed_unit, true);

      chart_speed_configuration_popup = (
        <ChartSpeedConfigurationPopup
          activity_id={this.props.activity_id}
          visible={this.props.speed_configuration_popup_visible}
          distance_unit={this.props.distance_unit}
          speed_scale_min={speed_scale_min}
          speed_scale_max={speed_scale_max}
          setSpeedScaleMin={this.setSpeedScaleMin}
          setSpeedScaleMax={this.setSpeedScaleMax}
          target_run_paces={this.props.target_run_paces}
        />
      );

    }

    var chart_power_configuration_popup;
    if (power_supported) {

      let power_scale_min = this.getPowerScaleMin(activities_section.state.reduced_trackpoints[this.props.activity_id], true);
      let power_scale_max = this.getPowerScaleMax(activities_section.state.reduced_trackpoints[this.props.activity_id], true);

      chart_power_configuration_popup = <ChartPowerConfigurationPopup
        target_bike_powers={this.props.target_bike_powers}
        activity_id={this.props.activity_id}
        visible={this.props.power_configuration_popup_visible}
        power_scale_min={power_scale_min}
        power_scale_max={power_scale_max}
        setPowerScaleMin={this.setPowerScaleMin}
        setPowerScaleMax={this.setPowerScaleMax}
      />;
    }

    var km_axis_classes = 'unit km';
    var mile_axis_classes = 'unit miles';
    if (this.props.distance_unit == 'km') {
      km_axis_classes += ' active';
    }
    else {
      mile_axis_classes += ' active';
    }

    var toggle_show_all_button;
    if (this.props.current_lapset.id != 'overall_activity') {
      var toggle_extra_laps_button_text = this.props.display_compared_laps_only ? 'show full session': 'show laps only';
      toggle_show_all_button = <div style={{ margin: '11px auto 0', display: 'block', width: '85px', textAlign: 'center' }} key="toggle-extra-laps" className="small-button" onClick={this.toggleHideUncomparedData}>{toggle_extra_laps_button_text}</div>;
    }

    let chart_smoothing_button;
    if (hasBetaAccess('show_smoothing_controls_on_chart')) {
      if (this.props.smoothing_is_active) {
        chart_smoothing_button = <span className="small-button" onClick={this.props.applySmoothing.bind(null, false)} style={{float: 'right', marginRight: '14px'}}>remove smoothing</span>
      }
      else {
        chart_smoothing_button = <span className="small-button" onClick={this.props.applySmoothing.bind(null, true)} style={{float: 'right', marginRight: '14px'}}>add smoothing</span>
      }
    }

    let tooltip;
    let charts_container;
    if (this.props.activity_data[this.props.activity_id].supported_charts.any_to_display) {

      tooltip = <div id={'compare-tooltip-activity-container-' + this.props.activity_id} className="chart-tooltip" style={{top: '233px'}}>
        {tooltip_rows}
      </div>

      charts_container = (
        <div className="charts-container" style={{position: 'relative'}}>
          <div className="marker-line" style={{width: '1px', background: '#515151', position: 'absolute', top: '0px', bottom: '0px', left: '-50px', pointerEvents: 'none', display: 'none'}}></div>
          <div className="highlight-box" style={{display: 'none', position: 'absolute', top: '0px', bottom: '0px', left: '400px', width: '0px', background: 'rgba(0, 0, 0, 0.1)', pointerEvents: 'none'}}>
            <div className="left" onMouseDown={this.handleMouseDownOnHighlightBoxLeftSide} onMouseUp={this.handleMouseUpOnHighlightBoxLeftSide} style={{ width: '1px', backgroundColor: 'white', height: '100%', position: 'absolute', cursor: 'col-resize', pointerEvents: 'auto' }}></div>
            <div className="right" onMouseDown={this.handleMouseDownOnHighlightBoxRightSide} onMouseUp={this.handleMouseUpOnHighlightBoxRightSide} style={{ height: '100%', width: '1px', backgroundColor: 'white', position: 'absolute', right: '0px', cursor: 'col-resize', pointerEvents: 'auto' }}></div>
          </div>
          {chart_containers}
          {toggle_show_all_button}
          <div className="axis-label" style={{textAlign: 'center'}}>
            <span className={km_axis_classes} onClick={this.setChartsToKM}>kilometers</span> <span>/</span> <span className={mile_axis_classes} onClick={this.setChartsToMiles}>miles</span>
            {chart_smoothing_button}
          </div>
        </div>
      );

    }

    return (
      <div className="charts-section-container-inner">
        {tooltip}
        {charts_container}
        {chart_speed_configuration_popup}
        {chart_power_configuration_popup}
      </div>
    );
  }
}

class TrainingSessionFormContainer extends React.Component {

  state = {
    currently_saving: false,
    changes_need_saving: false,
    form_errors: []
  }

  updateTrainingSession = (training_session, needs_saving) => {
    this.setState({training_session: training_session});
    if (needs_saving) {
      this.setState({changes_need_saving: true});
    }
  }

  updateRace = (race, needs_saving) => {
    this.setState({race: race});
    if (needs_saving) {
      this.setState({changes_need_saving: true});
    }
  }

  sessionDataChanged = () => {
    if (this.state.changes_need_saving === false) {
      this.setState({changes_need_saving: true});
    }
  }

  savingStarted = () => {
    this.setState({currently_saving: true, changes_need_saving: false})
  }

  trainingSessionSaved = (data) => {

    if ('errors' in data) {
      training_session_form.setState({form_errors: data.errors, currently_saving: false});
    }
    else if ('training_sessions' in data) {
      // If there were no errors, update the session with the saved details.
      let training_session = training_session_form.state.training_session;
      training_session.comments = data.training_sessions[0].comments;
      training_session.comments.new = '';
      this.setState({training_session_form_errors: [], currently_saving: false, training_session: training_session});
    }
    else if ('races' in data) {
      race = new Race(data.races[0]);
      training_session_form.setState({form_errors: [], currently_saving: false, race: race})
    }
  }

  render() {
    return (
      <TrainingSessionForm
        activity_plans={this.props.activity_plans}
        mode={this.props.mode}
        perspective={this.props.perspective}
        csrf_token={this.props.csrf_token}
        mobile_mode={mobile_mode}
        columns={mobile_mode ? 1 : 3}
        form_errors={this.state.form_errors}
        include_map={false}
        display_more_details_link={false}
        training_session={(typeof training_session != 'undefined') ? training_session : new TrainingSession()}
        race={(typeof race != 'undefined') ? race : null}
        athlete_data={athlete_data}
        saveTrainingSessions={saveTrainingSessions}
        savingStarted={this.savingStarted}
        sessionDataChanged={this.sessionDataChanged}
        currently_saving={this.state.currently_saving}
        changes_need_saving={this.state.changes_need_saving}
        updateTrainingSession={this.updateTrainingSession}
        updateRace={this.updateRace}
        mobile_display='inline'
        form_saved_callback={this.trainingSessionSaved}
        current_user_id={xhale_meta_data.user_id}
      />
    )
  }
}

function rebuild_target_run_paces(target_run_paces, distance_unit, base_activity_id) {

  // TODO Set based on the scale as well

  let pointer_starting_x_point = 5;
  Object.keys(target_run_paces).forEach((index) => {
    let pace_data = target_run_paces[index];
    if (pace_data.visible === true) {

      $('.pace-line-' + pace_data.id).show();
      if (distance_unit == 'miles') {
        $('.pace-label-km.pace-line-' + pace_data.id).hide();
        $('.pace-label-miles.pace-line-' + pace_data.id).show();
      }
      else if (distance_unit == 'km') {
        $('.pace-label-miles.pace-line-' + pace_data.id).hide();
        $('.pace-label-km.pace-line-' + pace_data.id).show();
      }

      // Calculate the width of this label's text element.
      if (distance_unit == 'km') {
        var text_element = select(".pace-label-km.pace-line-" + pace_data.id);
      }
      else if (distance_unit == 'miles'){
        var text_element = select(".pace-label-miles.pace-line-" + pace_data.id);
      }

      var label_text_width = text_element.node().getComputedTextLength();

      // Position each element along the x axis.
      selectAll(".pace-label.pace-line-" + pace_data.id).attr("dx", pointer_starting_x_point + 5);
      select(".pace-label-background.pace-line-" + pace_data.id).attr("width", label_text_width + 6).attr("x", pointer_starting_x_point + 2);
      select(".pace-label-pointer-background.pace-line-" + pace_data.id).attr("x1", pointer_starting_x_point).attr("x2", pointer_starting_x_point);
      select(".pace-label-pointer.pace-line-" + pace_data.id).attr("x1", pointer_starting_x_point).attr("x2", pointer_starting_x_point);

      // Position each element along the y axis.
      let pace = (distance_unit == 'km') ? xhale.helper.meters_per_second_as_seconds_per_km(pace_data.meters_per_second) : xhale.helper.meters_per_second_as_seconds_per_mile(pace_data.meters_per_second);

      selectAll(".data-line.pace-line-" + pace_data.id)
        .attr("y1", y_scale.speed[base_activity_id](pace))
        .attr("y2", y_scale.speed[base_activity_id](pace));

      selectAll(".pace-label-pointer-background.pace-line-" + pace_data.id)
        .attr("y1", 10)
        .attr("y2", y_scale.speed[base_activity_id](pace) - 1);

      selectAll(".pace-label-pointer.pace-line-" + pace_data.id)
        .attr("y1", 10)
        .attr("y2", y_scale.speed[base_activity_id](pace));

      // Record how much further along we need to put the next label.
      pointer_starting_x_point += label_text_width + 20;

    }
    else {
      $('.pace-line-' + pace_data.id).hide();
    }
  });

}

function rebuild_target_power(target_bike_powers) {

  var pointer_starting_x_point = 5;
  for (var index in target_bike_powers) {
    var power = target_bike_powers[index];
    if (power.visible === true) {

      $('.power-line-' + power.id).show();

      // Calculate the width of this label's text element.
      var text_element = select(".power-label.power-line-" + power.id);
      var label_text_width = text_element.node().getComputedTextLength();

      // Position each element along the x axis.
      selectAll(".power-label.power-line-" + power.id).attr("dx", pointer_starting_x_point + 5);
      select(".power-label-background.power-line-" + power.id).attr("width", label_text_width + 6).attr("x", pointer_starting_x_point + 2);
      select(".power-label-pointer-background.power-line-" + power.id).attr("x1", pointer_starting_x_point).attr("x2", pointer_starting_x_point);
      select(".power-label-pointer.power-line-" + power.id).attr("x1", pointer_starting_x_point).attr("x2", pointer_starting_x_point);

      // Record how much further along we need to put the next label.
      pointer_starting_x_point += label_text_width + 20;

    }
    else {
      $('.power-line-' + power.id).hide();
    }
  }

}

/**
 * Set up the data in the page to a usable form.
 */
function prepareData() {

  // Determine if we are in desktop or mobile mode.
  mobile_mode = false;
  if (window.matchMedia) {
    enquire.register('all and (max-width: 506px)', {
      match: function () {
        mobile_mode = true;
      },
      unmatch: function () {
        mobile_mode = false;
      }
    });
  }

  // Turn JSON data into objects.
  if (typeof(training_session) != 'undefined') {
    training_session = new TrainingSession(training_session);
  }

  if (typeof(activity_plans) != 'undefined') {
    training_session.activity_plan_uuids = Object.keys(activity_plans);
  }

  if (typeof(race) != 'undefined') {
    race = new Race(race);
  }

  for (var i = 0; i < window.target_run_paces.length; i++) {
    // Set each target run pace line to be invisible to start with.
    window.target_run_paces[i].visible = false;
  }

  // Add FTP to the target power list if it exists.
  if (ftp) {
    window.target_power.push({"watts": ftp, "id": 'ftp', "description": "FTP"});
    window.target_power.sort(function (a, b) {return a.watts - b.watts} );
  }

  for (var i = 0; i < window.target_power.length; i++) {
    // Set each target power line to be invisible to start with.
    window.target_power[i].visible = false;
  }

}

/**
 * Render the page.
 */
function renderPage() {

  var csrf_token = xhale.helper.getCookie('csrftoken');

  if (xhale.helper.has_beta_access('keep_open_training_forms')) {
    activate_persistent_sidebar();
  }

  // Render training session or race form.
  const session_athlete_id = (typeof(training_session) != 'undefined') ? training_session.athlete_id : race.athlete_id;
  if (xhale_meta_data.user_id == session_athlete_id) {
    var perspective = 'athlete';
  }
  else if (typeof(is_coach_of_this_athlete) != 'undefined' && is_coach_of_this_athlete) {
    var perspective = 'coach';
  }
  else {
    var perspective = 'public';
  }

  const mode = (typeof training_session != 'undefined') ? 'training_session' : 'race';

  if (perspective == 'athlete' || perspective == 'coach') {
    let activity_plans = {};
    if (window.activity_plans) {
      Object.keys(window.activity_plans).forEach((activity_plan_uuid) => {
        activity_plans[activity_plan_uuid] = new ActivityPlan(window.activity_plans[activity_plan_uuid]);
      });
    }

    training_session_form = ReactDOM.render(
      <TrainingSessionFormContainer
      mode={mode}
      csrf_token={csrf_token}
      perspective={perspective}
      activity_plans={activity_plans}
      />, $('#training-session-container')[0]
    );
  }

  // For now we are using the template to get a list of activity IDs that exist
  // in this session. We will remove this when some refactoring has been done.
  let base_activity_ids = [];
  $('.activity').each(function (index) {
    var base_activity_id = parseInt($(this).attr('data-activity_id'));
    base_activity_ids.push(base_activity_id);
  });

  activities_section = ReactDOM.render(
    <ActivitiesSection
      base_activity_ids={base_activity_ids}
      training_session={(typeof training_session != 'undefined') ? training_session : null}
      race={(typeof race != 'undefined') ? race : null}
      user_has_premium_features={xhale_meta_data.has_premium_features}
      athlete_id={athlete_data.id}
      athlete_data={window.athlete_data}
      current_user_meta_data={xhale_meta_data}
      csrf_token={csrf_token}
      target_bike_powers={window.target_power}
      target_run_paces={window.target_run_paces}
      target_swim_paces={window.target_swim_paces}
    />, document.getElementById('activities-section')
  );

}

/**
 * Direct access is for use in apps so the page header is not required.
 */
function tidyUpForDirectAccess() {
  var direct_access_token = xhale.helper.get_url_parameter('direct_access_token');
  if (direct_access_token) {
    $('.main-content-inner').css('padding-top', '0');
    $('#header').hide();
    $('#training-session-container').hide();
  }
}

/**
 * Add generic behaviours to the page which are not currently contained in React Components.
 */
function addBehaviours() {

  // When the window resizes, also resize various elements on the page to
  // reflect the new size of the window.
  select(window).on('resize', function () {
    setScreenSize();
  });

  // When the mouse is released anywhere on the document, dragging has
  // ended. We do this since the mouseup behaviour often ends with the
  // mouse over a different element.
  document.body.addEventListener('mouseup', function() {
    dragging = false;
  });

  // Stop text highlighting while we are dragging things around.
  $('.charts-container').disableSelection();

}

/**
 * Set the width of various elements.
 */
function setScreenSize() {

  // Charts are the width of the document minus margin around the chart
  chart_width = $('#main-content .main-content-inner').prop('clientWidth') - margin.left - margin.right;

  if (!mobile_mode) {

    var main_content_width = $('#main-content .main-content-inner').prop('clientWidth');
    $(".top-section").css({width: main_content_width + 'px'});
    $(".chart-tooltip").css({width: main_content_width + 'px'});

    var extra_section_width = $('.top-section').width() - $('.activity-summary__inner').outerWidth() - $('.top-section-controls').width() - 1;
    $(".top-section-extras").css({width: extra_section_width + 'px'});

  }

}

function main() {
  try {
    prepareData();
    renderPage();
    tidyUpForDirectAccess();
    addBehaviours();
    setScreenSize();
  }
  catch (error) {
    logError(error);
  }
}

main();
